import React, { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { 
  FileText, 
  Upload, 
  X, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  Settings,
  Info,
  Download,
  Filter
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import DashboardTopbar from "@/components/layout/DashboardTopbar";
import { processCustomDocumentCheck, processMultiDocumentCheck, validateFinalComplianceStatus, reEvaluateComplianceStatuses } from "@/lib/geminiProcessor";
import { saveAnalysisResults, getAnalysisHistory } from "@/lib/firebaseStorageService";
import { CheckResult } from "@/lib/checkDefinitions";
import UserDeclaredQuestions, { UserQuestionAnswer } from "@/components/analyzer/UserDeclaredQuestions";
import { USER_QUESTIONS } from "@/lib/constants/userQuestions";
import { AUDIT_QUESTIONS } from "@/lib/constants/auditQuestions";

// ===================================================================
// GLOBAL STATUS GUIDANCE (Results Analyzer)
// ===================================================================

const RESULTS_ANALYZER_GLOBAL_STATUS_GUIDANCE = `
GLOBAL STATUS GUIDANCE (apply to every checklist result):
- Default to "Compliant".
- If you see a possible issue but you are not fully sure, return "Check" (not "Non-Compliant").
- Return "Non-Compliant" only when the document evidence is clear and you are very sure.
- Always follow the response format required by the prompt. If the prompt forces only "Compliant"/"Non-Compliant" (or "Yes"/"No"), follow that format but keep the same conservative threshold for "Non-Compliant"/"No".
`.trim();

function applyResultsAnalyzerStatusGuidance(prompt: string): string {
  if (!prompt) return prompt;
  if (prompt.includes("GLOBAL STATUS GUIDANCE (apply to every checklist result):")) return prompt;
  return `${RESULTS_ANALYZER_GLOBAL_STATUS_GUIDANCE}\n\n${prompt.trim()}`;
}

// ===================================================================
// INTERFACES
// ===================================================================

interface ResultsAnalysisParameters {
  // Simplified parameters
  selectedQuarter?: string;
  selectedType?: 'consolidated' | 'standalone' | 'Normal';
}

interface CheckOption {
  id: string;
  name: string;
  description: string;
  category: string;
  estimatedTime: string;
  isSelected: boolean;
  model: string;
  temperature: number;
}

interface CheckCategory {
  name: string;
  checks: CheckOption[];
  totalSelected: number;
  totalAvailable: number;
}

interface AnalysisResult {
  id: string;
  timestamp: number;
  parameters: ResultsAnalysisParameters;
  document: File;
  results: {
    [key: string]: {
      compliant: boolean;
      isWarning?: boolean;
      isDataUnavailable?: boolean;
      details: string;
      confidence: number;
      model_used: string;
      checklistMetadata?: {
        name: string;
        description: string;
        category: string;
        estimatedTime: string;
      };
      feedback: {
        reasoning: string;
        evidence: string[];
        recommendations?: string[];
      };
    };
  };
  summary: {
    total: number;
    compliant: number;
    nonCompliant: number;
    check?: number;
    dataUnavailable?: number;
    compliancePercentage: number;
    userInput?: {
      yes: number;
      no: number;
      na: number;
      total: number;
    };
    isPartial?: boolean;
    completedChecks?: number;
    totalSelectedChecks?: number;
    validatedComplianceStatus?: {
      isValidatedCompliant: boolean;
      validationExplanation: string;
    };
  };
  userAnswers: UserQuestionAnswer[];
}

interface ProcessingProgress {
  completed: number;
  total: number;
  currentCheck: string;
  phase: string;
}

// ===================================================================
// HARDCODED CONFIGURATION
// ===================================================================

// Quarter-specific check mapping
const QUARTER_CHECKS: Record<string, string[]> = {
  'Q1': [
    'rs_in_units',
    'tax_ratio_warning',
    'face_value_share',
    'is_face_value',
    'eps_pat_calculation',
    'line_items_comparison',
    'basic_diluted_eps',
    'audit_committee_board_approval',
    'audit_committee_board_dates',
    'basic_logic_issues',
    'publication_timeline',
    'eom_para_explanation',
    'audit_report_addressee',
    'independent_auditors_report',

    'auditor_review_note',
    'audit_report_conclusion_opinion',
    'company_name_consistency',
    'serial_numbers_sequence',
    'group_definition_usage',
    'company_word_highlighting',
  
    'standard_review_audit_report_format',
    'profit_loss_quarter_ytd_consistency',
    'quarterly_ytd_figures_notes',
    'sebi_lodr_reg_33_8_para',
    'tax_ratio_deviation_highlighting',
   
    'firm_reg_number_correctness',
    'partner_name_mention',
    'partner_membership_number',
    'udin_number_mention',
    'firm_seal_affixed',
    'audit_report_date_consistency',
    'segment_results_explanation_note',
    'segment_period_year_phrases',
    'notes_figures_denomination',
    'tax_pbt_ratio_highlighting',
    'major_events_explanation_notes',
    'qualified_audit_report_impact_disclosure',
    'eom_paragraph_notes_explanation',
    'not_annualised_eps',
    'segment_reporting_reconciliation_pnl',
    'standard_audit_report_format_comprehensive',
    // Additional missing checklist items
    'other_auditors_mention',
    'audit_report_qualified',
    'eom_paragraphs_present',
    'material_reclassification',
    'profit_loss_status',
    'castings_check_vertical',
  ],
  'Q2': [
    // Q2/Q4 specific points
    'period_ended',
    'period_ended_as_at_phrases',
    'is_face_value',
    'eps_pat_calculation',
    'line_items_comparison',
    'other_equity_comparison',
    'cashflow_pbt_depreciation_tallying',
    'hy_cce_tallying',
    'cashflow_opening_cce_tallying',
    'cce_breakup_tallying',
    'cce_tallying_check',
    'balance_sheet_tallying_check',
    'cashflow_net_change_check',
    'not_annualised_eps',
    'note_references_agreement',
    'segment_net_assets_tallying',
    'segment_results_assets_comprehensive',
    'company_word_highlighting',
    'assets_cashflow_statements_hy',
    'standard_audit_report_format_comprehensive',
    'group_components_note',
    'cross_references_correctness',
    'date_place_consistency',
    'q2_balance_verification',
    'cfo_pat_depreciation_ratio',
    // All Q1 points
    'rs_in_units',
    'tax_ratio_warning',
    'face_value_share',
    'basic_diluted_eps',
    'audit_committee_board_approval',
    'audit_committee_board_dates',
    'basic_logic_issues',
    'publication_timeline',
    'eom_para_explanation',
    'audit_report_addressee',
    'independent_auditors_report',
    'auditor_review_note',
    'audit_report_conclusion_opinion',
    'company_name_consistency',
    'serial_numbers_sequence',
    'group_definition_usage',
    'segment_reporting_reconciliation_pnl',
    'standard_review_audit_report_format',
    'profit_loss_quarter_ytd_consistency',
    'quarterly_ytd_figures_notes',
    'sebi_lodr_reg_33_8_para',
    'tax_ratio_deviation_highlighting',

    'firm_reg_number_correctness',
    'partner_name_mention',
    'partner_membership_number',
    'udin_number_mention',
    'firm_seal_affixed',
    'audit_report_date_consistency',
    'segment_results_explanation_note',
    'segment_period_year_phrases',
    'notes_figures_denomination',
    'tax_pbt_ratio_highlighting',
    'major_events_explanation_notes',
    'qualified_audit_report_impact_disclosure',
    'eom_paragraph_notes_explanation',
    'other_auditors_mention',
    'audit_report_qualified',
    'eom_paragraphs_present',
    'material_reclassification',
    'profit_loss_status',
    'castings_check_vertical',
    'castings_check_horizontal',
    // Additional Q3 points not in Q1
    'segment_explanations',
    'segment_results_assets',
    'cashflow_activities_separation',
  ],
  'Q3': [
    'rs_in_units',
    'company_name_consistency',
    'face_value_share',
    'is_face_value',
    'eps_pat_calculation',
    'line_items_comparison',
    'basic_diluted_eps',
    'audit_committee_board_approval',
    'audit_committee_board_dates',
    'basic_logic_issues',
    'publication_timeline',
    'eom_para_explanation',
    'audit_report_addressee',
    'independent_auditors_report',
    'auditor_review_note',
    'audit_report_conclusion_opinion',
    'serial_numbers_sequence',
    'group_definition_usage',
    'company_word_highlighting',
    'segment_explanations',
    'standard_audit_report_format_comprehensive',
    'standard_review_audit_report_format',
    'profit_loss_quarter_ytd_consistency',
    'quarterly_ytd_figures_notes',
    'sebi_lodr_reg_33_8_para',
    'tax_ratio_deviation_highlighting',

    'firm_reg_number_correctness',
    'partner_name_mention',
    'partner_membership_number',
    'udin_number_mention',
    'firm_seal_affixed',
    'audit_report_date_consistency',
    'segment_results_explanation_note',
    'segment_period_year_phrases',
    'notes_figures_denomination',
    'tax_pbt_ratio_highlighting',
    'segment_results_assets',
    'major_events_explanation_notes',
    'qualified_audit_report_impact_disclosure',
    'eom_paragraph_notes_explanation',
    'segment_reporting_reconciliation_pnl',
    // Additional missing checklist items
    'other_auditors_mention',
    'audit_report_qualified',
    'eom_paragraphs_present',
    'material_reclassification',
    'profit_loss_status',
    'castings_check_vertical',
    'not_annualised_eps',
  ],
  'Q4': [
    // Q2/Q4 specific points
    'period_ended',
    'period_ended_as_at_phrases',
    'is_face_value',
    'eps_pat_calculation',
    'line_items_comparison',
    'other_equity_comparison',
    'cashflow_pbt_depreciation_tallying',
    'hy_cce_tallying',
    'cashflow_opening_cce_tallying',
    'cce_breakup_tallying',
    'cce_tallying_check',
    'balance_sheet_tallying_check',
    'cashflow_net_change_check',
    'not_annualised_eps',
    'note_references_agreement',
    'segment_net_assets_tallying',
    'segment_results_assets_comprehensive',
    'assets_cashflow_statements_hy',
    'standard_audit_report_format_comprehensive',
    'segment_reporting_reconciliation_pnl',
    'group_components_note',
    'cross_references_correctness',
    'date_place_consistency',
    'q4_balance_verification',
    'cfo_pat_depreciation_ratio',
    // All Q1 points
    'rs_in_units',
    'tax_ratio_warning',
    'face_value_share',
    'basic_diluted_eps',
    'audit_committee_board_approval',
    'audit_committee_board_dates',
    'basic_logic_issues',
    'publication_timeline',
    'eom_para_explanation',
    'audit_report_addressee',
    'independent_auditors_report',
    'auditor_review_note',
    'audit_report_conclusion_opinion',
    'serial_numbers_sequence',
    'group_definition_usage',
    'company_word_highlighting',
    'company_name_consistency',
    'standard_review_audit_report_format',
    'profit_loss_quarter_ytd_consistency',
    'quarterly_ytd_figures_notes',
    'sebi_lodr_reg_33_8_para',
    'tax_ratio_deviation_highlighting',

    'firm_reg_number_correctness',
    'partner_name_mention',
    'partner_membership_number',
    'udin_number_mention',
    'firm_seal_affixed',
    'audit_report_date_consistency',
    'segment_results_explanation_note',
    'segment_period_year_phrases',
    'notes_figures_denomination',
    'tax_pbt_ratio_highlighting',
    'major_events_explanation_notes',
    'qualified_audit_report_impact_disclosure',
    'eom_paragraph_notes_explanation',
    'other_auditors_mention',
    'audit_report_qualified',
    'eom_paragraphs_present',
    'material_reclassification',
    'profit_loss_status',
    'castings_check_vertical',
    'castings_check_horizontal',
    // Additional Q3 points not in Q1
    'segment_explanations',
    'segment_results_assets',
    'cashflow_activities_separation',
  ]
};

const TYPE_CHECKS: Record<string, string[]> = {
  'consolidated': [
    'rs_in_units',
    'company_name_consistency',
    'period_ended',
    'tax_ratio_warning',
    'is_face_value',
    'eps_pat_calculation',
    'line_items_comparison',
    'basic_diluted_eps',
    'not_annualised_eps',
    'audit_committee_board_approval',
    'audit_committee_board_dates',
    'basic_logic_issues',
    'publication_timeline',
    'eom_para_explanation',
    'audit_report_addressee',
    'independent_auditors_report',
    'cfo_pat_depreciation_ratio',
    'hy_cce_tallying',
    'cashflow_opening_cce_tallying',
    'cce_breakup_tallying',
    'cce_tallying_check',
    'balance_sheet_tallying_check',
    'cashflow_net_change_check',
    'note_references_agreement',
    'auditor_review_note',
    'segment_results_assets',
    'segment_results_pnl_tallying',
    'segment_depreciation_interest_tallying',
    'segment_reporting_reconciliation_pnl',
    'segment_net_assets_tallying',
    'segment_explanations',
    'cashflow_activities_separation',
    'segment_results_assets_comprehensive',
    'assets_cashflow_statements_hy',
    'standard_audit_report_format_comprehensive',
    'audit_report_conclusion_opinion',
    'ind_as_adoption_disclosures',
    'serial_numbers_sequence',
    'ytd_figures_matching',
    'segment_figures_bs_tallying',
    'group_definition_usage',
    'company_word_highlighting',
    'face_value_share',
    'castings_check_vertical',
    'other_equity_comparison',
    'cashflow_pbt_depreciation_tallying',
    'group_components_note',
    'cashflow_activities_separation',
 
    'standard_review_audit_report_format',
    'profit_loss_quarter_ytd_consistency',
    'quarterly_ytd_figures_notes',
    'sebi_lodr_reg_33_8_para',
    'cross_references_correctness',
    'tax_ratio_deviation_highlighting',
    'firm_reg_number_correctness',
    'partner_name_mention',
    'partner_membership_number',
    'udin_number_mention',
    'date_place_consistency',
    'firm_seal_affixed',
    'audit_report_date_consistency',
    'segment_results_explanation_note',
    'period_ended_as_at_phrases',
    'segment_period_year_phrases',
    'notes_figures_denomination',
    'tax_pbt_ratio_highlighting',
    'currency_unit_consistency',
    'major_events_explanation_notes',
    'qualified_audit_report_impact_disclosure',
    'eom_paragraph_notes_explanation',
    // Additional missing checklist items
    'other_auditors_mention',
    'audit_report_qualified',
    'eom_paragraphs_present',
    'material_reclassification',
    'profit_loss_status',
  ],
  'standalone': [
    'rs_in_units',
    'company_name_consistency',
    'period_ended',
    'is_face_value',
    'eps_pat_calculation',
    'line_items_comparison',
    'basic_diluted_eps',
    'not_annualised_eps',
    'audit_committee_board_approval',
    'audit_committee_board_dates',
    'basic_logic_issues',
    'cfo_pat_depreciation_ratio',
    'cce_tallying_check',
    'balance_sheet_tallying_check',
    'cashflow_net_change_check',
    'publication_timeline',
    'eom_para_explanation',
    'audit_report_addressee',
    'assets_cashflow_statements_hy',
    'independent_auditors_report',
    'segment_results_assets',
    'face_value_share',
    'cashflow_activities_separation',
    'note_references_agreement',
    'auditor_review_note',
    'audit_report_conclusion_opinion',
   
    'serial_numbers_sequence',
    'currency_unit_consistency',
    'other_equity_comparison',
    'cashflow_pbt_depreciation_tallying',
    'group_components_note',
   
    'standard_review_audit_report_format',
    'profit_loss_quarter_ytd_consistency',
    'quarterly_ytd_figures_notes',
    'cross_references_correctness',
    'tax_ratio_deviation_highlighting',

    'firm_reg_number_correctness',
    'partner_name_mention',
    'partner_membership_number',
    'udin_number_mention',
    'date_place_consistency',
    'firm_seal_affixed',
    'audit_report_date_consistency',
    'segment_results_explanation_note',
    'period_ended_as_at_phrases',
    'segment_period_year_phrases',
    'notes_figures_denomination',
    'tax_pbt_ratio_highlighting',
    'major_events_explanation_notes',
    'qualified_audit_report_impact_disclosure',
    'eom_paragraph_notes_explanation',
    'standard_audit_report_format_comprehensive',
    // Additional missing checklist items
    'audit_report_qualified',
    'eom_paragraphs_present',
    'material_reclassification',
    'profit_loss_status',
    'castings_check_vertical',
  ],
  'Normal': [
    'rs_in_units',
    'company_name_consistency',
    'period_ended',
    'tax_ratio_warning',
    'is_face_value',
    'eps_pat_calculation',
    'line_items_comparison',
    'cashflow_activities_separation',
    'cfo_pat_depreciation_ratio',
    'basic_diluted_eps',
    'not_annualised_eps',
    'audit_committee_board_approval',
    'audit_committee_board_dates',
    'basic_logic_issues',
    'cce_tallying_check',
    'balance_sheet_tallying_check',
    'cashflow_net_change_check',
    'publication_timeline',
    'eom_para_explanation',
    'audit_report_addressee',
    'independent_auditors_report',
   'segment_reporting_reconciliation_pnl',
    'note_references_agreement',
    'auditor_review_note',
    'audit_report_conclusion_opinion',
   
    'serial_numbers_sequence',
    'group_definition_usage',
    'company_word_highlighting',
   
    'segment_explanations',
    'segment_results_assets_comprehensive',
    'assets_cashflow_statements_hy',
    'standard_audit_report_format_comprehensive',
    'other_equity_comparison',
    'cashflow_pbt_depreciation_tallying',
    'group_components_note',

    'standard_review_audit_report_format',
    'profit_loss_quarter_ytd_consistency',
    'quarterly_ytd_figures_notes',
    'sebi_lodr_reg_33_8_para',
    'cross_references_correctness',
    'tax_ratio_deviation_highlighting',

    'firm_reg_number_correctness',
    'partner_name_mention',
    'partner_membership_number',
    'udin_number_mention',
    'date_place_consistency',
    'firm_seal_affixed',
    'audit_report_date_consistency',
    'segment_results_explanation_note',
    'period_ended_as_at_phrases',
    'segment_period_year_phrases',
    'notes_figures_denomination',
    'tax_pbt_ratio_highlighting',
    'major_events_explanation_notes',
    'qualified_audit_report_impact_disclosure',
    'eom_paragraph_notes_explanation',
    'segment_results_assets',
    'castings_check_vertical',
    // Additional missing checklist items
    'other_auditors_mention',
    'audit_report_qualified',
    'eom_paragraphs_present',
    'material_reclassification',
    'profit_loss_status',
  ]
};


const AVAILABLE_CHECKS: CheckOption[] = [
  {
    id: 'rs_in_units',
    name: 'R.V.1.5 - Rs in units',
    description: 'Check for currency units mention in financial statements',
    category: 'Basic Compliances',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.2
  },
  {
    id: 'period_ended',
    name: 'R.V.1.2 - For the "period ended"',
    description: 'Verify correct use of "for the period ended" phrase',
    category: 'Basic Compliances',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'tax_ratio_warning',
    name: 'R.V.1.3 - Tax ratio warning',
    description: 'Check tax ratio deviations over 25% compared to other periods',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'face_value_share',
    name: 'R.V.1.84 - Face value of share',
    description: 'Compare face value of shares between main document and consolidated/standalone document (interlinked multi-document)',
    category: 'Interlinked Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'is_face_value',
    name: 'Face Value Disclosure Check',
    description: 'Check if face value of shares is mentioned in the document and extract the value',
    category: 'Share Capital Compliance',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'eps_pat_calculation',
    name: 'EPS PAT vs TCI Calculation Check',
    description: 'Verify EPS is calculated using PAT and not TCI, with proper share count calculation',
    category: 'EPS Calculation Compliance',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.2
  },
  {
    id: 'line_items_comparison',
    name: 'Line Items Comparison Check',
    description: 'Compare line items between Previous Year End and Current Quarter Statements of Results',
    category: 'Financial Statement Compliance',
    estimatedTime: '25',
    isSelected: false,
    model: 'gemini-2.5-pro',
    temperature: 0.4
  },
  {
    id: 'basic_diluted_eps',
    name: 'R.V.1.5 - Basic & Diluted EPS',
    description: 'Check if Basic and Diluted EPS are shown separately',
    category: 'Financial Analysis',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'not_annualised_eps',
    name: 'R.V.1.29 - Not annualised EPS',
    description: 'Verify "not annualised" mention for EPS of periods',
    category: 'Financial Analysis',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'audit_committee_board_approval',
    name: 'R.V.1.7 - Audit Committee & Board Approval',
    description: 'Check if results reviewed by audit committee and approved by board',
    category: 'Regulatory Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'major_events_notes',
    name: 'R.V.1.8 - Major events notes',
    description: 'Check for notes explaining major events like mergers, acquisitions (Based on User Answer)',
    category: 'Regulatory Compliance',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to major events question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'audit_committee_board_dates',
    name: 'R.V.1.9 - Audit Committee & Board Dates',
    description: 'Check audit committee and board meeting dates',
    category: 'Regulatory Compliance',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'basic_logic_issues',
    name: 'R.V.1.3 - Basic logic issues',
    description: 'Check for basic logic issues in results',
    category: 'Data Verification',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'qualified_audit_explanation',
    name: 'R.V.1.11 - Qualified audit explanation',
    description: 'Check for management explanation of qualified audit report',
    category: 'Audit Compliance',
    estimatedTime: '30',
    isSelected: false,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'publication_timeline',
    name: 'R.V.1.12 - Publication timeline',
    description: 'Check if publication done within timeline',
    category: 'Regulatory Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'eom_para_explanation',
    name: 'R.V.1.13 - EOM para explanation',
    description: 'Check if EOM paragraphs in audit report explained in notes',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'audit_report_addressee',
    name: 'R.V.1.14 - Audit report addressee',
    description: 'Check if audit report addressed to Board of Directors',
    category: 'Audit Compliance',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'independent_auditors_report',
    name: 'R.V.1.15 - Independent auditors report',
    description: 'Check if audit report states "Independent auditors report"',
    category: 'Audit Compliance',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },

  {
    id: 'hy_cce_tallying',
    name: 'R.V.1.18 - HY CCE Tallying',
    description: 'Check if CCE in HY results tallies with Statement of Assets for both years',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'cashflow_opening_cce_tallying',
    name: 'R.V.1.19 - Cash Flow Opening CCE Tallying',
    description: 'Check if opening CCE of current year tallies with closing CCE of last year',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'cce_breakup_tallying',
    name: 'R.V.1.20 - CCE Breakup Tallying',
    description: 'Check if CCE breakup tallies with CCE mentioned in cash flow',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'note_references_agreement',
    name: 'R.V.1.21 - Note References Agreement',
    description: 'Check if note references in results and statement of assets agree with note contents',
    category: 'Basic Compliances',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'forex_exchange_difference_disclosure',
    name: 'R.V.1.22 - Forex Exchange Difference Disclosure',
    description: 'Check if exchange difference and CCE mentioned separately for forex bank accounts',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to forex bank account question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'auditor_review_note',
    name: 'R.V.1.23 - Auditor Review Note',
    description: 'Check if note says results have been reviewed by auditor (unless audited)',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'segment_results_assets',
    name: 'R.V.1.24 - Segment Results and Assets',
    description: 'Check if results give segment results and assets',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'segment_results_pnl_tallying',
    name: 'R.V.1.25 - Segment Results P&L Tallying',
    description: 'Check if segment results tally with P&L results',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'segment_depreciation_interest_tallying',
    name: 'R.V.1.26 - Segment Depreciation Interest Tallying',
    description: 'Check if depreciation, interest etc. in segment tally with P&L results',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'segment_reporting_reconciliation_pnl',
    name: 'R.V.1.26A - Segment Reporting Reconciliation with P&L',
    description: 'Does depreciation, interest etc. in segment tally with P&L results?',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'segment_net_assets_tallying',
    name: 'R.V.1.27 - Segment Net Assets Tallying',
    description: 'Check if total of net assets per segment results tally with networth per statement of assets',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'cashflow_activities_separation',
    name: 'R.V.1.29 - Cash Flow Activities Separation',
    description: 'Does cash flow show operating, investing and financing activities separately?',
    category: 'Financial Analysis',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'segment_results_assets_comprehensive',
    name: 'R.V.1.30 - Segment Results and Assets Comprehensive',
    description: 'Do results give segment results and assets?',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'company_word_highlighting',
    name: 'R.V.1.31 - Company Word Highlighting',
    description: 'Highlight wherever the word Company appears unless it is Holding company or subsidiary company or associate company or JV company',
    category: 'Financial Analysis',
    estimatedTime: '15',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'assets_cashflow_statements_hy',
    name: 'R.V.1.32 - Assets and Cash Flow Statements HY',
    description: 'Is there a statement of assets and cashflow added for HY results?',
    category: 'Financial Analysis',
    estimatedTime: '15',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'segment_explanations',
    name: 'R.V.1.28 - Segment Explanations',
    description: 'Check if segment results give explanation for all segments and what they represent',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'audit_report_conclusion_opinion',
    name: 'R.V.1.29 - Audit Report Conclusion/Opinion',
    description: 'Check if audit report issued for review provides conclusion (unless audit in which case opinion)',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  // {
  //   id: 'company_name_consistency',
  //   name: 'R.V.1.63 - Company Name Consistency',
  //   description: 'Check if name of Company is same all across results, audit report etc.',
  //   category: 'Data Verification',
  //   estimatedTime: '25',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro',
  //   temperature: 0.1
  // },
  {
    id: 'ind_as_adoption_disclosures',
    name: 'R.V.1.32 - IND AS Adoption Disclosures',
    description: 'Check if adequate disclosures made for first-time adoption of IND AS',
    category: 'Regulatory Compliance',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'serial_numbers_sequence',
    name: 'R.V.1.33 - Serial Numbers Sequence',
    description: 'Check if serial numbers used in sequence with no gaps or errors',
    category: 'Data Verification',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.6
  },
  {
    id: 'ytd_figures_matching',
    name: 'R.V.1.34 - YTD Figures Matching',
    description: 'Check if YTD figures match with previous quarter plus current quarter numbers',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'segment_figures_bs_tallying',
    name: 'R.V.1.35 - Segment Figures BS Tallying',
    description: 'Check if segment figures for last quarter/year end tie up between themselves for BS items',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'group_definition_usage',
    name: 'R.V.1.36 - Group Definition Usage',
    description: 'Check if word Group is used for Holding and subsidiaries together in all places',
    category: 'Data Verification',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },


  {
    id: 'eps_pat_ratio_comparison',
    name: 'R.V.1.39 - EPS PAT Ratio Comparison',
    description: 'Compare ratio of EPS to Consolidated PAT with standalone ratio of EPS to Standalone PAT',
    category: 'Interlinked Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'associates_jv_oci',
    name: 'R.V.1.40 - Associates JV OCI',
    description: 'Check if OCI related to associates/JV mentioned in results if they exist (Based on User Answer)',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to associates/joint ventures question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },

  {
    id: 'other_equity_comparison',
    name: 'R.V.1.41 - Other Equity Comparison',
    description: 'Compare Other equity mentioned in results with other equity in statement of assets and liabilities',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'cashflow_pbt_depreciation_tallying',
    name: 'R.V.1.42 - Cash Flow PBT Depreciation Tallying',
    description: 'Check if Profit before tax, depreciation, finance costs etc. tally with results in cash flow',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'foreign_subsidiaries_exchange_disclosure',
    name: 'R.V.1.43 - Foreign Subsidiaries Exchange Disclosure',
    description: 'Check if disclosure of exchange difference on translation of foreign currency balances in cash flow',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to foreign subsidiaries question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'group_components_note',
    name: 'R.V.1.44 - Group Components Note',
    description: 'Check if note on components of the group in results and review/audit report',
    category: 'Regulatory Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'profit_loss_quarter_ytd_consistency',
    name: 'R.V.1.47 - Profit Loss Quarter YTD Consistency',
    description: 'Check if profit or loss correctly mentioned in review/audit report for quarter vs YTD',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-flash',
    temperature: 0.4
  },
  {
    id: 'foreign_components_translation_disclosure',
    name: 'R.V.1.48 - Foreign Components Translation Disclosure',
    description: 'Check if para on translation from foreign GAAP to IND AS and foreign currency to INR exists',
    category: 'Regulatory Compliance',
    estimatedTime: '30',
    isSelected: false, // Only selected when user answers "Yes" to foreign subsidiaries question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'associates_jv_audit_report_mention',
    name: 'R.V.1.49 - Associates JV Audit Report Mention',
    description: 'Check if associates/JV mentioned along with group in review/audit report when they exist (Based on User Answer)',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to associates/joint ventures question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'quarterly_ytd_figures_notes',
    name: 'R.V.1.50 - Quarterly YTD Figures Notes',
    description: 'Check if both quarterly and YTD figures mentioned in notes/review/audit report',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'subsidiary_word_highlighting',
    name: 'R.V.1.51 - Subsidiary Word Highlighting',
    description: 'Check if word subsidiary mentioned anywhere in results, statement of assets, segment results, cashflow or notes',
    category: 'Data Verification',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'sebi_lodr_reg_33_8_para',
    name: 'R.V.1.52 - SEBI LODR Reg 33(8) Para',
    description: 'Check if para on Reg 33(8) of SEBI LODR included in review/audit report',
    category: 'Regulatory Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'goodwill_impairment_note',
    name: 'R.V.1.53 - Goodwill Impairment Note',
    description: 'Check if note explains goodwill impairment if it exists( Based on User Answer)',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to goodwill impaired question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'cross_references_correctness',
    name: 'R.V.1.54 - Cross References Correctness',
    description: 'Check if cross references to note numbers in results, statement of assets and cashflow and review/audit report are correct',
    category: 'Data Verification',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'qualification_eom_consistency',
    name: 'R.V.1.55 - Qualification EOM Consistency',
    description: 'Check if qualification/EOM in standalone review/audit report also present in consolidated report',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.3
  },
  // {
  //   id: 'tax_ratio_deviation_highlighting',
  //   name: 'R.V.1.56 - Tax Ratio Deviation Highlighting',
  //   description: 'Check if ratio of total tax to PBT differs by over 25% between periods and highlight',
  //   category: 'Financial Analysis',
  //   estimatedTime: '30',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro',
  //   temperature: 0.1
  // },
  // {
  //   id: 'cin_number_mention',
  //   name: 'R.V.1.57 - CIN Number Mention',
  //   description: 'Check if CIN number mentioned in results',
  //   category: 'Regulatory Compliance',
  //   estimatedTime: '25',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro',
  //   temperature: 0.1
  // },
  {
    id: 'firm_reg_number_correctness',
    name: 'R.V.1.58 - Firm Reg Number Correctness',
    description: 'Check if firm registration number follows correct format structure',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-flash',
    temperature: 0.4
  },
  {
    id: 'partner_name_mention',
    name: 'R.V.1.59 - Partner Name Mention',
    description: 'Check if name of partner mentioned in audit report',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'partner_membership_number',
    name: 'R.V.1.60 - Partner Membership Number',
    description: 'Check if membership no. of partner included and is 6 digits',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'udin_number_mention',
    name: 'R.V.1.61 - UDIN Number Mention',
    description: 'Check if UDIN no. mentioned in audit report',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'date_place_consistency',
    name: 'R.V.1.62 - Date Place Consistency',
    description: 'Check if date and place mentioned correctly and tally between results and review/audit report',
    category: 'Data Verification',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  // {
  //   id: 'firm_seal_affixed',
  //   name: 'R.V.1.63 - Firm Seal Affixed',
  //   description: 'Check if firm seal affixed in audit report',
  //   category: 'Audit Compliance',
  //   estimatedTime: '25',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro',
  //   temperature: 0.1
  // },
  {
    id: 'audit_report_date_consistency',
    name: 'R.V.1.64 - Audit Report Date Consistency',
    description: 'Check if date of review/audit report same as results',
    category: 'Data Verification',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'segment_results_explanation_note',
    name: 'R.V.1.65 - Segment Results Explanation Note',
    description: 'Check if segments explained by way of a note in segment results',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'period_ended_as_at_phrases',
    name: 'R.V.1.66 - Period Ended As At Phrases',
    description: 'Check if results and cash flow state for the period/year ended and statement of assets shows as at',
    category: 'Basic Compliances',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.2
  },
  {
    id: 'segment_period_year_phrases',
    name: 'R.V.1.67 - Results Period Year Phrases',
    description: 'Check if words for the period/year and as at stated appropriately in results',
    category: 'Basic Compliances',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'notes_figures_denomination',
    name: 'R.V.1.68 - Notes Figures Denomination',
    description: 'Check if notes figures in same denomination as the results',
    category: 'Data Verification',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'tax_pbt_ratio_highlighting',
    name: 'R.V.1.69 - Tax PBT Ratio Highlighting',
    description: 'Check if total tax to PBT less than 25% or more than 40% and highlight',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },

  {
    id: 'qualified_audit_report_impact_disclosure',
    name: 'R.V.1.72 - Qualified Audit Report Impact Disclosure',
    description: 'If audit report is qualified, is there a note explaining management view and cumulative impact? (Based on User Answer)',
    category: 'Audit Compliance',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.2
  },
  {
    id: 'eom_paragraph_notes_explanation',
    name: 'R.V.1.73 - EOM Paragraph Notes Explanation',
    description: 'Is any EOM para in audit report explained in note?',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.2
  },
  {
    id: 'material_reclassification_explanation',
    name: 'R.V.1.74 - Material Reclassification Explanation',
    description: 'If there are material reclassification of past results, explanation given in notes? (Based on User Answer)',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.2
  },
  {
    id: 'foreign_component_translation_disclosure',
    name: 'R.V.1.76 - Foreign Component Translation Disclosure',
    description: 'If there are foreign country components, is there a para on translation from foreign GAAP into IND AS and foreign currency into INR? (Based on User Answer)',
    category: 'Regulatory Compliance',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.2
  },
 
  {
    id: 'q2_balance_verification',
    name: 'R.V.1.77 - Q2 Balance Verification',
    description: 'Verify Q2 opening and closing balances with previous year Q4 (interlinked multi-document)',
    category: 'Interlinked Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.2
  },
  {
    id: 'q4_balance_verification',
    name: 'R.V.1.78 - Q4 Balance Verification',
    description: 'Verify Q4 opening and closing balances with previous year Q4 (interlinked multi-document)',
    category: 'Interlinked Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.2
  },
  {
    id: 'currency_unit_consistency',
    name: 'R.V.1.79 - Currency unit consistency',
    description: 'Compare currency measurement units (crores, lakhs, thousands) between main document and consolidated/standalone document (interlinked multi-document)',
    category: 'Interlinked Analysis',
    estimatedTime: '20',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'standalone_consolidated_notes_interlink',
    name: 'R.V.1.80 - Standalone vs Consolidated Notes Interlink',
    description: 'Verify all notes in standalone document are repeated in consolidated document with suitable changes (interlinked multi-document)',
    category: 'Interlinked Analysis',
    estimatedTime: '35',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'previous_auditor_different',
    name: 'R.V.1.81 - Previous Auditor Different',
    description: 'Check if previous period auditor is different from current auditor and mentioned in report',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to previous auditor different question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'other_auditors_mention',
    name: 'R.V.1.82 - Other Auditors Mention',
    description: 'Check if components reviewed/audited by other auditors mentioned in review/audit report',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'first_year_firm_previous_auditor',
    name: 'R.V.1.83 - First Year Firm Previous Auditor',
    description: 'Check if there is a paragraph on who has done review/audit in previous year and if report is modified for first year firm',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to first year audit firm question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'goodwill_impaired',
    name: 'R.V.1.84 - Goodwill Impaired',
    description: 'Check if goodwill is impaired and if there is a note to explain that',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to goodwill impaired question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'associates_joint_ventures',
    name: 'R.V.1.85 - Associates Joint Ventures',
    description: 'Check if the company has associates or joint ventures',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to associates/joint ventures question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'eom_paragraphs_present',
    name: 'R.V.1.87 - EOM Paragraphs Present',
    description: 'Check if EOM paragraphs are present in audit report',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
    // {
  //   id: 'audit_report_qualified',
  //   name: 'R.V.1.86 - Audit Report Qualified',
  //   description: 'Check if audit report is qualified',
  //   category: 'Audit Compliance',
  //   estimatedTime: '25',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro',
  //   temperature: 0.1
  // },
   // {
  //   id: 'major_events_explanation_notes',
  //   name: 'R.V.1.71 - Major Events Explanation Notes',
  //   description: 'Are notes given to explain all major events like mergers, acquisitions, new share issues etc.?',
  //   category: 'Regulatory Compliance',
  //   estimatedTime: '25',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro-lite',
  //   temperature: 0.2
  // },
    // {
  //   id: 'standard_review_audit_report_format',
  //   name: 'R.V.1.46 -  Standard Review Audit Report Format',
  //   description: 'Check if standard format of review/audit report is followed',
  //   category: 'Audit Compliance',
  //   estimatedTime: '25',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro-lite',
  //   temperature: 0.1
  // },
    // {
  //   id: 'standard_audit_report_format_comprehensive',
  //   name: 'R.V.1.33 - Standard Audit Report Format Comprehensive',
  //   description: 'Is standard format of audit report used?',
  //   category: 'Audit Compliance',
  //   estimatedTime: '20',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro-lite-lite',
  //   temperature: 0.1
  // },
  // {
  //   id: 'material_reclassification',
  //   name: 'R.V.1.88 - Material Reclassification',
  //   description: 'Check if there are material reclassifications of past results',
  //   category: 'Financial Analysis',
  //   estimatedTime: '25',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro-lite',
  //   temperature: 0.1
  // },
  // {
  //   id: 'profit_loss_status',
  //   name: 'R.V.1.89 - Profit Loss Status',
  //   description: 'Check if profit/loss status is correctly stated in audit report',
  //   category: 'Audit Compliance',
  //   estimatedTime: '25',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro-lite',
  //   temperature: 0.1
  // },
    // {
  //   id: 'company_name_consistency',
  //   name: 'R.V.1.79 - Company Name Consistency Check',
  //   description: 'Verify that the company name is consistent throughout the financial results and audit documents',
  //   category: 'Data Verification',
  //   estimatedTime: '25',
  //   isSelected: true,
  //   model: 'gemini-2.5-pro-lite',
  //   temperature: 0.1
  // },
  {
    id: 'foreign_country_components',
    name: 'R.V.1.90 - Foreign Country Components',
    description: 'Check if there are foreign country components',
    category: 'Financial Analysis',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Yes" to foreign country components question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'sre_2410_mention',
    name: 'R.V.1.93 - SRE 2410 Mention in Audit Report',
    description: 'Check if the audit report mentions SRE 2410',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Auditor" to PKF auditor/co-reviewer question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'sre_2400_mention',
    name: 'R.V.1.94 - SRE 2400 Mention in Audit Report',
    description: 'Check if the audit report mentions SRE 2400',
    category: 'Audit Compliance',
    estimatedTime: '25',
    isSelected: false, // Only selected when user answers "Co-reviewer" to PKF auditor/co-reviewer question
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'cfo_pat_depreciation_ratio',
    name: 'R.V.1.91 - CFO vs PAT+Depreciation Ratio',
    description: 'Verify whether Cash Flow from Operating Activities (CFO) is at least 50% of the sum of Profit After Tax (PAT) and Depreciation/Other Non-Cash Provisions',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
      id: 'cce_tallying_check',
      name: 'R.V.1.92 - CCE Tallying with Statement of Assets',
      description: 'Verify CCE (Cash and Cash Equivalents) from Statement of Cash Flows matches with Statement of Assets and Liabilities for both periods (single document)',
      category: 'Financial Statement Compliance',
      estimatedTime: '35',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'balance_sheet_tallying_check',
    name: 'R.V.1.93 - Balance Sheet Tallying Check',
    description: 'Verify that total of Statement of Assets and Liabilities both sides agree (Assets = Equity + Liabilities) for both periods',
    category: 'Financial Statement Compliance',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'cashflow_net_change_check',
    name: 'R.V.1.94 - Cash Flow Net Change Verification',
    description: 'Verify that net decrease/increase in cash flow agrees with the difference in opening and closing CCE (OCF + ICF + FCF = Closing CCE - Opening CCE)',
    category: 'Financial Statement Compliance',
    estimatedTime: '35',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'castings_check_vertical',
    name: 'Castings Check-Vertical (beta)',
    description: 'Verify internal arithmetic consistency within each reporting period column by validating totals, subtotals, and derived figures',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  },
  {
    id: 'castings_check_horizontal',
    name: 'Castings - Horizontal Check',
    description: 'Verify consistency, completeness, and comparability of financial data across reporting periods by performing row-wise checks',
    category: 'Financial Analysis',
    estimatedTime: '30',
    isSelected: true,
    model: 'gemini-2.5-pro',
    temperature: 0.1
  }
]; 


const CHECK_PROMPTS: Record<string, string> = {
  rs_in_units: `You are reviewing the financial results document to generally verify that wherever currency is mentioned, appropriate units/measures are also specified.

REVIEW CHECK: Look for currency mentions throughout the document and confirm, in a reasonable and practical manner, that they are paired with appropriate units.

INTELLIGENT CURRENCY-UNIT PAIRING LOGIC:

Wherever you find currency symbols (Rs, ₹, INR, USD, $), check whether units are also mentioned.

Focus ONLY on currency symbols paired with units (e.g., "Rs crores", "$ millions", "₹ lakhs").

IGNORE actual numerical values and amounts — only check currency symbol + unit combinations.

The goal is to ensure currency symbols and units are generally paired together.

IMPORTANT: Units can be different throughout the document (crores, lakhs, thousands, etc.) — this is acceptable.

The requirement is that currency should have SOME unit specified in most reasonable cases, not that all units must be the same.

ACCEPTABLE PAIRING FORMATS:
✅ "Rs. in crores" or "Rs crores"
✅ "₹ in lakhs" or "₹ lakhs"
✅ "INR in thousands" or "INR thousands"
✅ "USD in millions" or "$ millions"
✅ "(Rs. in crores)" or "(₹ in lakhs)"
✅ "Statement of Results (Rs. in crores)"
✅ "Amounts in Rs. crores"
✅ "Figures in ₹ lakhs"

IMPORTANT: Different units throughout the document are perfectly acceptable:
✅ Document can have "Rs. in crores" in one section and "₹ in lakhs" in another
✅ Document can mix "crores", "lakhs", "thousands" as appropriate for different amounts
✅ The key requirement is that each currency mention generally has SOME unit specified

LOCATIONS TO CHECK:

Table headers and titles

Statement titles

Column headers

Notes and footnotes

Anywhere currency amounts are presented

IMPORTANT: This check ensures that wherever currency symbols (Rs, ₹, INR, USD, $) are mentioned, appropriate units are also specified. Focus only on currency symbol + unit combinations, not on individual numbers. Be practical and lenient in your assessment — mark as compliant in most cases unless you are fully certain that a currency symbol appears without any appropriate unit.

OUTPUT FORMAT (mandatory):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide a detailed text analysis:
   - List the currency-unit pairings found (e.g., "Rs. in lakhs", "₹ crores")
   - Note any sections where currency appears without a unit
   - Summarize whether the document generally pairs currency symbols with units
   - If non-compliant, explain which specific instances are missing units

Do NOT return bounding boxes, coordinates, JSON objects, or spatial detection data. Return only plain text analysis.`,

  period_ended: `Check the captions and introductory lines of the Statement of Results and the Cash Flow Statement. Confirm that these documents use the phrase "for the period ended" along with the correct reporting date (e.g., "for the period ended June 30, 2025" or "for the QUARTER AND SIX MONTHS ENDED SEPTEMBER 30, 202x"). If this phrasing is not used or is incorrect (e.g., using "as at"), raise a formatting inconsistency warning.

Output format:
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote the exact phrases found in the document headers
   - Explain why the phrasing is correct or incorrect
   - If non-compliant, specify what should be changed
   - Provide specific recommendations for improvement`,

  tax_ratio_warning: `When tax ratio of total tax expenses (excluding prior period tax) to PBT differs by over 25% compared to other periods raise warning.

You are analyzing the 'Statement of Unaudited Standalone Financial Results' for the three months ended June 30, 2025.

The table typically has multiple columns representing different periods, including:

Current Quarter (June 30, 2025)

Previous Quarter (March 31, 2025)

Corresponding Quarter Previous Year (June 30, 2024)

Year Ended (March 31, 2025)

Within each column, locate the following line items:

Profit Before Tax (PBT)

Tax Expense, which is the sum of Current Tax and Deferred Tax

For each of the periods (at least 3 quarters, including the current), calculate the tax-to-PBT ratio as:


Tax Ratio = (Current Tax + Deferred Tax) / Profit Before Tax
⚠️ Important:

Exclude any prior period tax adjustments or one-time tax provisions from this calculation.

Once all tax ratios are computed:

Compare the tax ratio of the current quarter (June 2025) with the tax ratios of at least two other periods (e.g., Mar 2025 and Jun 2024).

If the ratio for the current quarter differs by more than 25% (absolute) from either of the other periods, raise a compliance warning stating:
"Tax-to-PBT ratio deviates >25% from other periods – investigate cause or provide explanation."

Include the ratios and period labels in your output for clarity.`,


  face_value_main_document: `You are reviewing the MAIN DOCUMENT (current year financial results) to extract the face value of shares. This will be used for comparison with a consolidated/standalone document.

Your task:
Search the entire document, including tables and footnotes, for any mention of the face value per share, usually phrased as:
- 'Face value of equity shares is ₹1 each'
- 'FV of ₹10 per share' 
- 'Equity share of face value ₹5/-'
- or similar variants

Ensure that the face value is specified clearly in numeric terms (e.g., ₹1, ₹2, ₹5, ₹10) and is not missing or vague.

The disclosure may appear:
- In the EPS section of the results
- In the header of the financial statements table
- In notes to the results

❌ Do not treat implied references (e.g., in EPS formula) as valid unless the face value is explicitly stated.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Second line: Face Value: ₹[NUMBER] (or "NOT_FOUND" if not found)
3) Then provide detailed analysis:
   - Quote the exact text where face value is mentioned (if found)
   - Explain why the disclosure is compliant or non-compliant
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Example output:
Compliant
Face Value: ₹2
The face value of ₹2 per share is clearly disclosed in the EPS section on page 3: "Earnings per share (face value ₹2 each)..."

Keep the response structured and informative with clear evidence from the document.`,

  face_value_consolidated_standalone: `You are reviewing the CONSOLIDATED/STANDALONE DOCUMENT to extract the face value of shares. This will be used for comparison with the main document.

Your task:
Search the entire document, including tables and footnotes, for any mention of the face value per share, usually phrased as:
- 'Face value of equity shares is ₹1 each'
- 'FV of ₹10 per share' 
- 'Equity share of face value ₹5/-'
- or similar variants

Ensure that the face value is specified clearly in numeric terms (e.g., ₹1, ₹2, ₹5, ₹10) and is not missing or vague.

The disclosure may appear:
- In the EPS section of the results
- In the header of the financial statements table
- In notes to the results

❌ Do not treat implied references (e.g., in EPS formula) as valid unless the face value is explicitly stated.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Second line: Face Value: ₹[NUMBER] (or "NOT_FOUND" if not found)
3) Then provide detailed analysis:
   - Quote the exact text where face value is mentioned (if found)
   - Explain why the disclosure is compliant or non-compliant
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Example output:
Compliant
Face Value: ₹2
The face value of ₹2 per share is clearly disclosed in the EPS section on page 3: "Earnings per share (face value ₹2 each)..."

Keep the response structured and informative with clear evidence from the document.`,

 is_face_value: `Analyze the document to determine whether the face value of shares is disclosed and extract the stated value.

TASK:
Search the entire document for any reference to the face value of equity shares and assess compliance based on the presence of a clear numeric disclosure.

SPECIFIC INSTRUCTIONS:

Review the full document carefully for any mention of face value.
Look for phrases such as:

"Face value of equity shares is ₹X each"

"Face value per share ₹X"

"Equity share of face value ₹X/-"

"Face value ₹X per share"

"Share capital of ₹X each"

Any similar wording that clearly indicates the face value per share

Ensure that the reference includes a specific numeric amount (e.g., ₹1, ₹2, ₹5, ₹10, etc.).

COMPLIANCE CRITERIA:

COMPLIANT:
Face value is clearly mentioned with a specific numeric amount.

NON-COMPLIANT:
Face value is not mentioned anywhere in the document, or the reference is unclear, incomplete, or lacks a numeric amount.

OUTPUT FORMAT (STRICT):

First line: exactly one of the following (based strictly on findings):

Compliant
or
Non-Compliant

Second line:

Face Value: ₹[AMOUNT]
or
Face Value: NOT_FOUND

Then provide detailed analysis:

Quoted Text:
Include the exact quoted disclosure(s) where the face value is mentioned (if found).

Explanation:
Clearly explain why the document is compliant or non-compliant based on the presence or absence of a clear numeric face value disclosure.

Recommendations:

If compliant:
"No further recommendations required."

If non-compliant:
Recommend adding an explicit disclosure such as:
"Face value of equity shares is ₹10 each" in the Share Capital section or Notes to Accounts for clarity and regulatory compliance.

Page References:
Include page numbers, note numbers, or section titles where the disclosure appears (if available). If not found, state "N/A".
`,
  eps_pat_calculation: `Verify EPS calculation in the Statement of Financial Results.

TASK:
Check whether EPS is computed using Profit After Tax (PAT) — and not Total Comprehensive Income (TCI) — and validate the EPS calculation for all reporting periods presented.

KEY CHECKS:

EPS Numerator Validation

Confirm that EPS is calculated using PAT / Profit for the period.

If EPS appears to be derived from Total Comprehensive Income instead of PAT, treat this as non-compliant.

Share Count Validation

Determine number of shares using:
Paid-up Equity Share Capital ÷ Face Value per Share

Use disclosed face value and paid-up capital from the document.

EPS Recalculation

Compute:
EPS = PAT ÷ Number of Shares

Compare the computed EPS with the disclosed EPS for each reporting period.

Allow minor rounding differences (±0.01).

OUTPUT FORMAT — TABLE STRUCTURE (MANDATORY)

Final result must strictly follow this order:

First Line:
Compliant
or
Non-Compliant

Second Line:
EPS Basis: PAT
or
EPS Basis: TCI

Third Line:
Share Count: ₹[Paid-up Capital] crores ÷ ₹[Face Value] = [Number of Shares] crores shares

Then provide the table exactly in this structure:

Period	PAT (₹ Crores)	Share Count (Crores)	Computed EPS	Disclosed EPS	Status
Current Quarter	[PAT Amount]	[Share Count]	₹[Computed]	₹[Disclosed]	[Match/No Match]
Year-to-Date	[PAT Amount]	[Share Count]	₹[Computed]	₹[Disclosed]	[Match/No Match]
Prior Year	[PAT Amount]	[Share Count]	₹[Computed]	₹[Disclosed]	[Match/No Match]

EVALUATION RULES:

If EPS is based on PAT and all computed EPS values match disclosed EPS (allowing minor rounding), return:
Compliant

If EPS is based on TCI OR any computed EPS materially differs from disclosed EPS, return:
Non-Compliant

Do not average periods.

Do not ignore mismatches in any single column.

Every reported period must be validated independently.

Ensure calculations are numerically accurate and audit-defensible.`,

  line_items_previous_year_extraction: `Task: Identify all detailed line items in the Previous Year End Statement of Results.

Go to the section titled "Standalone/Consolidated Audited Financial Results for the Year Ended [date]".

Extract the full list of line items presented in the Statement of Results (e.g., Revenue from Operations, Other Income, Total Income, Finance Costs, etc.).

Capture the exact wording and ordering of these line items.

Output: Return the complete list of line items from the Previous Year End Statement of Results.`,

  line_items_current_quarter_extraction: `Task: Identify all detailed line items in the Current Quarter Statement of Results.

Go to the section titled "Standalone/Consolidated Unaudited/Audited Financial Results for the Quarter Ended [date]".

Extract the full list of line items presented in the Statement of Results.

Capture the exact wording and ordering of these line items.

Output: Return the complete list of line items from the Current Quarter Statement of Results.`,

    line_items_comparison: `Task: Compare line items between the Previous Year End and the Current Quarter Statements of Results.

**IMPORTANT: This is a COMPLIANCE CHECK, not a wording perfection check. If all items are present with only minor wording variations, the result MUST be "Compliant".**

**STATUS DETERMINATION RULE:**
- If Missing Items = None → Status = "Compliant"
- If Material Differences = None → Status = "Compliant"  
- If all Previous Year items are present → Status = "Compliant"
- If essential items are missing or materially different → Status = "Check"
- Do NOT use "Non-Compliant" — use only "Compliant" or "Check"

Using outputs from Prompt 1 and Prompt 2, perform a practical line-by-line comparison with realistic compliance assessment:

**PRACTICAL COMPARISON REQUIREMENTS:**
1. **Core Principle**: All essential line items from Previous Year End must be present in Current Quarter
2. **Additional Items**: Current Quarter may have MORE items than Previous Year (this is acceptable and expected)
3. **Wording Flexibility**: Minor wording variations are acceptable if they don't change the fundamental meaning
4. **Acceptable Variations**:
   - "period/year" vs "period" (grammatical simplification)
   - "loss (net)" vs "(gain)/loss (net)" (business reality change)
   - "expense/(credit)" vs "expense" (simplification)
   - Currency symbols (₹ vs Rs.)
   - Note number changes (Note 4 vs Note 5)

**COMPLIANCE ASSESSMENT LOGIC:**
- **Compliant**: All Previous Year items present + acceptable wording variations + additional items allowed
- **Check**: Missing essential items OR wording changes that materially mislead users — requires manual review
- **Focus on**: Missing items, not minor wording improvements
- **CRITICAL RULE**: If Missing Items = None AND Material Differences = None, then status MUST be "Compliant"
- **ACCEPTABLE VARIATIONS**: Foreign exchange changes, tax simplifications, grammatical improvements, note number changes
- **IMPORTANT**: Do NOT use "Non-Compliant". The only valid statuses are "Compliant" or "Check"

**MANDATORY COMPLIANCE RULES:**
1. If ALL Previous Year items are present in Current Quarter → Status = "Compliant"
2. If Missing Items = None → Status = "Compliant" 
3. If Material Differences = None → Status = "Compliant"
4. If essential items are missing → Status = "Check" (NOT "Non-Compliant")
5. Wording changes that don't change meaning are NOT compliance failures
6. NEVER use "Non-Compliant" — only "Compliant" or "Check"

**POINT-WISE ANALYSIS STRUCTURE:**
1. **Item-by-Item Checklist**: Go through each Previous Year item systematically - each item must be a separate bullet point
2. **Presence Verification**: Confirm each item exists in Current Quarter (exact or equivalent) - format as individual bullet points
3. **Difference Categorization**: Classify differences as Acceptable, Minor, or Material - each difference as separate bullet point
4. **Missing Item Identification**: Highlight any truly missing essential items - each missing item as separate bullet point
5. **Additional Item Recognition**: Acknowledge new items in Current Quarter - each new item as separate bullet point

**CRITICAL FORMATTING REQUIREMENT:**
- Each line item comparison must be a separate bullet point
- Do NOT group multiple items in one bullet point
- Each bullet point should focus on ONE specific item or comparison
- Use format: "• [Item Name]: [Status/Description] ✓/✗"
- NO explanatory text before bullet points
- NO paragraph-style descriptions
- Each section must start with header followed immediately by bullet points

**STRICT OUTPUT FORMAT - FOLLOW EXACTLY:**
[Status]
Missing Items: [None or bullet points]
Renamed Items: [bullet points]
Additional Items: [bullet points]
Material Differences: [None or bullet points]
Acceptable Variations: [bullet points]
Point-wise Analysis: [bullet points]
Compliance Justification: [bullet points]
Recommendations: [bullet points]

**ENHANCED OUTPUT FORMAT:**
- First line: "Compliant" or "Check" (MUST match the compliance justification — NEVER use "Non-Compliant")
- Second line: "Missing Items: [Bullet-point list of truly missing items or 'None']"
- Third line: "Renamed Items: [Bullet-point list with before → after format]"
- Fourth line: "Additional Items: [Bullet-point list of new items in Current Quarter]"
- Fifth line: "Material Differences: [Bullet-point list of items that materially affect comparability or 'None']"
- Sixth line: "Acceptable Variations: [Bullet-point list of minor changes that don't affect meaning]"
- Seventh line: "Point-wise Analysis: [Bullet-point systematic item-by-item comparison - each item must be a separate bullet point]"
- Eighth line: "Compliance Justification: [Bullet-point format - break down reasoning into individual points]"
- Ninth line: "Recommendations: [Bullet-point priority-based actionable steps]"

**CRITICAL STATUS RULE:**
- If Missing Items = None AND Material Differences = None → First line MUST be "Compliant"
- If Compliance Justification says "COMPLIANT" → First line MUST be "Compliant"
- The first line status MUST match the compliance justification

**BULLET POINT FORMATTING RULES:**
- Use "•" symbol for bullet points
- Each bullet point on a new line
- Start each bullet point with "• " (bullet + space)
- Keep bullet points concise and clear
- NO paragraph text - everything must be in bullet point format
- Each section header must be followed immediately by bullet points
- Do NOT write explanatory text before bullet points

**EXAMPLE OUTPUT:**
Compliant
Missing Items: None
Renamed Items: 
• "Foreign exchange loss (net)" → "Foreign exchange (gain)/loss (net)"
• "Total tax expense/(credit) (net)" → "Total tax expense (net)"
• "Profit for the period/year" → "Profit for the period"
Additional Items: None
Material Differences: None
Acceptable Variations: 
• Foreign exchange change reflects actual business reality (gain/loss vs loss-only)
• Tax expense simplification removes unnecessary notation without losing meaning
• "period/year" to "period" is grammatical improvement
• Note number changes (Note 4 vs Note 5) are acceptable
Point-wise Analysis: 
• Revenue from operations: Present with identical wording ✓
• Other income: Present with identical wording ✓
• Total income: Present with identical wording ✓
• Cost of materials consumed: Present with identical wording ✓
• Changes in inventories of finished goods: Present with identical wording ✓
• Employee benefits expense: Present with identical wording ✓
• Finance costs: Present with identical wording ✓
• Depreciation and amortisation expense: Present with identical wording ✓
• Other expenses: Present with identical wording ✓
• Total expenses: Present with identical wording ✓
• Profit before exceptional items and tax: Present with identical wording ✓
• Exceptional items: Present with identical wording ✓
• Profit before tax: Present with identical wording ✓
• Tax expense: Present with simplified wording (acceptable) ✓
• Profit for the period: Present with grammatical simplification ✓
• Other comprehensive income: Present with identical wording ✓
• Total comprehensive income: Present with identical wording ✓
• Basic earnings per share: Present with identical wording ✓
• Diluted earnings per share: Present with identical wording ✓
Compliance Justification: 
• All essential line items from Previous Year End are present in Current Quarter
• Missing Items = None AND Material Differences = None
• The wording changes identified are acceptable variations that reflect business reality or minor simplifications
• No items are missing, and no changes materially mislead users
Recommendations: 
• LOW PRIORITY: Consider maintaining consistent terminology across periods for better comparability
• No immediate action required as all changes are acceptable and don't affect financial statement integrity

**FINAL COMPLIANCE CHECK:**
Before finalizing your response, verify:
1. Are all Previous Year items present in Current Quarter? If YES → Status = "Compliant"
2. Are there any missing essential items? If NO → Status = "Compliant"  
3. Are there only minor wording variations? If YES → Status = "Compliant"
4. Only mark "Non-Compliant" if essential items are actually missing or materially misleading

**CRITICAL OUTPUT INSTRUCTIONS:**
- Output ONLY the structured format shown above
- Do NOT include "Analysis Details", "Evidence Found", "Reasoning" sections
- Do NOT include explanatory paragraphs before bullet points
- Do NOT include "💡" or "🔍" symbols
- Output should be clean, structured bullet points only
- Follow the exact format: Status → Missing Items → Renamed Items → etc.`,

  currency_unit_main_document: `You are reviewing the MAIN DOCUMENT (current year financial results) to extract the currency measurement units used throughout the document. This will be used for comparison with a consolidated/standalone document.

Your task:
Search the entire document, including tables, headers, and footnotes, for currency measurement units such as:
- "₹ in Crores" or "₹ Crores"
- "₹ in Lakhs" or "₹ Lakhs" 
- "₹ in Thousands" or "₹ Thousands"
- "₹ in Millions" or "₹ Millions"
- "Amounts in Crores" or "Amounts in Lakhs"
- Or similar variants

Look for these units in:
- Table headers
- Column headers
- Footnotes
- Statement headers
- Any currency denomination labels

❌ Do not confuse with face value or share price denominations.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Second line: Currency Unit: [UNIT] (or "NOT_FOUND" if not found)
3) Then provide detailed analysis:
   - Quote the exact text where currency unit is mentioned (if found)
   - Explain why the disclosure is compliant or non-compliant
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Example output:
Compliant
Currency Unit: Crores
The currency unit "₹ in Crores" is clearly disclosed in the table header on page 2: "Statement of Unaudited Standalone Financial Results (₹ in Crores)..."

Keep the response structured and informative with clear evidence from the document.`,

  currency_unit_consolidated_standalone: `You are reviewing the CONSOLIDATED/STANDALONE DOCUMENT to extract the currency measurement units used throughout the document. This will be used for comparison with the main document.

Your task:
Search the entire document, including tables, headers, and footnotes, for currency measurement units such as:
- "₹ in Crores" or "₹ Crores"
- "₹ in Lakhs" or "₹ Lakhs" 
- "₹ in Thousands" or "₹ Thousands"
- "₹ in Millions" or "₹ Millions"
- "Amounts in Crores" or "Amounts in Lakhs"
- Or similar variants

Look for these units in:
- Table headers
- Column headers
- Footnotes
- Statement headers
- Any currency denomination labels

❌ Do not confuse with face value or share price denominations.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Second line: Currency Unit: [UNIT] (or "NOT_FOUND" if not found)
3) Then provide detailed analysis:
   - Quote the exact text where currency unit is mentioned (if found)
   - Explain why the disclosure is compliant or non-compliant
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Example output:
Compliant
Currency Unit: Crores
The currency unit "₹ in Crores" is clearly disclosed in the table header on page 2: "Statement of Unaudited Consolidated Financial Results (₹ in Crores)..."

Keep the response structured and informative with clear evidence from the document.`,

  standalone_notes_extraction: `You are reviewing the STANDALONE DOCUMENT to extract all notes from the Notes to the Financial Statements section. This will be used for comparison with the consolidated document.

Your task:
1. Locate the "Notes to the Financial Statements" section in the standalone document
2. Extract all note numbers and their corresponding titles/headings
3. For each note, capture:
   - Note number (e.g., "Note 1", "Note 2", etc.)
   - Note title/heading (e.g., "Significant Accounting Policies", "Property, Plant and Equipment", etc.)
   - Brief description of the note content (1-2 sentences)

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant" (Compliant if notes section found, Non-Compliant if not found)
2) Second line: Total Notes Found: [NUMBER]
3) Then provide detailed analysis:
   - List all notes found with their numbers and titles
   - Quote specific text from the document that supports your findings
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  consolidated_notes_extraction: `You are reviewing the CONSOLIDATED DOCUMENT to extract all notes from the Notes to the Financial Statements section. This will be used for comparison with the standalone document.

Your task:
1. Locate the "Notes to the Financial Statements" section in the consolidated document
2. Extract all note numbers and their corresponding titles/headings
3. For each note, capture:
   - Note number (e.g., "Note 1", "Note 2", etc.)
   - Note title/heading (e.g., "Significant Accounting Policies", "Property, Plant and Equipment", etc.)
   - Brief description of the note content (1-2 sentences)

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant" (Compliant if notes section found, Non-Compliant if not found)
2) Second line: Total Notes Found: [NUMBER]
3) Then provide detailed analysis:
   - List all notes found with their numbers and titles
   - Quote specific text from the document that supports your findings
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  standalone_consolidated_notes_comparison: `You are performing a one-way comparison of Notes to the Financial Statements
from a STANDALONE financial statement to a CONSOLIDATED financial statement.

IMPORTANT SCOPE LIMITATION:
- This exercise checks ONLY whether every note disclosed in the STANDALONE
  financial statements is appropriately reflected in the CONSOLIDATED
  financial statements.
- Do NOT check whether consolidated notes exist that are absent in the
  standalone statements.
- Do NOT perform a consolidated-to-standalone comparison.

Your task:
1. Identify and list all notes disclosed in the STANDALONE financial statements.
2. For EACH standalone note, verify whether there is a corresponding note
   in the CONSOLIDATED financial statements, either:
   - As the same note number, OR
   - Under a different note number or combined note, provided the substance
     is fully covered.

For each standalone note, assess whether the corresponding consolidated note:
- Exists in the consolidated financial statements
- Retains the same accounting policy or principle
- Appropriately reflects consolidation adjustments, such as:
  - Aggregation of subsidiary figures
  - Elimination of inter-company balances or transactions
  - Inclusion of non-controlling interest, where applicable
- Covers all material aspects disclosed in the standalone note

Key considerations:
- Reasonable differences due to consolidation are acceptable.
- Standalone notes may be merged, reorganized, or renumbered in the consolidated
  financial statements, provided substance is preserved.
- Any standalone note whose substance is missing or inadequately reflected
  in the consolidated notes must be flagged.

STRICT OUTPUT FORMAT (MANDATORY):
1) First line: exactly ONE word only:
   - "Compliant" → if ALL standalone notes are adequately reflected
   - "Check" → if ANY standalone note is missing, merged inadequately,
               or materially incomplete in the consolidated statements

2) Detailed analysis:
   - List EACH standalone note individually
   - Identify the corresponding consolidated note (or state "Not found")
   - Quote relevant supporting text from BOTH documents
   - Explain adequacy or gaps for each note
   - Where gaps exist, clearly explain what requires checking or correction
   - Include page references wherever available

Do NOT use the terms:
- Non-Compliant
- Non-Compliance
- Partial Compliance

The final conclusion must be ONLY "Compliant" or "Check".`,

  basic_diluted_eps: `You are analyzing the "Statement of Unaudited Standalone Financial Results for the Three Months Ended June 30, 2025."

At the end of the financial results table, verify whether both of the following line items are explicitly disclosed for each reported period (e.g., current quarter, previous quarter, year-to-date, prior year):

Earnings Per Share (EPS) – Basic

Earnings Per Share (EPS) – Diluted

REVIEW INSTRUCTIONS:

Carefully examine the bottom section of the main results table where EPS is typically presented.

Confirm that:

The term “Basic” EPS is clearly stated.

The term “Diluted” EPS is clearly stated.

Both have numeric values disclosed.

Values are presented across all relevant reporting columns.

If either:

One of the two (Basic/Diluted) is missing,

A value is not disclosed for a particular period, or

The labeling is ambiguous or incomplete,

treat it as incomplete disclosure.

OUTPUT FORMAT (STRICT):

If compliant:

Compliant — Both Basic and Diluted EPS are disclosed for all applicable periods.

Quoted EPS Disclosure:
[Include exact quoted line items as appearing in the document]

Periods Reviewed:
[List columns reviewed — e.g., Current Quarter, Previous Quarter, Year-to-Date, Prior Year]

Explanation:
Confirm that both Basic and Diluted EPS are clearly labeled and numerically disclosed for each period.

If non-compliant:

Non-Compliant — EPS disclosure is incomplete. Missing either Basic or Diluted EPS.

Quoted EPS Disclosure:
[Quote whatever EPS-related text is present]

Missing Periods / Elements:
[Specify whether Basic or Diluted is missing and for which columns]

Explanation:
Clearly explain what is absent or ambiguous in the disclosure.

Ensure the response is precise, evidence-based, and suitable for audit documentation.`,


not_annualised_eps: `You are reviewing the "Statement of Unaudited Standalone Financial Results for the Three Months Ended June 30, 2025."

TASK:
Verify whether the Earnings Per Share (EPS) disclosure for the quarterly/interim period is clearly marked as “Not Annualised” (or an equivalent phrase), confirming that the EPS figures are not extrapolated for the full financial year.

INSTRUCTIONS:

Focus Area:
Carefully examine the notes or footnotes section immediately below the main financial results table. Also review the EPS line item area within the table itself.

Search for phrases such as:

“Not Annualised”

“EPS is not annualised”

“Earnings per share (not annualised)”

“(not annualised for the quarter)”

Any equivalent wording that clearly indicates the EPS figures are not annualised

Context Rules:

The phrase may appear in brackets, parentheses, or within an explanatory note.

It does not need to be numerically tied to the EPS amount — its presence anywhere in the notes or EPS section is sufficient.

COMPLIANCE CRITERIA:

COMPLIANT:
The document explicitly includes the phrase “Not Annualised” or equivalent wording.

NON-COMPLIANT:
The phrase (or equivalent wording) is absent from both the EPS section and the accompanying notes.

OUTPUT FORMAT (STRICT):

First line:

Compliant
or
Non-Compliant

Then provide detailed analysis:

Quoted Text:
Provide the exact phrase or sentence found in the document (if applicable).

Explanation:
Clearly state whether the “Not Annualised” phrase is present or missing and why the disclosure is compliant or non-compliant.

If Non-Compliant:
Clearly specify what should be added (for example: insert “(Not Annualised for the quarter)” immediately below the EPS heading or within the notes section).

Recommendations:

If compliant:
“The current disclosure is clear and compliant. No further changes needed.”

If non-compliant:
“Include a clear statement such as ‘EPS is not annualised for the quarter’ in the notes section.”

Page References:
Mention page numbers or note numbers where the phrase appears. If not found, state “N/A”.

Ensure the response is precise, evidence-based, and suitable for audit documentation.
`,
  audit_committee_board_approval: `Verification Prompt: Audit Committee Review and Board Approval Disclosure

Context / Where to Check:
Review the Notes to the Statement of (Standalone/Consolidated) Financial Results and any related disclosure blocks on the results page(s), including:

Footnotes below the financial results tables

“Other Information” notes

Any note referencing a Board Meeting

Approval or review statements accompanying the results

What to Verify (must relate to the current reported results):

Identify clear and explicit statements confirming BOTH of the following:

The financial results were reviewed by the Audit Committee
Acceptable variations include:

“Reviewed by the Audit Committee”

“Reviewed by the Audit Committee of the Board”

“Reviewed by the Audit & Risk Committee”

The financial results were approved by the Board of Directors
Acceptable variations include:

“Approved by the Board of Directors”

“Considered and approved by the Board of Directors”

“Taken on record by the Board of Directors”

Disambiguation Rules:

Statements referring only to “reviewed by statutory auditors” or “reviewed by independent auditors” do NOT satisfy the Audit Committee review requirement.

The wording is case-insensitive; minor punctuation or formatting differences are acceptable.

The two confirmations (Audit Committee review and Board approval) may appear in the same note or in separate notes — but both must clearly relate to the same set of financial results being analyzed.

Compliance Logic:

Compliant = Both elements are clearly present:
(a) Audit Committee review
AND
(b) Board of Directors approval / consideration / taken on record

Non-Compliant =

Either element is missing, OR

Only auditor review is mentioned without explicit Audit Committee review, OR

Board approval is not clearly stated

FINAL OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Then provide detailed analysis structured as follows:

Quoted Text:
Include exact excerpts from the document that support your conclusion.

Explanation:
Clearly explain why the document meets or does not meet both required elements.

If Non-Compliant:
Specify precisely which element is missing (Audit Committee review or Board approval) and why the existing disclosure is insufficient.

Recommendations:
Provide specific corrective language, such as:
“The Unaudited Financial Results were reviewed by the Audit Committee and approved by the Board of Directors at their respective meetings held on [date].”

Page References:
Mention page numbers, footnote numbers, or section titles where the relevant disclosures appear. If absent, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for audit documentation.`,

  major_events_notes: `You are analyzing the "Statement of Unaudited Standalone Financial Results for the Three Months Ended June 30, 2025" and the accompanying notes.

Your task is to check whether the notes include clear and adequate disclosures about any major corporate events, including but not limited to:

Mergers or amalgamations

Acquisitions of subsidiaries or businesses

New issue of shares (including rights issue, bonus issue, QIP, private placement)

Buyback of shares

Corporate restructuring, demergers, spin-offs

Significant Board decisions post-quarter

✅ You should search the notes section for relevant keywords like:

"Merger", "Acquisition", "Scheme of arrangement", "Rights issue", "Bonus issue", "Allotment of shares", "Buyback", "Preferential issue", etc.

❗Even if such events are not reported, a note explicitly stating "No major events during the quarter" is acceptable.

❌ If any such event is expected based on news, filings, or is reported in the results (e.g., shown under Exceptional Items), but no explanatory note is given, raise a warning.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,




  audit_committee_board_dates: `You are analyzing the "Statement of Unaudited/Audited Standalone Financial Results for the Three Months Ended June 30, 2025" along with its Notes section.

Your task consists of two structured parts:

Part A — Disclosure of Meeting Dates

Objective:
Verify that the date of:

The Audit Committee meeting, and

The Board of Directors meeting

(at which the financial results were reviewed and approved)

are BOTH clearly disclosed in the notes or accompanying disclosure section.

Look for statements such as:

“These financial results were reviewed by the Audit Committee and approved by the Board of Directors at their meetings held on July 20, 2025.”

“The Audit Committee reviewed the results on [date] and the Board approved them on [date].”

Compliance Requirement:

Both dates must be explicitly stated and clearly tied to the current set of financial results.

If either:

Audit Committee meeting date is missing, OR

Board meeting date is missing, OR

The wording is ambiguous or unclear,

Return:

Non-Compliant — Audit Committee or Board meeting dates not clearly disclosed.

Only if BOTH dates are clearly disclosed, proceed to Part B.

Part B — Chronological Consistency Check

Objective:
Review all other dates mentioned anywhere in the results and notes section, including but not limited to:

Dividend declaration/payment dates

Acquisition or agreement dates

UDIN date

Review report date

Footers or signature dates

Any disclosed event dates

Chronological Logic Rules:

Let the Board Meeting Date = Reference Date

The following are COMPLIANT:

Any event date BEFORE the Board meeting date

Any event date ON THE SAME DAY as the Board meeting date

Future expected dates (e.g., “expected completion in April 2026”)

Future deadlines

Future anticipated events

The following is NON-COMPLIANT:

Any ACTUAL EVENT that is stated as having occurred AFTER the Board meeting date

Examples (if Board meeting date = August 8, 2025):

✅ July 30, 2025 (agreement signed) → Compliant
✅ August 8, 2025 (same-day event) → Compliant
✅ April 2026 (completion expected) → Compliant
✅ December 2025 (deadline) → Compliant
❌ August 9, 2025 (actual event occurred) → Non-Compliant

Important Distinction:

Future expected dates are acceptable.

Only actual completed events occurring after the Board meeting date create inconsistency.

FINAL OUTPUT FORMAT (STRICT)

If Part A fails:

Non-Compliant — Audit Committee or Board meeting dates not clearly disclosed.

Provide:

Quoted text reviewed

Clear explanation of what is missing

Page references (if available)

If Part A passes but Part B fails:

Non-Compliant — Date inconsistency found. An actual event date is later than Board meeting date.

Provide:

Board meeting date

Conflicting event date

Quoted evidence

Explanation of inconsistency

Page references

If both Part A and Part B pass:

Compliant — Dates of Audit Committee and Board meetings are mentioned and all actual event dates are consistent.

Provide:

Audit Committee meeting date (quoted)

Board meeting date (quoted)

Confirmation that all other actual event dates are on or before the Board meeting date

Page references

Ensure the response is structured, evidence-based, and suitable for audit documentation.`,

 basic_logic_issues: `Analyze the document titled "Statement of Unaudited Standalone Financial Results for the Three Months Ended June 30, 2025" to generally verify the logical accuracy and clarity of all line item labels.

TASK:
Review whether each line item label in the main results table appears logical, standard, and meaningful in the context of financial reporting.

STEP-BY-STEP INSTRUCTIONS:

Review all sections of the results table carefully, especially under:

Income

Expenses

Profit/Loss and Tax

Other Comprehensive Income (OCI)

Equity and Earnings per Share (EPS)

Identify and flag any potential issues, including:

Nonsensical, incomplete, or accidentally pasted text (e.g., “above unaudited results,” “refer below,” etc.)

Typographical or spelling errors (e.g., “Expensses,” “Intrest Paid”)

Narrative or explanatory text incorrectly included as a line item

Repetitions, misalignments, or formatting issues that affect readability

Missing or illogical sequencing between income, expenses, and profit lines

Check for logical flow and structure, broadly following:

Income → Total Income → Expenses → Profit Before Tax → Tax → Profit After Tax → Other Comprehensive Income → Total Comprehensive Income → Equity → EPS

Ensure each label represents a valid and standard financial metric or disclosure.

COMPLIANCE CRITERIA:

COMPLIANT: All line item labels appear logical, standard, and meaningful, with no noticeable typographical or structural issues.

Check: One or more line item labels appear illogical, contain possible errors, or disrupt the standard financial statement structure.

When in doubt, prefer Compliant, and use Check only where issues are reasonably evident.
Never return “Non-Compliant” for this review.

OUTPUT FORMAT (STRICT):

First Line:

"Compliant" → if all line items appear logically correct

"Check" → if any issues are reasonably identified

Second Line:

Summary: [Brief 1-line summary of findings]

Then provide detailed analysis:

Overall Assessment:
Describe the overall clarity, structure, and logical sequencing of the table.

Specific Line Item Review:
Provide a short, section-wise review (Income, Expenses, Profit/Loss, OCI, Equity/EPS), commenting on whether each item appears logical and standard.

Quoted Text:
Include examples or excerpts from any problematic lines, if applicable.

Explanation:
Explain why the content appears compliant or why it requires checking.

Recommendations:

If compliant:
"No further recommendations required."

If check:
Clearly specify the suggested corrections (e.g., fix typographical errors, remove narrative text from line items, adjust sequencing).

Page References:
Mention page numbers or table locations if available.

IMPORTANT: Always return "Check" (not "Non-Compliant") when issues are identified. Default to "Compliant" unless there is a clear and reasonable basis to flag concerns.
`,
  qualified_audit_explanation: `You are reviewing the “Independent Auditor’s Report” attached to the Statement of Unaudited/Audited Financial Results for the period ended June 30, 2025.

Your task is to determine:

Whether the audit report contains a qualified opinion, and

If qualified, whether management has:

Provided an explanation in the notes to the results/report, and

Quantified or clearly described the cumulative impact of the qualification on key financial metrics.

STEP 1 — Identify Type of Audit Opinion

Carefully review the Auditor’s Report for language indicating a qualification.

Look for phrases such as:

“We have qualified our opinion because…”

“Subject to the following…”

“Except for the matters described…”

“Basis for Qualified Opinion”

“Adverse Opinion”

“Disclaimer of Opinion”

If the report states:

“In our opinion, the financial results give a true and fair view…”
with no “except for” or qualification language → Treat as Unqualified.

STEP 2 — If Qualified, Verify Management Disclosure

If a qualification exists, check whether the financial results or notes include:

A management explanation, such as:

“The management’s view on the qualification is as follows…”

“Management response to the auditor’s qualification…”

“With respect to the auditor’s observation…”

A quantified or clearly described impact of the qualification on one or more of the following:

Profit or loss

Net worth

Total assets

Turnover / Total income

Earnings per share (EPS)

Any other key financial metric

Look for language such as:

“The impact of the qualification on profit is estimated to be ₹X…”

“Had the adjustment been recorded, profit would have decreased by ₹X…”

“The cumulative impact on net worth is ₹X…”

COMPLIANCE LOGIC

If Audit Report is Unqualified:

Return:

Compliant — Audit report is unqualified; no management explanation required.

If Audit Report is Qualified AND:

✔ Management explanation is disclosed
✔ Impact on financials is quantified or clearly described

Return:

Compliant — Qualified audit report disclosed and explained by management with impact on financials.

If Audit Report is Qualified BUT:

✘ No management explanation found
OR
✘ No quantified/clear impact assessment disclosed

Return:

Non-Compliant — Qualified audit report not followed by management note or impact disclosure.

OUTPUT FORMAT (STRICT)

First Line:
Either:

Compliant
or
Non-Compliant

Detailed Analysis:

Audit Opinion Type

Quote exact opinion paragraph from the audit report

State whether it is Unqualified / Qualified / Adverse / Disclaimer

Qualification Details (if applicable)

Quote the qualification wording

Management Explanation (if applicable)

Quote relevant management note

Confirm whether impact is quantified or described

Impact Assessment

List which financial metrics are affected

State whether impact is clearly quantified

Conclusion Explanation

Clearly justify why the document is compliant or non-compliant

Page References

Mention page numbers or note numbers where relevant disclosures appear

Ensure the response is structured, evidence-based, and suitable for audit documentation.`,

  publication_timeline: `You are reviewing the “Statement of Unaudited/Audited Standalone Financial Results” and its attached “Independent Auditor’s Report.”

Your task requires two structured date validations.

Step 1 — Identify Applicable Deadline

First, determine the reporting quarter:

If Quarter ended June 30, 2025 → Publication deadline: 14th August 2025

If Quarter ended September 30, 2025 → Publication deadline: 14th November 2025

If Quarter ended December 30, 2025 → Publication deadline: 14th February 2026
(45 days from December 30, 2025)

Use the applicable deadline based strictly on the reporting period mentioned in the financial results.

Part A — 45-Day Timeline Compliance

Identify:

Date of the Standalone Financial Results (Board approval / publication date)

Date of the Independent Auditor’s Report

Verify:

Both dates must be on or before the applicable 45-day deadline.

If either document is dated after the deadline → Non-Compliant.

Part B — Chronological Sequence Validation

Verify chronological order:

The Audit Report date must be the same as or later than the Standalone Financial Results date.

The Audit Report cannot be dated earlier than the financial results it refers to.

If the audit report pre-dates the financial results → Non-Compliant.

OUTPUT FORMAT (STRICT)

If all conditions are satisfied:

Compliant — Standalone results and audit report published within the 45-day deadline; audit report dated same day or after financial results.

If either document is dated after the applicable deadline:

Non-Compliant — Publication not done within the 45-day deadline; dated after the prescribed deadline.

If audit report is dated before the financial results:

Non-Compliant — Audit report is dated earlier than the standalone financial results, which is chronologically incorrect.

DETAILED ANALYSIS (MANDATORY BELOW THE FIRST LINE)

Include:

Reporting Quarter Identified

Applicable Deadline

Standalone Financial Results Date (quoted)

Audit Report Date (quoted)

Timeline Assessment (before/after deadline)

Chronological Assessment (proper order or not)

Page references (if available)

Ensure the conclusion is strictly evidence-based and regulator-defensible.`,

eom_para_explanation: `You are reviewing the “Independent Auditor’s Report” attached to the Standalone/Consolidated Financial Results for the Three/Six Months Ended June/September 30, 2025.

Your review consists of two structured steps.

Step 1 — Detect Emphasis of Matter (EOM)

Carefully examine the Independent Auditor’s Report to determine whether an Emphasis of Matter (EOM) paragraph exists.

Typical indicators include headings or phrases such as:

“Emphasis of Matter”

“Emphasis of Matter — Without qualifying our opinion…”

“We draw attention to Note X…”

“We draw attention to the accompanying financial results…”

If NO Emphasis of Matter paragraph exists in the audit report:

Return:

Compliant

(Emphasis of Matter check not applicable as no EOM paragraph is present.)

Step 2 — Cross-Check with Notes to Accounts

If an Emphasis of Matter paragraph EXISTS:

Identify the specific Note number referenced in the EOM.

Locate the corresponding note in the Notes to Accounts.

Confirm that:

The referenced note number exists.

The note clearly explains the same matter highlighted by the auditor.

The disclosure is sufficiently detailed and consistent with the auditor’s emphasis.

Examples of commonly emphasized matters:

Going concern uncertainty

Pending litigations

Impairment of assets

Significant accounting judgments

Regulatory investigations

Significant uncertainties

Compliance Logic:

Compliant =

EOM paragraph exists, AND

The referenced note exists, AND

The note adequately explains the emphasized matter.

Non-Compliant =

EOM paragraph exists but:

Referenced note number does not exist, OR

The note does not clearly explain the matter, OR

Disclosure is insufficient or inconsistent with the auditor’s emphasis.

FINAL OUTPUT FORMAT (STRICT)

First Line:

Compliant
or
Non-Compliant

Detailed Analysis:

EOM Detection

State whether EOM paragraph exists.

Quote the relevant EOM wording (if present).

Referenced Note

Mention the note number referenced in the EOM.

Quote relevant portion of the corresponding note.

Consistency Assessment

Explain whether the note sufficiently and clearly explains the matter emphasized by the auditor.

Conclusion

Clearly justify why the report is compliant or non-compliant.

Page References

Mention page numbers or note numbers where applicable.

Ensure the response is structured, evidence-based, and audit-defensible.`,


  audit_report_addressee: `You are reviewing the “Independent Auditor’s Report” attached to the Standalone Financial Results for the Three Months Ended June 30, 2025.

Your objective is to validate the formal addressee of the audit/review report.

Step-by-Step Review Instructions

Locate the opening salutation or header line of the Independent Auditor’s Report.

This typically appears at the beginning of the report and may read:

“To the Board of Directors of [Company Name]” ✅

“To the Board of Directors” ✅

“To the Members of [Company Name]” ❌

“To the Shareholders of [Company Name]” ❌

Apply the Regulatory Logic:

For quarterly or interim financial results, the audit/review report must be addressed to the Board of Directors, since such results are approved at a Board meeting and not by shareholders.

Therefore:

If addressed to “Board of Directors” (with or without company name) → Compliant

If addressed to “Members,” “Shareholders,” or any other party → Non-Compliant

OUTPUT FORMAT (STRICT)

First Line:
Compliant
or
Non-Compliant

Detailed Analysis:

Quoted Addressee:
Provide the exact opening line or salutation from the audit report.

Explanation:
Explain clearly whether the addressee satisfies the requirement for interim/quarterly financial results.

If Non-Compliant:
Specify that the correct addressee should be:
“To the Board of Directors of [Company Name]”

Recommendations:
Provide clear corrective wording if necessary.

Page References:
Mention page number or section location where the addressee appears.

Ensure the response is precise, evidence-based, and suitable for regulatory or audit documentation.`,

  independent_auditors_report: `You are reviewing the “Independent Auditor's Report” attached to the Standalone Financial Results for the Three Months Ended June 30, 2025.

Your objective is to verify whether the document contains a proper and clearly identifiable audit report title.

Step-by-Step Review Instructions

Search the entire document for a clear report title indicating that it is an Independent Auditor’s Report.

Accept any of the following variations:

“Independent Auditor's Report”

“Independent Auditors' Report”

“Independent Auditor Report”

“Independent Auditors Report”

Minor differences in:

Capitalization

Spacing

Apostrophe usage

Formatting

are acceptable.

The title may appear:

At the top of the document

Below a company header

After the salutation

Anywhere clearly indicating the nature of the document

Mark as Non-Compliant only if:

No such title appears anywhere in the document, OR

The title is materially different and does not indicate it is an independent auditor’s report, OR

The document is clearly not structured as an audit/review report

OUTPUT FORMAT (STRICT)

First Line:
Compliant
or
Non-Compliant

Detailed Analysis:

Quoted Title:
Provide the exact wording of the title as it appears in the document.

Location in Document:
Specify where the title appears (e.g., top of page 1, below company header, centered heading, etc.).

Explanation:
Explain clearly why the document satisfies or fails the title requirement.

If Non-Compliant:
Specify what is missing (e.g., no formal title, incorrect wording such as “Audit Summary” instead of “Independent Auditor’s Report”).

Recommendations:
State that the document should include a clear heading such as:
“Independent Auditor’s Report” at the beginning of the report for regulatory clarity.

Page References:
Include page numbers where applicable. If not available, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for regulatory review.`,

  


  hy_cce_tallying: `In the Half Yearly (HY) financial results document, verify the consistency of the Cash and Cash Equivalents (CCE) values reported in the Statement of Cash Flows with the CCE values presented in the Statement of Assets and Liabilities for both years presented in the HY report.

Specifically, locate the CCE value at the end of the period in the Statement of Cash Flows and cross-reference this value with the CCE line item within the Statement of Assets and Liabilities for each respective year.

If the CCE values match exactly between the Statement of Cash Flows and the Statement of Assets and Liabilities for both years presented, mark as Compliant. If any discrepancy exists between the two statements for either year, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  cashflow_opening_cce_tallying: `In the Statement of Cash Flows, verify whether the opening cash and cash equivalents (CCE) balance for the current fiscal year matches the closing CCE balance reported in the Statement of Cash Flows for the previous fiscal year.

Locate the line item representing "Cash and Cash Equivalents" or a similar label:
- In the current year's Statement of Cash Flows, find the beginning balance.
- In the prior year's Statement of Cash Flows, find the ending balance.

Compare these two values. If they match exactly, mark as Compliant. If the values do not match or if either value cannot be located, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  cce_breakup_tallying: `In the financial statements, locate the Cash Flow Statement and the Statement of Cash and Cash Equivalents (CCE).

Verify if a breakdown of CCE is provided. If no breakdown is provided, mark as Compliant.

If a CCE breakdown *is* provided:

1. Cross-verify each component of the CCE breakdown (e.g., cash on hand, balances with banks, short-term investments, etc.) with the corresponding line items presented in the Cash Flow Statement.  Ensure the values match precisely.
2. Sum the individual components of the CCE breakdown. Verify that this sum equals the total CCE value reported in the Cash Flow Statement.

If all components of the CCE breakdown reconcile with the Cash Flow Statement, mark as Compliant. If any discrepancies or mismatches are found between the CCE breakdown and the Cash Flow Statement, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  note_references_agreement: `In the Standalone/Consolidated Audited Financial Results and the Statement of Standalone/Consolidated Assets and Liabilities, verify cross-references between line items and their corresponding notes.

For each numerical value or line item in the Results and Statement of Assets that has a note reference (e.g., superscript number, bracketed number), locate the corresponding note in the Notes to the Financial Statements section.

Verify the following:

1. The referenced note exists in the Notes section.
2. The content of the note directly relates and provides further explanation/detail for the referenced line item in the Results or Statement of Assets.  The note content should be consistent with the value and description presented in the referencing line item.

If all cross-references are accurate and the note content agrees with the corresponding line item in both the Results and Statement of Assets, mark as Compliant. If any discrepancies or missing notes are found, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  forex_exchange_difference_disclosure: `In the Statement of Profit and Loss (P&L) and the Notes to Accounts related to Foreign Exchange transactions, verify the following conditions related to companies with foreign exchange bank accounts:

1. **Existence of Exchange Difference:** Check if any exchange difference (gain or loss) arising from foreign exchange transactions is reported.

2. **Separate Line Item for CCE:**  If Currency Conversion Charges/Expenses (CCE) are incurred, verify that they are presented as a separate line item, distinct from the exchange difference.  Both exchange differences and CCE should be clearly identifiable and not combined into a single line item.

3. **Location of Disclosure:** If exchange differences and/or CCE exist, check if they are appropriately disclosed either within the P&L itself or within the accompanying Notes to Accounts, specifically those related to foreign currency transactions.

If the company has foreign exchange bank accounts and both exchange difference (if any) and CCE (if any) are reported as separate line items in either the P&L or the relevant Notes to Accounts, then mark as "Compliant". If the company does not have foreign exchange bank accounts and consequently no exchange differences or CCE, also mark as "Compliant". Otherwise, if either the exchange difference or CCE is missing, combined, or not clearly disclosed for a company with foreign exchange accounts, mark as "Non-Compliant".

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  auditor_review_note: `In the Notes to the Financial Statements section, specifically within the note related to the review or audit of the results (typically titled "Basis of Preparation" or similar), verify the presence of a statement indicating whether the financial results have been reviewed or audited.

If the financial statements have been audited, the note MUST state explicitly that the results have been audited.  For example: "These financial statements have been audited by [Auditor Name]".

If the financial statements have NOT been audited, the note SHOULD state that the results have been reviewed by management, or an equivalent statement clarifying they have not been subject to an external audit.  Example:  "These financial statements have been reviewed by management and have not been audited."

If the note clearly indicates the audited/reviewed status of the results according to the criteria above, mark as Compliant. If the note is absent, ambiguous, or does not clearly state the audited/reviewed status, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  segment_results_assets: `Segment Reporting Verification Prompt (Standalone & Consolidated)

You are required to review the Standalone and Consolidated Financial Results, including the Statement of Assets and Liabilities, to verify whether segment reporting disclosures are present.

Scope of Verification

Check for the presence of segment-related disclosures in any of the following areas:

Financial Results (Standalone / Consolidated)

Revenue by operating segment

Expenses by segment

Profit/Loss by segment

Clear identification and naming of reportable segments

Segment figures presented alongside total/consolidated figures

Statement of Assets and Liabilities (Standalone / Consolidated)

Disclosure of segment assets

Clear attribution of assets to specific operating segments

Assessment Criteria

Compliant → Segment reporting data is present in at least one of the above sections.

Check → Segment reporting data is not found in any of the above sections.

This review only verifies the presence or absence of segment disclosures.
It does NOT assess adequacy, completeness, accounting compliance, or correctness.

🧾 OUTPUT FORMAT (STRICT)

First line: exactly one of:

Compliant
or
Check

Second line:

Highlighted Words:

List all occurrences of the word “segment” and its variations (e.g., segment-wise, operating segment, reportable segment), along with page or note references.

Detailed Analysis:

Evidence Identified

Quote relevant portions of the document (text or table extracts) showing segment disclosures.

Specify whether disclosure appears in:

Standalone Results

Consolidated Results

Statement of Assets & Liabilities

Notes to Accounts

Section-wise Observation

Financial Results Section

Assets & Liabilities Section

Gaps or Limitations (if applicable)

Mention if segment data is present only in one statement (e.g., consolidated only).

Note if only narrative reference exists without numeric disclosure.

Conclusion Explanation

Clearly explain why the document qualifies as Compliant (presence confirmed) or Check (absence across all sections).

Recommendations

If Compliant:
State whether segment disclosure is limited to one section (if applicable).

If Check:
Recommend adding a segment reporting table disclosing:

Segment revenue

Segment profit/loss

Segment assets (if applicable)

Clear identification of operating segments

Page / Note References

Include page numbers, note numbers, or section titles where evidence was found.
If none found, state “N/A”.

Interpretation Guidance

Presence in any one eligible section is sufficient for Compliant.

Absence across all reviewed sections results in Check.

Do not infer regulatory non-compliance or make accounting judgments beyond presence verification.`,

  segment_results_pnl_tallying: `In the 'Segment Reporting' section (typically found within the Notes to the Financial Statements), verify that the total segment revenues, total segment expenses, and total segment results (profit or loss) reconcile with the corresponding line items presented in the 'Statement of Profit and Loss' (also known as the Income Statement).

Specifically, check that:

1. The sum of revenues across all reported segments equals the total revenue reported in the Statement of Profit and Loss.
2. The sum of expenses across all reported segments equals the total expenses reported in the Statement of Profit and Loss.
3. The sum of results (profit or loss) across all reported segments equals the profit or loss before tax reported in the Statement of Profit and Loss.

Perform this check for each period presented (e.g., quarterly, half-yearly, and annual). If any discrepancies are found between the segment results and the consolidated P&L figures for any period, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  segment_depreciation_interest_tallying: `In the 'Notes to Accounts' section, specifically within the segment reporting note (typically titled "Segment Reporting" or similar), locate the disclosures for depreciation, interest expense, and any other relevant line items mentioned in the segment report.  Cross-verify these segment-specific figures with the corresponding line items presented in the consolidated Statement of Profit and Loss (P&L). Ensure the segment totals reconcile precisely to the consolidated P&L amounts for each respective item (depreciation, interest, etc.).  Account for any eliminations or corporate-level adjustments that might be disclosed and explained in the segment reporting note.

For example, if Segment A reports depreciation of 10, Segment B reports 20, and the consolidated P&L shows depreciation of 30, the prompt should verify this alignment. If there are inter-segment eliminations, ensure those are clearly explained and properly accounted for in the reconciliation.

If all segment figures reconcile perfectly with the consolidated P&L after accounting for any disclosed adjustments, mark as Compliant.  If any discrepancies or inconsistencies are found, or if the reconciliation is not clearly explained, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  segment_reporting_reconciliation_pnl: `Prompt: Segment Reporting Reconciliation with P&L

🚨 CRITICAL INSTRUCTION: Read the decision rules carefully. If reconciliation is "impossible" or "not possible" due to missing data, you MUST return "Non-Compliant", not "Compliant".

Step 1: Identify Presence of Segment Reports

Review the financial results for a "Segment Reporting" section or note.

Segment disclosures are typically titled:
- "Segment Reporting"
- "Segment Information" 
- "Segment Results"

If no segment report is present, return: "Not Applicable – No Segment Reporting" and stop.

Step 2: Reconciliation (Only if Segment Report Exists)

If segment results are disclosed, extract from the Segment Results table:
- Depreciation & Amortisation Expense (per segment + unallocated).
- Interest/Finance Costs (per segment + unallocated).

From the Statement of Profit and Loss, extract:
- Depreciation & Amortisation Expense (total).
- Finance Costs / Interest Expense (total).

Check reconciliation:
- Sum of segment figures (including "Unallocated") must equal the corresponding totals in the P&L.
- Allow rounding tolerance of ±1 unit (lakhs/crores).

CRITICAL DECISION RULES (MUST FOLLOW EXACTLY):

RULE 1: If no segment report is found → "Not Applicable – No Segment Reporting"

RULE 2: If segment report exists BUT any of the following conditions are true → "Non-Compliant":
- Missing unallocated amounts for Depreciation & Amortisation
- Missing unallocated amounts for Interest/Finance Costs  
- Insufficient data to perform reconciliation
- Cannot verify if segment totals + unallocated = P&L totals
- Any reconciliation is "impossible" or "not possible"

RULE 3: If segment report exists AND reconciliation is possible AND figures reconcile within tolerance → "Compliant"

⚠️ CRITICAL: If your analysis mentions "impossible", "missing", "cannot be performed", "not possible", or similar phrases regarding reconciliation, you MUST return "Non-Compliant", NOT "Compliant".

⚠️ CRITICAL: The presence of a segment report does NOT automatically mean "Compliant". You must verify complete reconciliation is possible.

Final Output: Return only one of:
- "Not Applicable – No Segment Reporting"
- "Compliant"
- "Non-Compliant"

Output format (STRICT):
1) First line: exactly one of "Not Applicable – No Segment Reporting", "Compliant", or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant, non-compliant, or not applicable
   - If non-compliant, specify what discrepancies were found or what information is missing
   - Provide specific recommendations for improvement
   - Include page references if available

🚨 FINAL CHECK: Before submitting your response, verify that your conclusion matches your analysis. If you mention "impossible", "missing", or "not possible" in your analysis, your conclusion MUST be "Non-Compliant".

Keep the response structured and informative with clear evidence from the document.`,

  cashflow_activities_separation: `Task: Verify that the Statement of Cash Flows discloses cash flows under all three required categories: Operating Activities, Investing Activities, and Financing Activities, as per SEBI LODR and Schedule III of the Companies Act.

Checklist:

1. Locate the Cash Flow Statement (Standalone and/or Consolidated).
   - Ensure the section is clearly titled "Statement of Cash Flows" or similar.

2. Check for Operating Activities:
   - Confirm presence of a section labelled "Cash Flow from Operating Activities" (or equivalent wording).
   - Ensure at least one line item is disclosed under this section.

3. Check for Investing Activities:
   - Confirm presence of a section labelled "Cash Flow from Investing Activities".
   - Ensure at least one line item is disclosed under this section.

4. Check for Financing Activities:
   - Confirm presence of a section labelled "Cash Flow from Financing Activities".
   - Ensure at least one line item is disclosed under this section.

5. Check for Net Cash Flow Presentation:
   - Verify that the statement provides subtotals for each category (Operating, Investing, Financing).
   - Confirm there is a final reconciliation showing:
     Net Cash Flow + Opening Cash Balance = Closing Cash Balance

Decision Rule:
- If all three categories (Operating, Investing, Financing) are clearly presented with subtotals, mark as Compliant.
- If any one category is missing, mark as Non-Compliant.

Final Output: Return only one of:
- "Compliant"
- "Non-Compliant"

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what categories are missing or what information is insufficient
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  segment_results_assets_comprehensive: `Task: Verify whether the financial results include disclosures of Segment Results and Segment Assets, in line with SEBI LODR and Ind AS 108 (Operating Segments).

Steps:

1. Identify Segment Disclosures
   - Review the financial results for a section titled "Segment Reporting", "Segment Results", or "Segment Information".
   - If no such section is found, return "Not Applicable – No Segment Reporting" and stop.

2. Check for Segment Results
   - Within the segment disclosure table, confirm that Segment Revenue/Results are disclosed separately for each reportable segment.
   - Ensure there is also a disclosure of Total Segment Results and a reconciliation to overall Profit Before Tax (PBT).

3. Check for Segment Assets
   - Verify that Segment Assets are disclosed (per segment + unallocated, if applicable).
   - Ensure there is a total Segment Assets figure and a reconciliation to the balance sheet total.

Decision Rule:
- If both Segment Results and Segment Assets are disclosed → Compliant.
- If only one of them is disclosed → Non-Compliant.
- If neither is disclosed → Not Applicable – No Segment Reporting.

Final Output: Return only one of:
- "Compliant"
- "Non-Compliant"
- "Not Applicable – No Segment Reporting"

Output format (STRICT):
1) First line: exactly one of "Compliant", "Non-Compliant", or "Not Applicable – No Segment Reporting".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant, non-compliant, or not applicable
   - If non-compliant, specify what segment disclosures are missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  company_word_highlighting: `Prompt: Highlight Usage of the Word "Company"

Task: Review the entire financial report/document for occurrences of the word "Company" and highlight them, except when the word appears as part of the following phrases:

- "Holding company"
- "Subsidiary company"
- "Associate company"
- "JV company" (or "Joint Venture company")

Steps:

1. Search for every instance of the word "Company" (case-insensitive).
2. Exclude any instances where "Company" immediately follows one of the exempted descriptors (Holding, Subsidiary, Associate, JV/Joint Venture).
3. Highlight all remaining occurrences of "Company" in the text.

Decision Rule:
- If only exempted forms are present (Holding/Subsidiary/Associate/JV company), return "Compliant".
- If any non-exempt use of "Company" is found, return "Non-Compliant" and highlight those occurrences.

Final Output:
1) Highlighted list of all non-exempt instances of "Company" with page references and context.
2) Then return only one of:
   - "Compliant"
   - "Non-Compliant"

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - List all non-exempt instances of "Company" found with page references
   - Quote the specific text containing each highlighted "Company" occurrence
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what needs to be changed
   - Provide specific recommendations for improvement

Keep the response structured and informative with clear evidence from the document.`,

  assets_cashflow_statements_hy: `Check within the financial results/report whether the following statements are explicitly included:

1. Statement of Assets and Liabilities
2. Statement of Cash Flows

Only the existence of these two statements needs to be verified. No further content validation is required.

CRITICAL DECISION RULES (MUST FOLLOW EXACTLY):

RULE 1: If BOTH Statement of Assets and Liabilities AND Statement of Cash Flows are present → "Compliant"

RULE 2: If EITHER Statement of Assets and Liabilities OR Statement of Cash Flows is missing → "Non-Compliant"

RULE 3: If BOTH statements are missing → "Non-Compliant"

⚠️ CRITICAL: If your analysis mentions "missing", "absent", "not contain", "not included", or similar phrases regarding either statement, you MUST return "Non-Compliant", NOT "Compliant".

⚠️ CRITICAL: The presence of only one statement (P&L/Income Statement) does NOT mean "Compliant". You must verify BOTH required statements are present.

Final Output: Return only one of: "Compliant" or "Non-Compliant".

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify which statements are missing
   - Provide specific recommendations for improvement
   - Include page references if available

🚨 FINAL CHECK: Before submitting your response, verify that your conclusion matches your analysis. If you mention "missing" or "absent" in your analysis, your conclusion MUST be "Non-Compliant".

Keep the response structured and informative with clear evidence from the document.`,

standard_audit_report_format_comprehensive: `SIMPLE DECISION TREE:

1. Look at the document title
2. If title contains "Independent Auditor's Review Report" → Go to step 3
3. If title does NOT contain "Independent Auditor's Review Report" or “Independent Auditor's Report” These both titles are okay→ Return "Non-Compliant" and stop

4. If title contains "Independent Auditor's Review Report" or “Independent Auditor's Report”, check for these elements:
   - Addressee to Board of Directors
   - Management's Responsibility section
   - Auditor's Responsibility section  
   - Conclusion section
   - Reference to SRE 2410
   - Mention of "moderate assurance"

5. If ALL elements present → Return "Compliant"
6. If ANY elements missing → Return "Non-Compliant"

CRITICAL: If you write "not an Independent Auditor's Review Report" or "lacks the crucial elements" or "entirely absent" in your analysis, you MUST return "Non-Compliant".

Task: Check if the document is an Independent Auditor's Review Report with all required elements.

Check for the following standard elements in the audit report:

1. **Report Title**
   - Should be "Independent Auditor's Review Report on review of Interim Standalone Financial Results"
   - Or similar standard title for audit/review reports

2. **Addressee**
   - Should be addressed "To the Board of Directors of [Company Name]" It should NOT be addressed to the ‘Shareholders of the {company}’ or not be addressed to ‘Members of the {company}
3. **Management's Responsibility Section**
   - Must state that the statement is the responsibility of the Company's Management
   - Must mention approval by the Board of Directors
   - Must reference Ind AS 34 "Interim Financial Reporting"
   - Must reference Companies Act, 2013 and relevant rules

4. **Auditor's Responsibility Section**
   - Must state the auditor's responsibility to issue a report based on review
   - Must reference SRE 2410 'Review of Interim Financial Information'
   - Must mention "moderate assurance" and "material misstatement"
   - Must clarify that it's a review, not an audit
   - Must state "we do not express an audit opinion"

5. **Conclusion Section**
   - Must state "Based on our review conducted as stated above"
   - Must include "nothing has come to our attention that causes us to believe"
   - Must reference compliance with Ind AS and Regulation 33 of Listing Obligations
   - Must mention "material misstatement"

6. **Other Matter Section (if applicable)**
   - Should address any additional matters or qualifications
   - Should not modify the main conclusion unless explicitly stated

7. **Standard Language and Structure**
   - Should use professional audit terminology
   - Should follow logical flow: Title → Addressee → Management Responsibility → Auditor Responsibility → Conclusion → Other Matters

🚨 CRITICAL DECISION RULES (MUST FOLLOW EXACTLY - NO EXCEPTIONS):

STEP 1: Check if the document is an Independent Auditor's Review Report
- If the document title is NOT "Independent Auditor's Review Report" or similar → IMMEDIATELY return "Non-Compliant"
- If the document is just financial results, P&L, balance sheet, etc. → IMMEDIATELY return "Non-Compliant"

STEP 2: Only if the document IS an Independent Auditor's Review Report, check for standard elements
- If ALL required elements are present → "Compliant"
- If ANY elements are missing → "Non-Compliant"

🚨 MANDATORY CHECK: Before writing your conclusion, ask yourself:
1. Is this document titled "Independent Auditor's Review Report" or similar?
2. If NO → You MUST return "Non-Compliant"
3. If YES → Check if all standard elements are present

🚨 FORBIDDEN: You are FORBIDDEN from returning "Compliant" if:
- The document is "not an Independent Auditor's Review Report"
- The document "lacks the crucial elements"
- Any elements are "missing", "absent", "entirely absent", "completely missing", "not present", or "does not contain"

⚠️ CRITICAL: The presence of financial results or P&L statements does NOT mean "Compliant". You must verify the document is actually an Independent Auditor's Review Report.

FINAL INSTRUCTION: Follow the decision tree above. If you determine the document is "not an Independent Auditor's Review Report" or "lacks crucial elements", you MUST return "Non-Compliant".

Output format:
1) First line: "Compliant" or "Non-Compliant"
2) Then explain your reasoning based on the decision tree above

🚨 FINAL CHECK: Before submitting your response, verify that your conclusion matches your analysis. If you mention ANY of these phrases in your analysis, your conclusion MUST be "Non-Compliant":
- "not an Independent Auditor's Review Report"
- "lacks the crucial elements"
- "missing"
- "absent"
- "entirely absent"
- "completely missing"
- "not present"
- "does not contain"

🚨 CRITICAL: If your analysis contains any of these phrases but you return "Compliant", you are making a SERIOUS ERROR and violating the instructions.

🚨 MANDATORY: Before submitting, read your analysis again. If you see phrases like "not an Independent Auditor's Review Report" or "completely missing", you MUST change your conclusion to "Non-Compliant".

Keep the response structured and informative with clear evidence from the document.`,

  // New checkpoints 11-20
  segment_net_assets_tallying: `In the financial statements, locate the following sections:

- **Statement of Segment Results:** Identify the total net assets value reported for each segment.  Sum these individual segment net asset values to arrive at a total net assets figure.
- **Statement of Assets and Liabilities:** Identify the total net worth (or equivalent term representing total equity) reported.

Compare the total net assets figure calculated from the Statement of Segment Results with the total net worth reported in the Statement of Assets and Liabilities.

If the two figures match precisely, mark as Compliant.  If the figures do not match or if either figure cannot be located, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  segment_explanations: `In the segment reporting section of the financial report (typically within the Management Discussion and Analysis or Results of Operations), verify the presence of clear explanations for each reported segment.

The explanation for each segment must:

- Clearly identify the segment (e.g., by name or product/service category).
- Describe the nature of the segment's operations (what it does).
- Explain the products or services included in the segment.
- Provide sufficient detail for a reader to understand the segment's composition and contribution to the overall business.

If a segment is mentioned in the results table but lacks an adequate explanation as described above, mark as Non-Compliant.  If all reported segments have accompanying explanations that fulfill the above criteria, mark as Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  audit_report_conclusion_opinion: `ou are reviewing the Audit Report section attached to the financial results.

Your task is to perform two structured validations.

Step 1 — Determine the Type of Engagement

Carefully review the beginning of the report to identify whether the engagement is:

A Review (commonly titled “Limited Review Report” or referring to “review conducted in accordance with…”), or

An Audit (commonly titled “Independent Auditor’s Report” and referring to “audit conducted in accordance with Standards on Auditing…”).

This is usually clearly stated in:

The title of the report

The introductory paragraph

The scope paragraph describing the procedures performed

Step 2 — Verify Presence of Appropriate Conclusion or Opinion

A) If the report is a Review:

Verify that the report contains a Conclusion, typically phrased as:

“Based on our review conducted as above, nothing has come to our attention that causes us to believe…”

“We have not identified any material modifications…”

“Nothing has come to our attention…”

A review report must NOT provide an audit opinion.
It must provide a limited assurance conclusion.

B) If the report is an Audit:

Verify that the report contains a formal Opinion, typically phrased as:

“In our opinion, the accompanying financial statements present fairly, in all material respects…”

“In our opinion, the financial results give a true and fair view…”

An audit report must clearly express an opinion.

Compliance Logic

Compliant =

The type of engagement (Review or Audit) is clearly identified, AND

The report contains the correct form of conclusion/opinion corresponding to the engagement type.

Non-Compliant =

Engagement type is unclear, OR

Required conclusion/opinion is missing, OR

A review report contains an audit opinion, OR

An audit report only provides a review-style conclusion.

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Engagement Type Identified

Quote exact wording from the report indicating whether it is a review or audit.

Conclusion / Opinion Extract

Quote the relevant conclusion or opinion paragraph.

Compliance Assessment

Explain why the wording is appropriate (or not appropriate) for the identified engagement type.

If Non-Compliant:

Clearly specify what is missing or incorrect.

State what should be included (e.g., “In our opinion…” for audit, or “Nothing has come to our attention…” for review).

Recommendations

Provide specific corrective suggestions if required.

Page References

Mention page numbers or section headings where relevant.

Ensure the response is structured, evidence-based, and suitable for audit or regulatory review.`,

  ind_as_adoption_disclosures: `In the section discussing the first-time adoption of Indian Accounting Standards (IND AS) within the financial report (typically the Notes to the Financial Statements or a separate standalone section), verify the presence and adequacy of disclosures related to the adoption.

The following disclosures MUST be present and adequately explained:

* Date of transition to IND AS.
*  A reconciliation of the previous Generally Accepted Accounting Principles (GAAP) net assets to IND AS net assets as of the date of transition.
*  A summary of significant accounting policies under IND AS.
*  An explanation of any material adjustments made to the financial statements due to the adoption of IND AS.
*  The impact of IND AS adoption on key financial ratios (e.g., EPS, net profit margin).
*  If applicable, disclosure of exemptions taken under IND AS along with justification.

If all the required disclosures are present and provide sufficient information to understand the impact of IND AS adoption, mark as Compliant. If any of the required disclosures are missing or inadequately explained, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  serial_numbers_sequence: `You are reviewing the entire document to verify the sequential integrity of serial numbers used in all numbered lists and tables.

Scope of Review

Perform the following checks across the entire document:

Identify All Numbered Lists and Tables

Financial result line items

Notes to accounts

Explanatory notes

Annexures

Footnote numbering

Any tabular format containing serial numbers

Sequential Order Validation
For each numbered list or table:

Confirm numbering begins at 1 (or at an explicitly stated alternative starting number).

Verify numbers increment by exactly 1 for each subsequent item.

Ensure no numbers are skipped.

Ensure no numbers are duplicated.

Row Count Reconciliation

Confirm that the total number of rows equals the highest serial number.

Ensure no extra rows exist without serial numbers.

Ensure no serial numbers exist without corresponding rows.

Compliance Logic

Compliant =

All numbered lists and tables follow a correct sequential order.

No gaps, duplicates, or inconsistencies exist.

Row counts align with the highest serial number.

Non-Compliant =

Any skipped number, duplicate number, incorrect increment, or mismatch between row count and serial numbering is identified anywhere in the document.

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Lists/Tables Identified

Identify each numbered list or table reviewed.

Mention page references where available.

Sequential Verification

Quote sample serial number sequences (e.g., “1, 2, 3, 4, 5”).

Highlight any detected gap (e.g., “1, 2, 4” — missing 3).

Highlight duplicates if found.

Row Count Reconciliation

State the total rows observed.

State the highest serial number.

Confirm whether they match.

Conclusion Explanation

Clearly explain why the document is compliant or non-compliant.

If Non-Compliant

Specify exact error(s).

Provide corrective recommendation (e.g., renumber sequentially, adjust row count).

Page References

Include page numbers or section references.

If unavailable, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for audit or regulatory review.`,

  ytd_figures_matching: `In the financial results document, locate the Year-To-Date (YTD) figures presented within the Half-Yearly (HY) or equivalent results section.  Also, locate the quarterly results for both the current quarter and the preceding quarter within the same fiscal year.

Perform the following checks:

1. **YTD Figure Accuracy:** Verify that the reported YTD figures match the sum of the previous quarter's figures and the current quarter's figures, accounting for potential rounding differences.  The YTD figures should be consistent with the cumulative performance reported across the two quarters.  

2. **HY Results Consistency (if applicable):** If HY results are presented, ensure that the figures for the two quarters comprising the HY period accurately sum to the reported HY figures. This check ensures internal consistency within the HY presentation.  If HY results are not explicitly presented, skip this check.

3. **Rounding Consistency:**  Ensure that rounding is applied consistently across all figures used in the comparison (YTD, current quarter, previous quarter, HY). If any discrepancies exist beyond acceptable rounding thresholds, mark as Non-Compliant.


If all the above conditions are met, mark as Compliant. Otherwise, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  segment_figures_bs_tallying: `In the financial statements, specifically the Balance Sheet (BS) section, verify the consistency of segment figures between the last quarter and year-end reports.

Locate the segment reporting data for both the most recent quarter and the fiscal year-end.  For each Balance Sheet line item reported by segment:

1. Sum the individual segment figures reported for the last quarter.
2. Compare this sum to the corresponding total BS line item figure reported in the last quarter's consolidated financial statements.
3. Sum the individual segment figures reported for the fiscal year-end.
4. Compare this sum to the corresponding total BS line item figure reported in the fiscal year-end consolidated financial statements.

If all segment figures for both the last quarter and fiscal year-end individually sum up to their respective consolidated totals in the Balance Sheet, mark as Compliant. If any discrepancies are found between the summed segment figures and the consolidated figures for either period, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  group_definition_usage: `Throughout the entire document, verify the usage of the word 'Group' in reference to the Holding company and its subsidiaries.

1. **Definition Check:**  Examine the document (Glossary, Definitions section, or footnotes) for a definition of the term 'Group.'

2. **Usage Consistency Check:** If a definition of 'Group' IS provided, ensure that all references to the Holding company and its subsidiaries collectively utilize the term 'Group' consistently.  Do not mark as Non-Compliant if "Holding company and its subsidiaries" or similar clear wording is used instead of just "Group" in some instances.

3. **Joint Reference Check:** If a definition of 'Group' IS NOT provided, verify that every instance where either the Holding company OR a subsidiary is mentioned, they are referred to jointly (e.g., "Holding company and its subsidiaries," "the Company and its subsidiaries," or a similarly clear phrasing).  Individual references to either the Holding company or a subsidiary without mention of the other constitute Non-Compliance in this case.

If a definition of 'Group' is present and used consistently as described in point 2, mark as Compliant.

If a definition of 'Group' is absent and joint reference is maintained as described in point 3, mark as Compliant.

Otherwise, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,


  company_name_consistency: `🔹 Company Name Consistency Verification

Context / Where to Check (entire document):
Scan all pages/sections of the financial results and audit documents, including: cover/title pages, headers/footers, "Statement of (Standalone/Consolidated) Audited/Unaudited Financial Results", "Statement of Assets and Liabilities", "Statement of Cash Flows", notes/footnotes, "Limited Review Report" / "Independent Auditor's Report" (including opinion and signature blocks), and annexures (e.g., CARO).

Input:
TypedCompanyName = exact company name provided by the user (e.g., "Tata Motors Limited").

Normalization for comparisons:
- Ignore case and extra internal spaces.
- Ignore a single trailing punctuation mark (e.g., a final "." or ",").
- Do not normalize or equate legal-form words (e.g., "Limited" ≠ "Ltd" for exact match).

Near-Match Definition (trigger a warning if any occur):
Flag occurrences that are almost the same as TypedCompanyName but fail exact match under the above normalization, provided the difference is limited to one or more of the following:
- Legal suffix variants: "Limited" vs "Ltd/Ltd."; "Private" vs "Pvt/Pvt."; "Company" vs "Co/Co."; "Public Limited Company" vs "PLC/Plc."; "LLP" vs "L.L.P."
- "&" vs "and"
- Minor punctuation or hyphenation changes inside the name
- Spacing around punctuation
- Small edit-distance differences where all core tokens are present (Levenshtein similarity ≥ 0.85)

What to Do:
1. Extract every occurrence that appears to be the company name within the specified sections.
2. Compare each to TypedCompanyName using the normalization rules.
3. For any non-exact match that satisfies the Near-Match Definition, emit a warning.

Output format (STRICT):
1) First line: exactly "Warning" (always return Warning status for company name review).
2) Then provide detailed analysis:
   - List all company name occurrences found in the document
   - For each occurrence, show the exact text and location
   - For any near-matches, provide warning in format:
     WARNING | Found: "<ObservedName>" | Location: <Section/Page/Context> | Difference: <e.g., "Limited vs Ltd", "& vs and", "punctuation difference", "minor edit distance">
   - If no near-matches are found, return exactly: "No warnings."
   - Provide specific recommendations for consistency improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  eps_pat_ratio_comparison: `🔹 Multi-Document Verification Prompt
(EPS Consistency & Share-Base Reconciliation — Comparable, Scaled, Audit-Safe)

You are analyzing two financial result documents:

Standalone Financial Results (Document 1)

Consolidated Financial Results (Document 2)

Your objective is to reasonably verify EPS consistency by reconciling the implied weighted-average equity share base between standalone and consolidated results for strictly comparable reporting periods.

This check is intended only to validate mathematical consistency under Ind AS 33 (Earnings Per Share) and must not be used for valuation or performance interpretation.

⚠️ Mandatory Normalisation & Comparability Rules (Apply Carefully)

To ensure ratios are numerically comparable and clearly presented, apply the following rules:

Earnings Base

Standalone → Use Profit After Tax attributable to owners

Consolidated → Use Earnings available for equity shareholders

Unit Normalisation

If earnings are reported in ₹ crores, convert to absolute ₹ by multiplying by 10,000,000

EPS is already per share → do not rescale EPS

Ratio Definition (Use Consistently)

Implied Shares = Normalised Earnings ÷ EPS

This represents the implied weighted-average number of equity shares.

Scaling for Readability

Divide implied shares by 10,000,000

Report final value as:

"Implied Weighted Avg Shares (in crores)"

Comparability Guardrail

Compare periods only if:

Same financial year and

Same period type (Quarter / Nine-Months / Year)

If an exact match exists → use it.

If no exact match exists → mark as Check (do not approximate).

📊 Step 1: Extract Data

Standalone Financial Results (Document 1)

For each reporting period, extract:

Reporting period

PAT attributable to owners (₹ crores)

Basic EPS

Diluted EPS

Calculate:

Implied Basic Shares = (PAT × 10,000,000) ÷ Basic EPS ÷ 10,000,000

Implied Diluted Shares = (PAT × 10,000,000) ÷ Diluted EPS ÷ 10,000,000

Consolidated Financial Results (Document 2)

For each reporting period, extract:

Reporting period

Earnings available for equity shareholders (₹ crores)

Basic EPS

Diluted EPS

Calculate:

Implied Basic Shares = (Earnings × 10,000,000) ÷ Basic EPS ÷ 10,000,000

Implied Diluted Shares = (Earnings × 10,000,000) ÷ Diluted EPS ÷ 10,000,000

📊 Step 2: Identify Comparable Periods

List all reporting periods in both documents.

Identify strictly matching periods (same FY and same period type).

Exclude non-matching periods and flag as Check where applicable.

📊 Step 3: Compliance Comparison

For each comparable period:

Compare Standalone vs Consolidated implied Basic shares.

Compare Standalone vs Consolidated implied Diluted shares.

✅ Decision Rule

Difference ≤ 0.0001 crores → Compliant

Difference > 0.0001 crores → Check

Missing comparable period → Check

The tolerance is intended to accommodate rounding differences only, not structural share-base changes.

🧾 Output Format (STRICT)

First line (single word):

Compliant
or
Check

Default to Compliant unless a clear numerical variance beyond tolerance or a comparability issue is identified.

Detailed Analysis:

Periods identified in each document

Comparable periods selected

Extracted earnings and EPS values (quoted)

Calculated Implied Weighted Avg Shares (in crores)

Side-by-side comparison table

Exact numerical differences

Explanation of results

Identification of periods requiring Check (if any)

Accounting-level reasoning (rounding vs possible share-base change)

Page / note references (if available)

🧠 Interpretation Rules (Important)

This analysis validates EPS arithmetic consistency only.

Do not draw conclusions about valuation, profitability, or performance.

Differences may reasonably arise due to:

Share issuance or ESOP exercise

Conversion of instruments

Differences in share-base application

Disclosure or rounding differences

Use Check where differences are clearly outside tolerance or comparability conditions are not met. Default to Compliant in the absence of confirmed discrepancies.

IMPORTANT: Do NOT use "Non-Compliant" — the only valid statuses are "Compliant" or "Check". NEVER output "Non-Compliant" under any circumstances.`,

  // First prompt: Extract data from standalone document
  eps_pat_standalone_extraction: `🔹 Standalone Document Data Extraction (PAT-to-EPS Analysis)

You are analyzing the STANDALONE Financial Results document to extract PAT-to-EPS ratio data.

Your task is to extract the following data for EACH reporting period found in the document:

📊 Required Data Extraction:
For each reporting period (quarterly/annually):
- Reporting period (e.g., "Quarter ended September 30, 2024")
- Standalone PAT (Profit After Tax attributable to owners)
- Standalone Basic EPS
- Standalone Diluted EPS
- Calculate: Basic Ratio =  Standalone PAT ÷ Basic EPS
- Calculate: Diluted Ratio = Standalone PAT ÷ Diluted EPS

📊 Output Format:
For each period found, provide:
Period: [Period Name]
Standalone PAT: ₹[Amount]
Basic EPS: ₹[Amount]
Diluted EPS: ₹[Amount]
Basic Ratio: [Calculated Value]
Diluted Ratio: [Calculated Value]

If no data is found for any field, mark as "NOT_FOUND".

Quote the exact text from the document that supports each extracted value.

Keep the response structured and clear.`,

  // Second prompt: Extract data from consolidated document
  eps_pat_consolidated_extraction: `🔹 Consolidated Document Data Extraction (PAT-to-EPS Analysis)

You are analyzing the CONSOLIDATED Financial Results document to extract PAT-to-EPS  ratio data.

Your task is to extract the following data for EACH reporting period found in the document:

📊 Required Data Extraction:
For each reporting period (quarterly/annually):
- Reporting period (e.g., "Quarter ended September 30, 2024")
- Consolidated PAT attributable to owners of the company
- Consolidated Basic EPS
- Consolidated Diluted EPS
- Calculate: Basic Ratio =  Consolidated PAT (earnings available for equity shareholders) ÷ Basic EPS
- Calculate: Diluted Ratio = Consolidated PAT (earnings available for equity shareholders) ÷ Diluted EPS

📊 Output Format:
For each period found, provide:
Period: [Period Name]
Consolidated PAT: ₹[Amount]
Basic EPS: ₹[Amount]
Diluted EPS: ₹[Amount]
Basic Ratio: [Calculated Value]
Diluted Ratio: [Calculated Value]

If no data is found for any field, mark as "NOT_FOUND".

Quote the exact text from the document that supports each extracted value.

Keep the response structured and clear.`,

  // Third prompt: Compare and classify the extracted data
  eps_pat_ratio_classification: `🔹 PAT-to-EPS Ratio Comparison and Classification

You are comparing extracted data from two documents:
1. Standalone Financial Results data
2. Consolidated Financial Results data

Your task is to compare the implied weighted-average number of shares (PAT ÷ EPS) and determine the status.

📊 Step 1: Recalculate Implied Shares for BOTH Documents

For EACH reporting period in EACH document, show the full calculation:

**Standalone Implied Shares Calculation:**
For each period:
  Implied Basic Shares = (Standalone PAT in Lakhs × 100,000) ÷ Basic EPS = [result]
  Implied Diluted Shares = (Standalone PAT in Lakhs × 100,000) ÷ Diluted EPS = [result]

**Consolidated Implied Shares Calculation:**
For each period:
  Implied Basic Shares = (Consolidated PAT in Lakhs × 100,000) ÷ Basic EPS = [result]
  Implied Diluted Shares = (Consolidated PAT in Lakhs × 100,000) ÷ Diluted EPS = [result]

IMPORTANT: You MUST show the step-by-step calculation with actual numbers for BOTH Standalone AND Consolidated. Do not skip the Consolidated calculation steps.

📊 Step 2: Identify Comparable Periods
Match periods between datasets (same FY, same period type).

📊 Step 3: Compare
For each matching period, compare Standalone vs Consolidated implied shares side-by-side.

📊 Decision Rules:
- If implied shares match within 0.001 tolerance → Compliant
- If either Basic or Diluted differs beyond tolerance → Check
- If no comparable periods exist → Check
- Do NOT use "Non-Compliant" — use ONLY "Compliant" or "Check"
- NEVER output "Non-Compliant" under any circumstances

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Check" (NEVER "Non-Compliant").
2) Then provide detailed analysis with these sections:

**1. Periods Identified** — list all periods in each dataset

**2. Standalone Implied Shares Calculations** — for each period show the formula with actual numbers and result

**3. Consolidated Implied Shares Calculations** — for each period show the formula with actual numbers and result (same level of detail as Standalone)

**4. Comparison Table** — side-by-side comparison table with columns: Period | Standalone Implied Shares | Consolidated Implied Shares | Difference

**5. Analysis Findings** — explain results and any discrepancies

**6. Recommendations** — specific recommendations if discrepancies found

Keep the response structured and informative with clear evidence.`,




  associates_jv_oci: `In the Statement of Standalone/Consolidated Audited Financial Results (specifically the Profit and Loss section, and Other Comprehensive Income section), verify the following:

1. **Check for the presence of Associates/Joint Ventures (JVs):** Examine disclosures, notes, or any section indicating the existence of associates or JVs.

2. **Conditional Check for Other Comprehensive Income (OCI) related to Associates/JVs:**  IF associates or JVs are identified in step 1, THEN verify if any OCI component is specifically attributed to these associates/JVs.  This may appear as a separate line item or within a summarized OCI figure with accompanying details in the notes.

If associates/JVs are present AND related OCI is also reported, mark as Compliant. 
If associates/JVs are present but NO related OCI is reported, mark as Non-Compliant.
If no associates/JVs are present, mark as Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,



  other_equity_comparison: `In the provided financial document, compare the value of "Other Equity" reported in the Statement of Standalone/Consolidated Audited Financial Results with the value of "Other Equity" reported in the Statement of Standalone/Consolidated Assets and Liabilities.

Specific Checks:

1. Locate the "Other Equity" line item in the Statement of Standalone/Consolidated Audited Financial Results. Note the reported value.
2. Locate the "Other Equity" line item in the Statement of Standalone/Consolidated Assets and Liabilities. Note the reported value.
3. Compare the two values.

Output Requirements:

If the values are identical, return "Compliant".
If the values differ, return "Non-Compliant".  Do not calculate or report the specific difference; only report compliance status.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  cashflow_pbt_depreciation_tallying: `In the financial statements, perform the following checks:

A) Cash Flow – PBT/Depreciation/Finance Costs Tallying

In the Statement of Standalone/Consolidated Cash Flows, verify the numerical consistency of the following line items with the corresponding values reported in the Statement of Standalone/Consolidated Audited Financial Results:

Profit before tax

Depreciation and amortization expense

Finance costs

The cash flow statement often presents these items as adjustments to net profit to arrive at cash flow from operating activities.

Ensure the values used in these adjustments match the corresponding figures in the results.

Verify that the signs (+/-) applied are correct and consistent with accounting principles (e.g., depreciation added back, finance costs deducted).

B) Placement of "Profit for the Period"

In the Statement of Standalone/Consolidated Audited Financial Results, verify that the line item “Profit for the period / Profit for the year” is correctly placed:

It must appear after the line item “Profit before exceptional items and tax.”

It must also appear after the line item “Profit before tax.”

The sequence should clearly follow this logical order:

Profit before exceptional items and tax

Profit before tax

Profit for the period/year

If “Profit for the period” is missing or positioned incorrectly, mark as Non-Compliant.

Final Output Format (STRICT):

First line: exactly one of “Compliant” or “Non-Compliant”.

Then provide detailed analysis:

Quote specific text or data from the document that supports your conclusion

Explain why the document is compliant or non-compliant

If non-compliant, specify what is missing or incorrect

Provide specific recommendations for improvement

Include page references if available`,

  foreign_subsidiaries_exchange_disclosure: `In the Statement of Cash Flows section, specifically within the 'Cash flows from operating activities', 'Cash flows from investing activities', or 'Cash flows from financing activities' subsections (or equivalent wording depending on the reporting standard used), verify the presence of a disclosure related to the effect of exchange rate differences on the translation of foreign currency balances if the company has foreign subsidiaries.

Check for explicit mention of the impact of exchange rate fluctuations on cash and cash equivalents, or a separate line item disclosing the effect of exchange differences on cash flows. This could appear as a positive or negative value affecting the overall net increase or decrease in cash and cash equivalents. If the report mentions the existence of foreign subsidiaries, this disclosure MUST be present.

If the company has foreign subsidiaries and the disclosure is present and clearly quantifiable, mark as Compliant. If the company has foreign subsidiaries and the disclosure is absent, unclear, or not quantifiable within the cash flow statement, mark as Non-Compliant. If the company has no foreign subsidiaries, mark as Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  group_components_note: `In the Standalone/Consolidated Financial Results and the Independent Auditor's Report sections, verify the presence of a note or disclosure explaining the components of the group.

Specific Checks:

* **Financial Results:** Locate a note (often within the notes to the financial statements) that lists or describes the constituent companies or subsidiaries comprising the group. This note should provide sufficient detail to understand the group's structure.
* **Auditor's Report:**  Check for a mention of the group structure and components, often within the scope or basis of opinion paragraphs. This might refer to the same note in the financial statements or offer a summary overview.

If both the Financial Results and Auditor's Report contain clear information (either directly or by reference) about the group's components, mark as Compliant. If either section lacks this information, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,


  standard_review_audit_report_format: `In the provided Audit/Review Report document, verify adherence to the standard reporting format as prescribed by the relevant regulatory body (e.g., Generally Accepted Auditing Standards (GAAS) if applicable, or the specific standards for review reports).

This verification should include, but not be limited to, checking for the presence and correct placement of the following sections:

- Title (clearly indicating whether it is an "Audit Report" or a "Review Report")
- Addressee (e.g., Board of Directors, Shareholders)
- Introductory Paragraph (identifying the entity being audited/reviewed and the financial statements covered)
- Management's Responsibility section (for the preparation and fair presentation of the financial statements)
- Auditor's/Reviewer's Responsibility section (describing the scope and nature of the audit/review)
- Opinion/Conclusion paragraph (expressing an opinion on the fairness of the financial statements in the case of an audit, or a conclusion on the review engagement)
- Signature, date, and place of signing by the auditor/reviewer.

Additionally, verify the overall structure and language used in the report conforms to professional and regulatory standards, including appropriate headings, paragraphing, and terminology.

If the report adheres to all required elements and format as per the applicable standards, mark as Compliant.  If any deviations from the standard format are identified, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  profit_loss_quarter_ytd_consistency: `In the Independent Auditor's Report and the Management Discussion & Analysis (MD&A) section (or similar sections discussing financial performance), verify the consistency and accurate reporting of profit or loss figures for both the current quarter and year-to-date (YTD) periods.

Specifically, check the following:

1. **Consistency across the report:** Ensure that profit/loss figures mentioned in different parts of the report (e.g., income statement, MD&A, summary tables) are consistent with each other for both the quarter and YTD periods.

2. **Accuracy of YTD calculation:**  Verify that the YTD figures accurately reflect the cumulative results of all quarters up to the current reporting period.

3. **Explanation of discrepancies:** If the current quarter shows a profit while the YTD shows a loss, or vice versa, verify that the report provides a clear and concise explanation for this discrepancy.  This explanation should identify the factors contributing to the difference in performance between the quarter and the YTD period.  The explanation must be present in both the Auditor's Report commentary (if applicable) and the MD&A section.

If all the above conditions are met, mark as Compliant.  If any discrepancies are found or explanations are missing or inadequate, mark as Check.

Output format (STRICT):
1) First line: exactly ONE word only:
   - "Compliant" → if all conditions are met
   - "Check" → if any discrepancies are found or explanations are missing or inadequate

2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or requires checking
   - If Check, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Do NOT use the terms:
- Non-Compliant
- Non-Compliance
- Partial Compliance

The final conclusion must be ONLY "Compliant" or "Check".

Keep the response structured and informative with clear evidence from the document.`,

  foreign_components_translation_disclosure: `In the Notes to Accounts section of the financial report, specifically within the segment reporting or related party disclosures (if applicable), verify the presence of a paragraph explaining the translation process for foreign operations, if any exist.

The paragraph MUST address BOTH of the following:

a) Translation of financial statements from the foreign Generally Accepted Accounting Principles (GAAP) used by the foreign component to Indian Accounting Standards (IND AS).  This should explain the methodology used for this conversion.

b) Translation of foreign currency amounts into Indian Rupees (INR). This should specify the exchange rates used for translation.

If foreign country components are mentioned and both (a) and (b) are clearly explained within a dedicated paragraph, mark as Compliant. If no foreign components are mentioned, mark as Compliant.  If foreign components are mentioned but either (a) or (b) are missing or not clearly explained, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  associates_jv_audit_report_mention: `In the Independent Auditor's Report section, specifically within the scope of the audit, verify the inclusion of Associate Companies and/or Joint Ventures alongside the Group, if any such entities exist.

Check the following:

1. **Existence of Associates/JVs:** First, determine if the company has any reported Associate Companies or Joint Ventures.  This information can typically be found in the Notes to the Consolidated Financial Statements or in a separate section on related party disclosures.

2. **Mention in Auditor's Report:** If Associates/JVs exist, the Independent Auditor's Report MUST explicitly mention their inclusion within the scope of the audit alongside the Group.  Look for phrasing indicating that the audit covered not just the Group but also its Associates/JVs, either individually named or as a category. Example phrasing:  "...the consolidated financial statements of the Group and its associates/joint ventures..."

3. **Absence when None Exist:** If the company has NO reported Associate Companies or Joint Ventures, the Auditor's Report should NOT mention them. The report should focus solely on the Group.

If Associates/JVs exist and are mentioned in the Auditor's Report alongside the Group, OR if no Associates/JVs exist and they are NOT mentioned, mark as Compliant.  Otherwise, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  quarterly_ytd_figures_notes: `In the Notes to the Financial Statements and in the Auditor’s Report sections (or Review sections, if it is a “Review” instead of an Audit), assess whether quarterly and year-to-date (YTD) figures are generally presented for key financial metrics.

Specifically, review the following:

Notes to the Financial Statements: Check that notes presenting financial data typically include both quarterly and YTD figures for the reporting period, wherever applicable.

Auditor’s Report or Review Report: While the Auditor’s Report/Review Report may not itself contain detailed numerical disclosures, confirm that it refers to the financial statements, which in turn should generally include quarterly and YTD data.

EVALUATION APPROACH:

If quarterly and YTD figures appear to be present across the relevant sections described above, mark as Compliant.

If something seems potentially missing or unclear regarding quarterly or YTD disclosure for key metrics, mark as Check.

Only mark as Non-Compliant if you are very certain that required quarterly or YTD figures for key financial metrics are absent in the relevant sections.

Default to Compliant in most cases, unless there is a clear and confident reason to conclude otherwise.

Keep the response structured, clear, and informative, and reference specific sections or excerpts from the document as supporting evidence for your conclusion.`,



  subsidiary_word_highlighting: `In the provided financial document, verify the presence of the word "subsidiary" (or its plural "subsidiaries") within the following sections:

- Statement of Standalone/Consolidated Audited Financial Results
- Statement of Standalone/Consolidated Assets and Liabilities
- Statement of Standalone/Consolidated Cash Flows
- Notes to the Standalone/Consolidated Financial Statements
- Segment Reporting (if included)

The search should be case-insensitive and include variations like "wholly-owned subsidiary" or "partially-owned subsidiary".

Assessment criteria:
- If the word "subsidiary" (or its plural or variations) is found in ANY of the specified sections, mark as "Check"
- If the word is NOT found in ANY of the specified sections, mark as "Check"

Output format (STRICT):
1) First line: exactly "Check".
2) Second line: Highlighted Words: [List all instances of "subsidiary" and its variations found, with page references]
3) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain the specific concerns or issues with subsidiary disclosure
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  sebi_lodr_reg_33_8_para: `In the Independent Auditor's Review Report or the Audit Report (as applicable), verify the presence of a paragraph specifically addressing Regulation 33(8) of the SEBI (Listing Obligations and Disclosure Requirements) Regulations, 2015.  This paragraph should discuss the company's compliance with the requirements of Regulation 33(8) concerning the limited review or audit of quarterly/annual financial results.  The paragraph should explicitly reference "Regulation 33(8)" or "SEBI LODR Regulation 33(8)".

If such a paragraph explicitly addressing Regulation 33(8) is present, mark as Compliant. If the paragraph is absent, mentions Regulation 33 without specifically mentioning sub-regulation (8), or does not discuss the limited review/audit in the context of Regulation 33(8), mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,


  goodwill_impairment_note: `In the Notes to the Financial Statements section, specifically within the Note related to Intangible Assets or Goodwill (if a separate note exists), verify the presence of an explanation if Goodwill impairment is recognized.

The explanation must detail:

* The amount of the impairment loss recognized.
* The reason(s) for the impairment.
* The method used to determine the impairment.
* The impact of the impairment on the financial statements.

If Goodwill impairment is recognized and an explanation including all the above elements is present, mark as Compliant. If Goodwill impairment is not recognized, also mark as Compliant. If Goodwill impairment is recognized, but the explanation is missing or incomplete (lacking any of the above elements), mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  cross_references_correctness: `In the financial statements, specifically the Statement of Standalone/Consolidated Audited Financial Results, Statement of Standalone/Consolidated Assets and Liabilities, and Statement of Standalone/Consolidated Cash Flows, verify the accuracy of all cross-references to note numbers within the accompanying notes to the financial statements.

Similarly, in the Independent Auditor's Report (for audited financials) or Review Report (for reviewed financials), verify the accuracy of all cross-references to note numbers within the financial statements.

This verification requires checking that each cross-reference points to the correct note and that the information within the note corresponds to the item being referenced in the financial statements or report.  For example, if the Income Statement mentions revenue of Rs. X (Note Y), then Note Y should indeed pertain to revenue and provide details/breakdown related to the Rs. X figure. Conversely, if Note Y discusses a different topic or figure, mark as Check (indicating manual review is needed).

If all cross-references in the specified sections are accurate and consistent, mark as Compliant. If any discrepancy or incorrect cross-reference is found, mark as Check (this indicates that manual verification is required, not a definitive non-compliance).

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Check".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or needs manual checking
   - If marked as "Check", specify what discrepancies or issues were found that require manual verification
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  qualification_eom_consistency: `🔹 Multi-Document Verification Prompt
(Qualification / EOM Consistency — Standalone vs Consolidated Audit Report)

You are comparing Qualification/EOM data from two audit reports:
1. Standalone Financial Review/Audit Report
2. Consolidated Financial Review/Audit Report

Your task: Verify that all qualifications, Emphasis of Matter (EOM) paragraphs, and opinion modifications found in the Standalone audit report are consistently reflected in the Consolidated audit report.

Comparison Rules — BE LENIENT:
- Focus on SUBSTANCE, not exact wording
- If the same issue is addressed in both reports, even with different phrasing → Compliant
- Minor wording variations due to consolidated context are acceptable
- Reorganization, renumbering, or merging of paragraphs is acceptable if substance is preserved
- Additional qualifications/EOMs in Consolidated that are NOT in Standalone are acceptable (one-way check only)
- If NO qualifications/EOM/modifications exist in the Standalone report → Compliant

Status Determination:
- No qualifications/EOM in Standalone → "Compliant"
- All Standalone items reflected in Consolidated → "Compliant"
- Any Standalone item missing or materially different in Consolidated → "Check"
- Do NOT use "Non-Compliant" — use ONLY "Compliant" or "Check"
- NEVER output "Non-Compliant" under any circumstances

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Check" (NEVER "Non-Compliant")
2) Detailed analysis with sections for Standalone Findings, Consolidated Findings, Consistency Assessment, Analysis Findings, and Recommendations.

DEPRECATED — This prompt is retained for backward compatibility. The 3-step pipeline uses qualification_eom_standalone_extraction, qualification_eom_consolidated_extraction, and qualification_eom_comparison instead.

IGNORE BELOW — old single-pass instructions retained for reference:

Step 1 — Review Standalone Report

Identify whether the Standalone report contains any of the following:

Qualified Opinion

Adverse Opinion

Disclaimer of Opinion

“Basis for Qualified Opinion” section

Emphasis of Matter (EOM) paragraph

Any paragraph beginning with “We draw attention to Note…”

Any other modification to a standard unqualified opinion

If none of the above are present → Mark as Compliant (no further comparison required).

If any of the above are present → Proceed to Step 2.

Step 2 — Cross-Verify with Consolidated Report

Review the Consolidated Independent Auditor’s Report and/or Review Report.

Verify that:

The same qualification/EOM/modification appears in the Consolidated report.

The wording is substantially similar.

Minor wording variations due to consolidated context are acceptable.

The underlying issue described is consistent in substance.

Mark as Non-Compliant if:

A qualification/EOM exists in Standalone but is missing in Consolidated.

The issue is materially different in description or severity.

The modification is diluted, altered, or omitted without explanation.

Compliance Logic

Compliant:

No qualifications/EOM/modifications exist in the Standalone report, OR

All qualifications/EOMs/modifications in Standalone are also present in Consolidated with substantially similar wording.

Non-Compliant:

Any qualification/EOM/modification in Standalone is missing in Consolidated, OR

There is a material inconsistency in wording or substance.

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Standalone Report Findings

State whether modifications exist.

Quote exact wording from the Standalone report.

Identify type of modification (Qualified Opinion / EOM / etc.).

Consolidated Report Findings

Quote corresponding wording from Consolidated report.

State whether modification is present or absent.

Consistency Assessment

Compare wording and substance.

State whether differences are minor or material.

If Non-Compliant

Specify what is missing or materially different.

Explain why this creates inconsistency.

Provide corrective recommendation (e.g., align consolidated wording with standalone report).

Page References

Mention page numbers or section headings where relevant disclosures appear.

If unavailable, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for audit or regulatory review.`,

  qualification_eom_standalone_extraction: `🔹 Standalone Audit Report — Qualification/EOM Data Extraction

You are analyzing the STANDALONE Financial Review/Audit Report to extract all qualification and opinion modification data.

📊 Required Data Extraction:
Carefully review the entire audit/review report and identify ALL of the following:

1. **Opinion Type**: What type of opinion was issued?
   - Unqualified/Unmodified Opinion
   - Qualified Opinion
   - Adverse Opinion
   - Disclaimer of Opinion

2. **Basis for Qualification** (if any):
   - Quote the exact "Basis for Qualified Opinion" section
   - Identify the specific matters causing qualification

3. **Emphasis of Matter (EOM)** paragraphs:
   - Quote each EOM paragraph in full
   - Identify the note/matter being drawn attention to
   - Look for phrases like "We draw attention to Note…", "Without qualifying our opinion…", "We draw your attention to…"

4. **Other Modifications**:
   - Key Audit Matters (KAM) — list each one with brief description
   - Material Uncertainty Related to Going Concern (if any)
   - Other Matter paragraphs
   - Any other modification to a standard unqualified opinion

5. **Auditor Details**:
   - Firm name and registration number
   - Partner name and membership number
   - Date of report

📊 Output Format:
For each item found, provide:
- Type: [Opinion Type / Qualification / EOM / KAM / Other Matter / Going Concern]
- Description: [Brief description of the matter]
- Exact Quote: "[Quoted text from the document]"
- Page Reference: [Page number if available]
- Note Reference: [Note number referenced, if any]

If NO qualifications, EOMs, or modifications are found, clearly state:
"FINDING: No qualifications, Emphasis of Matter paragraphs, or opinion modifications were identified in the Standalone audit/review report. The report contains a clean unqualified/unmodified opinion."

Be thorough — extract EVERY qualification, EOM, and modification present in the report.`,

  qualification_eom_consolidated_extraction: `🔹 Consolidated Audit Report — Qualification/EOM Data Extraction

You are analyzing the CONSOLIDATED Financial Review/Audit Report to extract all qualification and opinion modification data.

📊 Required Data Extraction:
Carefully review the entire audit/review report and identify ALL of the following:

1. **Opinion Type**: What type of opinion was issued?
   - Unqualified/Unmodified Opinion
   - Qualified Opinion
   - Adverse Opinion
   - Disclaimer of Opinion

2. **Basis for Qualification** (if any):
   - Quote the exact "Basis for Qualified Opinion" section
   - Identify the specific matters causing qualification

3. **Emphasis of Matter (EOM)** paragraphs:
   - Quote each EOM paragraph in full
   - Identify the note/matter being drawn attention to
   - Look for phrases like "We draw attention to Note…", "Without qualifying our opinion…", "We draw your attention to…"

4. **Other Modifications**:
   - Key Audit Matters (KAM) — list each one with brief description
   - Material Uncertainty Related to Going Concern (if any)
   - Other Matter paragraphs
   - Any other modification to a standard unqualified opinion

5. **Auditor Details**:
   - Firm name and registration number
   - Partner name and membership number
   - Date of report

📊 Output Format:
For each item found, provide:
- Type: [Opinion Type / Qualification / EOM / KAM / Other Matter / Going Concern]
- Description: [Brief description of the matter]
- Exact Quote: "[Quoted text from the document]"
- Page Reference: [Page number if available]
- Note Reference: [Note number referenced, if any]

If NO qualifications, EOMs, or modifications are found, clearly state:
"FINDING: No qualifications, Emphasis of Matter paragraphs, or opinion modifications were identified in the Consolidated audit/review report. The report contains a clean unqualified/unmodified opinion."

Be thorough — extract EVERY qualification, EOM, and modification present in the report.`,

  qualification_eom_comparison: `🔹 Qualification/EOM Consistency — Standalone vs Consolidated Comparison

You are comparing extracted Qualification/EOM data from two audit reports:
1. Standalone Financial Review/Audit Report
2. Consolidated Financial Review/Audit Report

Your task: Verify that ALL qualifications, Emphasis of Matter (EOM) paragraphs, and opinion modifications found in the Standalone audit report are consistently reflected in the Consolidated audit report.

📊 Comparison Rules — BE LENIENT:
- Focus on SUBSTANCE, not exact wording
- If the same issue is addressed in both reports, even with different phrasing → Compliant
- Minor wording variations due to consolidated context (e.g., "Company" vs "Group", "Holding Company" vs "Company") are acceptable
- Reorganization, renumbering, or merging of paragraphs is acceptable if substance is preserved
- Additional qualifications/EOMs in Consolidated that are NOT in Standalone are acceptable (this is a one-way check: Standalone → Consolidated only)
- If NO qualifications/EOM/modifications exist in the Standalone report → Compliant (no further comparison needed)

📊 Status Determination:
- If no qualifications/EOM/modifications exist in Standalone → "Compliant"
- If all Standalone qualifications/EOMs are reflected in Consolidated with substantially similar substance → "Compliant"
- If any Standalone qualification/EOM is missing or materially different in Consolidated → "Check"
- Do NOT use "Non-Compliant" — use ONLY "Compliant" or "Check"
- NEVER output "Non-Compliant" under any circumstances

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Check" (NEVER "Non-Compliant")
2) Then provide detailed analysis:

**1. Standalone Report Findings** — List all qualifications/EOM/modifications found (or state none found)
**2. Consolidated Report Findings** — List corresponding items found (or state none found)
**3. Item-by-Item Consistency Assessment** — For each Standalone item, confirm whether it appears in Consolidated with substantially similar substance
**4. Analysis Findings** — Overall findings and explanation
**5. Recommendations** — Specific recommendations if any gaps found

Keep the response structured and evidence-based.`,

  tax_ratio_deviation_highlighting: `In the Statement of results, locate the line items for 'Total Tax Expense (excluding prior year adjustments)' and 'Profit Before Tax (PBT)' for each reporting period presented.

Calculate the ratio of Total Tax Expense (excluding prior year adjustments) to PBT for each reporting period.

Compare the calculated ratios between consecutive reporting periods.

If the absolute percentage difference between the ratios of any two consecutive periods exceeds 25%, mark as Non-Compliant.  The percentage difference is calculated as:  abs((Ratio1 - Ratio2) / Ratio1) * 100 where Ratio1 is the ratio of the earlier period and Ratio2 is the ratio of the later period.

If no period-to-period ratio difference exceeds 25%, mark as Compliant.

Output format (STRICT):
1) First line: exactly warning".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is warning
   - If warning, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  cin_number_mention: `In the first few pages of the document, typically within the 'Company Information' or 'Corporate Information' section of the annual report or financial results, verify the presence of the Company Identification Number (CIN).  The CIN should be clearly stated and easily identifiable.  It should also match the publicly available CIN for the company being audited (cross-reference with official sources like the Ministry of Corporate Affairs website). If the CIN is present and correct, mark as Compliant. If the CIN is missing, incorrect, or not easily identifiable, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  firm_reg_number_correctness: ` You are reviewing the signature block at the end of the Independent Auditor’s Report specifically to check the presence and format of the auditor’s firm registration number — not the company’s CIN, LLPIN, membership number, or UDIN.

Focus Area
Examine only the auditor’s firm registration number appearing in the signature block near the auditor’s name or firm name.

What to Look For
The number should resemble the pattern below (leniently interpreted):
Firm Registration No.: 003990s/S 200018 or 003990S/S 200018

Evaluation Guidance (Lenient Approach)
• Confirm that a firm registration number is present in the signature block.
• It should generally follow the structure: six digits + “s/S” or “S/S” + six digits.
• Be flexible regarding minor variations such as spacing or letter case (case-insensitive).
• Do not reject merely for small formatting differences if the intent and structure clearly match the expected pattern.

Do Not Confuse With
• Company CIN
• LLPIN
• Membership Number
• UDIN

Decision Logic
Compliant:
The auditor’s firm registration number is present in the signature block and broadly matches the expected structure (even if spacing or case varies slightly).

Non-Compliant:
The number is missing, clearly unrelated to the audit firm, or does not resemble the expected six-digit + s/S + six-digit pattern.

Output Format

First Line:
Compliant
or
Non-Compliant`,

  partner_name_mention: `You are reviewing the Audit Report, specifically focusing on the signature block, to verify the presence of the signing partner’s full name.

Your review should follow the steps below:

Step-by-Step Review Instructions

Review the Signature Block

Examine the section at the end of the Audit Report where the auditor signs. This typically includes:

Audit firm name

Firm registration number

Signature

Partner name

Membership number

UDIN

Place and date

Identify the Signing Partner’s Full Name

The partner’s full name should:

Be clearly written (e.g., “CA Ramesh Kumar” or “Ramesh Kumar”)

Appear directly below or near the signature

Be clearly associated with the audit firm

Not be abbreviated or replaced with only initials

Check for Separate Partner’s Report (If Applicable)

If the document includes a separate:

“Partner’s Report”

“Report of the Engagement Partner”

Or similar titled section

Verify that the partner’s full name is clearly mentioned there as well.

Compliance Logic

Compliant =

The signing partner’s full name is clearly identifiable in:

The signature block of the main Audit Report, OR

A separate Partner’s Report (if present)

Non-Compliant =

No identifiable partner name is present in the signature block

Only the firm name is mentioned without the partner name

Only initials or signature image appears without a readable full name

A separate Partner’s Report exists but does not contain the partner’s full name

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Quoted Evidence

Quote the exact name as shown in the signature block (or Partner’s Report).

Include surrounding context for clarity.

Location Identified

Specify whether found in:

Main Audit Report signature block

Separate Partner’s Report section

Explanation

Clearly explain why the partner’s full name is sufficiently identifiable or missing.

If Non-Compliant

Specify what is missing (e.g., no partner name, initials only).

Provide corrective recommendation (e.g., include full name below signature along with membership number).

Page References

Mention page number or section location where the signature block appears.

If unavailable, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for audit or regulatory documentation.`,

  partner_membership_number: `You are reviewing the signature block or partner information section of the Audit Report to verify the presence and correct format of the Partner Membership Number.

Your review must strictly focus on the number format, not the label wording.

Step-by-Step Review Instructions

Locate the Signature Block

Review the section at the end of the audit report that includes:

Partner name

Membership number / Member No. / Membership No. / Partner ID / MEM No.

Firm registration number

UDIN

Place and date

Identify the Membership Number

Acceptable label formats include (but are not limited to):

“Membership Number”

“Membership No.”

“Member No.”

“MEM No.”

“Partner ID”

The label wording does NOT matter.
Only the numeric format matters.

Verify Digit Count (STRICT)

The membership number MUST:

Contain exactly 6 numerical digits

Count each digit individually

Leading zeros are allowed

No letters or additional characters allowed within the number

Examples:

220369 → 6 digits → Compliant

211785 → 6 digits → Compliant

12345 → 5 digits → Non-Compliant

1234567 → 7 digits → Non-Compliant

Compliance Logic

Compliant =

Membership number is present

Contains exactly six numerical digits

Non-Compliant =

Membership number missing

Contains fewer than 6 digits

Contains more than 6 digits

Contains non-numeric characters

Not clearly identifiable as partner membership number

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Quoted Evidence

Quote the exact membership number as shown in the document.

Include the surrounding label text for clarity.

Digit Verification

Count the digits explicitly.

Confirm whether the number contains exactly six digits.

Location Identified

Confirm that it appears within the signature block or partner information section.

Conclusion Explanation

Clearly explain why it meets or does not meet the six-digit requirement.

If Non-Compliant

Specify the exact deviation (e.g., 5 digits, 7 digits, missing number).

Recommend including a clearly identifiable six-digit membership number.

Page References

Include page number or section reference.

If unavailable, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for regulatory or audit documentation.`,

udin_number_mention: `ou are reviewing the Auditor’s Report section to verify the presence and validity of the Unique Document Identification Number (UDIN).

Your verification must strictly follow the criteria below.

Step-by-Step Review Instructions

Search Entire Auditor’s Report Section

Carefully scan the full Auditor’s Report, including:

Signature block

Footer area

Immediately below the membership number

Any concluding paragraphs

Identify UDIN

A valid UDIN must:

Be exactly 18 characters long

Be alphanumeric (letters and numbers allowed)

Match ICAI’s standard format pattern (e.g., 24220369BKCEX16565)

Format Validation

Count all characters carefully

Ensure total = 18 characters

Confirm alphanumeric composition

No extra spaces within the UDIN

Ignore surrounding text such as “UDIN:” label

Compliance Logic

Compliant =

An 18-digit alphanumeric UDIN is present anywhere within the Auditor’s Report section.

Non-Compliant =

UDIN completely missing

A number is present but:

Not 18 characters

Not alphanumeric

Incorrect format

Note:
The position of the UDIN does NOT affect compliance.

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Quoted UDIN

Quote the exact UDIN as shown in the document.

Supporting Text

Quote surrounding text where the UDIN appears (e.g., “UDIN: 24220369BKCEX16565”).

Format Verification

Confirm total characters counted = 18.

Confirm alphanumeric composition.

Conclusion Explanation

Clearly state whether a valid 18-digit UDIN was found.

If Non-Compliant

Specify whether UDIN is missing or incorrectly formatted.

Provide recommendation (e.g., include valid 18-digit alphanumeric UDIN in signature block).

Page References

Mention page number or section reference.

If unavailable, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for regulatory or audit documentation.`,

  date_place_consistency: `Verify the consistency of signature dates and places between the company officials' signatures in the Financial Results and the auditor's signature in the Independent Auditor's Report.

Specifically, check the following in the signature blocks:

1. **Signature Date Consistency:** 
   - Look for the signature date in the company officials' signature block (usually at the bottom of the Financial Results)
   - Look for the signature date in the auditor's signature block (usually at the bottom of the Auditor's Report)
   - Ensure these signature dates are consistent or reasonably close (within a few days is acceptable)

2. **Signature Place Consistency:**
   - Look for the place of signing in the company officials' signature block (e.g., "Place: Mumbai", "Madurai", etc.)
   - Look for the place of signing in the auditor's signature block (e.g., "Place: Mumbai", "Madurai", etc.)
   - Ensure these places are consistent or match

IMPORTANT: Focus on the actual signature blocks at the bottom of the documents, not the general reporting dates or company addresses mentioned in the body of the documents.

Examples of what to look for:
- Company signature: "Place: Mumbai, Date: 15th May 2025"
- Auditor signature: "Place: Mumbai, Date: 15th May 2025" or "Place: Mumbai, Date: 16th May 2025"

If both the signature dates and places are consistent between the company and auditor signatures, mark as Compliant. If there are discrepancies in signature dates or places, mark as Non-Compliant.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  firm_seal_affixed: `Verify the presence of the firm seal on every page of the Independent Auditor's Report section only.

Focus specifically on the auditor's report section (typically titled "Independent Auditor's Report" or similar). Do not check other sections like financial results or notes.

The firm seal can appear in various forms and must be present on every page of the auditor's report section:

1. **Circular/round stamp** with firm details
2. **Rectangular stamp** with firm information
3. **Digital watermark** or stamp
4. **Embossed seal** (may appear as raised text)
5. **Text-based seal** containing firm name and registration number

Look for ANY of the following elements that indicate a firm seal:
- Firm name (e.g., "PKF SRIDHAR & SANTHANAM", "SRIDHAR & SANTHANAM")
- Registration number (e.g., "FIRM REGN NO. 003990S/S200018", "003990S/S200018")
- Location (e.g., "CHENNAI", "MUMBAI")
- Any combination of the above in a stamped/sealed format

The seal may be located in:
- Bottom left corner
- Bottom right corner
- Any corner of the page
- Near the signature area
- As a watermark across the page

IMPORTANT: Be thorough in your search. The seal might be:
- Partially visible due to page margins
- Lightly printed or stamped
- In a different location than expected
- Appearing as text rather than a traditional stamp

Check each page of the auditor's report section systematically and look carefully for ANY indication of the firm seal, regardless of its exact format or location.

If you find evidence of the firm seal (firm name, registration number, or location) on every page of the auditor's report section, mark as Compliant. Only mark as Non-Compliant if you cannot find ANY evidence of the firm seal on any page of the auditor's report section.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify which pages of the auditor's report section are missing the seal
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  audit_report_date_consistency: `You are reviewing the Independent Auditor’s Report and the corresponding Statement of Standalone/Consolidated Financial Results to verify consistency of dates.

Your review must follow the structured checks below.

Step-by-Step Review Instructions

Identify Auditor’s Report Date

Locate the date mentioned in the Independent Auditor’s Report.

This is typically found:

In the signature block

At the end of the report

Below “Place:”

Quote the exact date as stated.

Identify Financial Results Reporting Period

Locate the reporting period end date (e.g., “For the quarter ended June 30, 2025” or “For the year ended March 31, 2025”).

This is usually mentioned:

In the title/header of the financial results

At the top of the results table

Quote the exact wording.

Identify Date of Approval (if disclosed)

Check notes or footnotes for statements such as:

“Approved by the Board of Directors on…”

“Taken on record on…”

Quote the approval date if available.

Date Consistency Logic

Compliant if:

The Auditor’s Report date is AFTER the reporting period end date; AND

The Auditor’s Report date does NOT predate the financial results; AND

The approval date (if disclosed) matches or reasonably follows the audit report date; AND

There is no unreasonable time gap suggesting inconsistency.

Non-Compliant if:

Auditor’s Report date is BEFORE the reporting period end date; OR

Auditor’s Report predates the financial results approval; OR

There is a significant unexplained discrepancy suggesting timeline inconsistency.

A reasonable timeframe between period end and audit date is expected (audit completion period). Minor timing gaps are acceptable.

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Reporting Period Identified

Quote the exact reporting period wording.

Auditor’s Report Date

Quote the exact date from the audit report.

Approval Date (if available)

Quote the approval date.

Timeline Assessment

Explain chronological order:

Reporting period end

Audit report date

Approval date

Conclusion Explanation

Clearly justify why the dates are logically consistent or inconsistent.

If Non-Compliant

Specify the exact inconsistency.

Recommend corrective action (e.g., align report date with approval date, verify reporting period header).

Page References

Mention page numbers or section titles.

If unavailable, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for audit or regulatory documentation.`,

  segment_results_explanation_note: `In the Notes to the Financial Statements section accompanying the segment reporting (typically presented after the primary financial statements), review whether there is an explanatory note describing the segment information in a clear and reasonably detailed manner.

This note should generally include:

The basis of segmentation (for example, product lines, geographic areas, customer categories, or other internal reporting structures).

The factors used to identify and determine the reportable segments.

A reconciliation of segment revenues, profits/losses, assets, and liabilities to the corresponding consolidated totals.

EVALUATION APPROACH:

If a reasonably comprehensive note explaining the segments, covering the above key elements, is present, mark as Compliant.

If the disclosure appears partially complete, unclear, or potentially missing one of the key elements, mark as Check.

Only mark as Non-Compliant if you are very certain that the segment note is missing entirely or clearly lacks essential elements such as the segmentation basis or reconciliation to consolidated figures.

Default to Compliant in most situations, unless there is a clear and confident reason to conclude otherwise.

Ensure your response is structured and informative, and reference specific notes, headings, or excerpts from the document as supporting evidence for your conclusion.`,

  period_ended_as_at_phrases: `In the Statement of Standalone/Consolidated Assets and Liabilities, verify whether the heading includes the correct time indicator.

Checks to perform:

The phrase must be either “as at [Date]” or “as on [Date]”.

The phrase “for the period” or “for the year” must never appear in this statement.

The date must correspond to the reporting date.

Output format (STRICT):

First line: exactly one of “Compliant” or “Non-Compliant”.

Then provide detailed analysis:

Quote specific text or data from the document that supports your conclusion.

Explain why the document is compliant or non-compliant.

If non-compliant, specify what is missing or incorrect.

Provide specific recommendations for improvement.

Include page references if available.`,

  segment_period_year_phrases: `You are reviewing the financial results document headings and applying the prescribed validation rules.

Follow the structured checks below.

Step-by-Step Review Instructions
Rule 1 — Main Results Heading

Locate the primary financial results heading (usually at the top of the results page).

Verify that:

The heading contains the phrase:
“For the … ended …”
(e.g., “For the quarter ended June 30, 2025”)

Confirm that the heading does NOT contain:

“As at”

“As on”

If either prohibited phrase appears in the main results heading → Non-Compliant.

Rule 2 — Segment Results (If Present)

Check whether a Segment Results section/table exists in the document.

If present, verify that its heading also contains:
“For the … ended …”

If no Segment Results section exists, skip this rule entirely.

Rule 3 — Exclusions

Do NOT apply the above checks to headings of:

Cash Flow Statement

Statement of Assets and Liabilities

These headings are excluded from validation.

Decision Logic

Compliant =

Main Results heading includes “For the … ended …”

Main Results heading does NOT include “As at” or “As on”

Segment Results heading (if present) includes “For the … ended …”

Exclusions properly ignored

Non-Compliant =

Main Results heading missing required phrase

Main Results heading includes prohibited wording

Segment Results heading (if applicable) missing required phrase

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Main Results Heading

Quote the exact heading text.

State whether it contains “For the … ended …”.

Confirm absence of “As at” / “As on”.

Segment Results Heading (if applicable)

Quote exact heading text.

Confirm whether required phrase is present.

If no segment section exists, clearly state: “Segment Results section not present — rule not applicable.”

Exclusion Confirmation

Confirm that Cash Flow Statement and Statement of Assets and Liabilities headings were not evaluated.

Conclusion Explanation

Clearly justify why the document is compliant or non-compliant.

If Non-Compliant

Specify exact issue (e.g., missing required phrase, prohibited wording used).

Provide corrective recommendation (e.g., revise heading to “Statement of Unaudited Financial Results for the Quarter Ended June 30, 2025”).

Page References

Mention page numbers or section titles where headings appear.

If unavailable, state “N/A”.

Ensure the response is structured, precise, and suitable for audit or regulatory documentation.`,

  notes_figures_denomination: `You are reviewing the Standalone/Consolidated Financial Results, specifically focusing on consistency of units of measurement between the Notes to Accounts and the main financial statements.

Follow the structured validation below.

Step-by-Step Review Instructions

Identify Units in Main Financial Statements

Carefully review the headings or footnotes of:

Statement of Standalone/Consolidated Financial Results

Statement of Standalone/Consolidated Assets and Liabilities

Statement of Standalone/Consolidated Cash Flows

Look for unit disclosures such as:

“Rs. in lakhs”

“₹ in crores”

“INR in thousands”

Similar denomination references

Quote the exact unit wording used in each statement.

Identify Units in Notes to Accounts

Review the Notes to Accounts section and identify any statement specifying units of measurement.

Quote the exact wording (e.g., “All amounts are in ₹ lakhs unless otherwise stated”).

Compare Units for Consistency

Verify that:

The denomination used in the Notes matches the denomination used in the main financial statements; OR

If different units are used in specific notes, they are clearly disclosed and reconciled appropriately.

Any mismatch without clear disclosure constitutes inconsistency.

Compliance Logic

Compliant =

Units in Notes to Accounts are consistent with units used in all corresponding financial statements; OR

Any variation is clearly disclosed and logically reconciled.

Non-Compliant =

Units in Notes differ from those in the main financial statements without clarification; OR

Inconsistent denomination appears across statements and notes.

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Units in Financial Results

Quote exact unit disclosure.

Units in Assets & Liabilities

Quote exact unit disclosure.

Units in Cash Flow Statement

Quote exact unit disclosure.

Units in Notes to Accounts

Quote exact unit disclosure.

Consistency Assessment

Explain whether denominations match across all sections.

Identify any discrepancy.

If Non-Compliant

Specify the exact inconsistency (e.g., Results in ₹ crores but Notes in ₹ lakhs without clarification).

Provide corrective recommendation (e.g., standardize unit disclosure or clearly state conversion basis).

Page References

Mention page numbers or section headings.

If unavailable, state “N/A”.

Ensure the response is structured, precise, and suitable for audit or regulatory documentation.`,

  tax_pbt_ratio_highlighting: `Role & Objective
You are a financial compliance analyst AI. Your task is to evaluate whether the effective tax rate appears compliant for every reporting period shown in the Statement of Profit and Loss (Income Statement).

You must independently evaluate each reporting period and then provide an overall compliance conclusion.

📥 Input Context

You will be provided with a Statement of Profit and Loss that may include:

Multiple reporting periods (quarters / years / comparative columns)

Line items such as:

Profit Before Tax (PBT)

Tax Expense (which may be split into Current Tax and Deferred Tax)

Assume figures are presented in consistent units unless otherwise stated.

🧠 Analysis Instructions

For each reporting period shown in the P&L:

Identify Profit Before Tax (PBT)

Look for labels such as:

“Profit Before Tax”

“PBT”

“Profit before exceptional items and tax”

If multiple PBT variants exist, select the final profit figure immediately before total tax expense.

Identify Total Tax Expense

Include:

Current Tax

Deferred Tax

If tax is shown as a single consolidated line, use that value.
If tax expense is not disclosed, treat that period as requiring review.

Validate PBT

If PBT is zero or negative, the effective tax rate is not meaningfully assessable → mark that period as Check.

Calculate Effective Tax Rate

Effective Tax Rate = (Total Tax Expense ÷ Profit Before Tax) × 100

Compliance Rule

Compliant → Effective tax rate is between 25% and 40% (inclusive).

Check → Effective tax rate is below 25%, above 40%, undefined, or data is missing.

Repeat the above steps for ALL reporting periods presented.
Do not skip any column or comparative period.

Overall Conclusion Rule

If any one reporting period results in Check, the overall result must be Check.

Only if all reporting periods meet the compliance threshold should the overall result be Compliant.

Perform your reasoning carefully and base conclusions strictly on figures disclosed in the document. Avoid assumptions not supported by explicit textual evidence.

📤 STRICT OUTPUT FORMAT (MANDATORY)

Line 1

Exactly one of:

Compliant

or

Check

IMPORTANT: Always return "Check" (not "Non-Compliant") if any issue is identified. Never return "Non-Compliant" for this review.

Detailed Analysis Section

For each reporting period, clearly present:

Reporting Period: (e.g., FY 2024, Q2 FY 2025)

Quoted Evidence from Document

Profit Before Tax (quote exact label and value)

Tax Expense (quote exact label and value, including breakdown if available)

Calculation

Show numeric values used

Show calculated effective tax rate (rounded to two decimal places)

Compliance Determination

State Compliant or Check, with a concise reason

If Overall Result is Check, also include:

Specific Issue(s) Identified

For example: tax rate outside permitted range, negative PBT, missing tax data

What Is Missing or Potentially Incorrect

Actionable Recommendations

For example: clarify tax components, reconcile deferred tax, provide explanation for unusually high or low effective tax rate

Page / Note References

Include page numbers or note references where available

✅ Quality & Accuracy Requirements

Do not assume values without explicit textual evidence.

Do not average tax rates across periods.

Do not overlook a non-compliant period.

Ensure the analysis is structured, evidence-based, and audit-defensible.`,

  // Fallback prompt for any check that doesn't have a specific prompt
  results_verification: `You are analyzing a financial results document to verify compliance with regulatory requirements.

Your task is to review the document and determine if it meets the specified compliance criteria.

Instructions:
- Thoroughly examine the document for relevant information
- Look for specific disclosures, statements, or data points
- Check for consistency and completeness
- Verify against regulatory requirements

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,



  major_events_explanation_notes: `🔹 Verification Prompt (Major Corporate Events Disclosure)

You are reviewing the Notes to the Financial Statements attached to the Standalone/Consolidated Unaudited/Audited Financial Results for the quarter/half-year ended September 30, 202x.

Your task is to check whether the notes include clear and adequate disclosures about major corporate events, including but not limited to:

Mergers or amalgamations

Acquisitions of subsidiaries or businesses

New issue of shares (rights issue, bonus issue, QIP, private placement)

Buyback of shares

Corporate restructuring, demergers, spin-offs

Significant Board decisions post-quarter

✅ Step-by-Step Checks

Search for Keywords:
Look in notes for terms such as:

"Merger," "Acquisition," "Scheme of arrangement,"

"Rights issue," "Bonus issue," "Allotment of shares,"

"Buyback," "Preferential issue," "Demerger," etc.

Disclosure Rules:

If a major event has occurred → A corresponding note must explain it (nature, date, impact).

If no events occurred → A note explicitly stating "No major events during the quarter/half year" is acceptable.

If an event is shown in results (e.g., Exceptional Items due to merger costs) but no explanatory note is given → mark Non-Compliant.

📊 Output Requirement

Strictly return "Compliant" or "Non-Compliant."

Add a short reasoning.

👉 Example Outputs

Case 1 (Compliant – Event Disclosed):

Notes state: "During the quarter, the Company acquired XYZ Pvt Ltd for ₹150 Cr; accounted as business combination."

Final Status: ✅ Compliant — Major event disclosed with details.

Case 2 (Compliant – No Event):

Notes state: "No major corporate events during the quarter ended September 30, 202x."

Final Status: ✅ Compliant — Absence of events clearly stated.

Case 3 (Non-Compliant):

Results include Exceptional Item tagged as "Merger expenses ₹25 Cr" but no note explaining merger.
Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text or data from the document that supports your conclusion
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what is missing or incorrect
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.

`,

  qualified_audit_report_impact_disclosure: `🔹 Verification Prompt (Qualified Audit Report — Impact Disclosure)

Follow the structured two-step validation below.

Step 1 — Determine the Type of Auditor’s Report

Review the “Opinion” (for audit) or “Conclusion” (for review) paragraph in the Auditor’s Report.

Identify whether the report is:

A) Qualified
Look for phrases such as:

“Qualified Opinion”

“Basis for Qualified Opinion”

“Except for the matters described…”

“Subject to…”

B) Unqualified (Clean)
Look for phrases such as:

“In our opinion, the financial statements present fairly, in all material respects…”

“Give a true and fair view…”

“Nothing has come to our attention that causes us to believe…”

Step 2 — Apply Compliance Logic

IF the Report is Unqualified (Clean):

The check is automatically Compliant.

No management explanation or impact disclosure is required.

IF the Report is Qualified:

You must verify disclosures in the Notes to the Financial Statements.

Check A — Management’s View

Confirm whether there is an explicit explanation from management addressing the qualification.

Look for wording such as:

“Management’s view on the qualification is as follows…”

“With respect to the auditor’s observation…”

Check B — Financial Impact

Confirm whether the cumulative financial impact is:

Quantified (e.g., ₹ amount impact on profit, net worth, EPS, assets, etc.), OR

Clearly and specifically explained.

Vague statements such as:

“Impact is minimal”

“No significant effect”
are NOT sufficient.

Compliance Criteria

Compliant:

Report is Unqualified; OR

Report is Qualified AND both:

Management view is clearly disclosed; AND

Financial impact is quantified or clearly explained.

Non-Compliant:

Report is Qualified AND either:

Management view is missing; OR

Financial impact is missing, vague, or ambiguous.

📊 OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Finding

Clearly state whether the Auditor’s Report is “Qualified” or “Unqualified.”

Quote (Evidence)

Quote the exact wording from the Opinion/Conclusion paragraph that supports your finding.

Explanation

If Compliant (Unqualified):
“The report is Unqualified. Therefore, no impact disclosure is required, and this check is compliant.”

If Compliant (Qualified with disclosures):
“The report is Qualified, and the company has provided the required management view and quantified financial impact.”
(Quote relevant disclosures from Notes.)

If Non-Compliant:
“The report is Qualified, but the required disclosures are missing or incomplete.”

Missing/Incorrect (If Non-Compliant)

Specify exactly what is missing:

Management explanation missing; OR

Financial impact not quantified; OR

Disclosure vague/insufficient.

Page References

Include page numbers or note references for:

Auditor’s opinion

Management disclosure

If unavailable, state “N/A”.

Ensure the response is structured, precise, and audit-defensible.`,
  eom_paragraph_notes_explanation: `🔹 Verification Prompt (EOM → Notes Consistency)

You are reviewing the Independent Auditor’s Report attached to the Standalone/Consolidated Unaudited/Audited Financial Results for the period ended September 30, 202x.

Follow the structured validation below.

Step 1 — Identify Emphasis of Matter (EOM) Paragraphs

Carefully scan the Auditor’s Report for:

A heading titled “Emphasis of Matter”

Paragraphs beginning with:

“We draw attention to Note X…”

“Emphasis of Matter — Without qualifying our opinion…”

“We draw attention to the accompanying financial results…”

Extract and document:

Each matter emphasized

The subject (e.g., pending litigations, going concern, impairment, regulatory proceedings, loans, etc.)

The referenced note number (if cited)

If no EOM paragraphs exist → Mark as Compliant (no further cross-check required).

Step 2 — Cross-Check with Notes to Accounts

For each matter identified in Step 1:

Locate the corresponding note in the Notes to Accounts / Notes to Financial Results section.

Verify that:

The referenced note number exists (if cited in EOM).

The note clearly describes the same subject matter.

The disclosure contains sufficient detail, such as:

Monetary amounts

Nature of uncertainty

Context of litigation/regulatory issue

Impact explanation (if applicable)

Confirm consistency:

Subject in EOM must align with the content in the Note.

The note must not be vague or unrelated.

Compliance Logic

Compliant:

No EOM paragraphs exist; OR

All EOM matters have clearly corresponding and sufficiently detailed Notes.

Non-Compliant:

An EOM matter does not have a corresponding note; OR

The referenced note number does not exist; OR

The note is vague, insufficient, or inconsistent with the EOM.

📊 OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

EOM Detection

State whether EOM exists.

Quote the EOM paragraph(s).

Identify subject(s) emphasized.

Corresponding Notes Identified

Quote relevant note(s) from Notes to Accounts.

Mention note number(s).

Consistency Assessment

Confirm whether subject matter matches.

Confirm whether sufficient detail is provided.

Conclusion Explanation

Clearly justify why compliant or non-compliant.

If Non-Compliant

Specify which EOM lacks support.

State whether note is missing, incorrect, or vague.

Provide recommendation (e.g., expand note disclosure to align with auditor emphasis).

Page References

Include page numbers or note references.

If unavailable, state “N/A”.

Ensure the response is structured, precise, and suitable for regulatory and audit documentation.`,

  material_reclassification_explanation: `🔹You are reviewing the “Statement of Unaudited/Audited Standalone Financial Results for the period ended XXX 30, 202x” along with the accompanying Notes to Accounts.

Step 1 — Confirm Applicability

Proceed with this check only if material reclassification of past period figures has been confirmed (dropdown = Yes).

If no reclassification is indicated, this check is Not Applicable.

Step 2 — Review Disclosure in Notes to Accounts

If reclassification is confirmed, examine the Notes to Accounts to identify whether disclosure regarding reclassification is provided.

The disclosure should ideally:

• Explain the reason for reclassification
(e.g., regrouping to align with current presentation, change in accounting policy, revised classification, correction of presentation)

• Indicate the financial impact, where available
(quantified impact on revenue, expenses, profit, or other line items)

Evaluation Guidance (Lenient Approach)

Compliant
Mark as Compliant if:

• A note on reclassification exists; AND
• Either the reason OR the financial impact is reasonably described (even if not perfectly quantified); AND
• The disclosure provides sufficient clarity for users to understand the nature of the reclassification.

General regrouping statements may be acceptable if they indicate that figures were reclassified to align with current presentation, even when detailed quantification is not provided.

Check
Mark as Check if:

• No disclosure regarding reclassification is found; OR
• Disclosure is extremely generic and does not provide meaningful clarity on the nature of reclassification; OR
• Impact is implied but not reasonably understandable.

📊 OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Check`,


  foreign_component_translation_disclosure: `🔹 Verification Prompt (Foreign Component Translation Disclosure)

You are reviewing the Notes to Accounts attached to the Standalone/Consolidated Unaudited/Audited Financial Results.

✅ Task

If it is indicated (dropdown = Yes) that the company has foreign country components, review the Notes to confirm whether they generally include a clear translation disclosure addressing both of the following areas:

Conversion of foreign GAAP into IND AS — a description of the methodology used to convert the financial statements of foreign components into Indian Accounting Standards (IND AS).

Conversion of foreign currency into INR — a description of the exchange rates applied (for example, closing rate for balance sheet items and average rate for profit and loss items).

EVALUATION APPROACH:

If both aspects of translation disclosure are reasonably addressed in the Notes, mark as Compliant.

If one of the elements appears unclear, partially described, or potentially missing, mark as Check.

Only mark as Non-Compliant if you are very certain that required translation disclosures are absent despite confirmation that foreign components exist.

Default to Compliant in most cases, unless there is a clear and confident basis to conclude otherwise.

Keep the response structured and informative, and refer to relevant notes or excerpts from the document as supporting evidence for your conclusion.`,



  q2_ob: `TASK: Extract Q2 Opening Balance from Current Document

You are analyzing a Q2 financial results document. Find the opening balance for "Cash and cash equivalents".

Look for these EXACT patterns in the Cash Flow Statement:
- "Cash and cash equivalents as at April 1, (opening balance)"
- "Cash and cash equivalents as at [date], (opening balance)" 
- "Cash and cash equivalents" with "opening balance"
- Any line mentioning "opening balance" for cash

CRITICAL INSTRUCTIONS:
1. Search ONLY in the Cash Flow Statement section
2. Look for the line that shows opening balance (usually at the bottom)
3. Extract ONLY the numerical value from the corresponding column
4. Do NOT provide any analysis or explanation
5. If found, return just the number (e.g., "3,345" or "3345")
6. If NOT found, return exactly "NOT_FOUND"

EXAMPLE:
If you see: "Cash and cash equivalents as at April 1, (opening balance) 3,345"
Return: "3,345"

If you see: "Cash and cash equivalents as at April 1, (opening balance) 1,121.43"  
Return: "1,121.43"

If you don't find any opening balance line, return: "NOT_FOUND"`,

  q4_ob: `TASK: Extract Q4 Opening Balance from Current Document

You are analyzing a Q4 financial results document. Find the opening balance for "Cash and cash equivalents".

Look for these EXACT patterns in the Cash Flow Statement:
- "Cash and cash equivalents as at April 1, (opening balance)"
- "Cash and cash equivalents as at [date], (opening balance)" 
- "Cash and cash equivalents" with "opening balance"
- Any line mentioning "opening balance" for cash

CRITICAL INSTRUCTIONS:
1. Search ONLY in the Cash Flow Statement section
2. Look for the line that shows opening balance (usually at the bottom)
3. Extract ONLY the numerical value from the corresponding column
4. Do NOT provide any analysis or explanation
5. If found, return just the number (e.g., "3,345" or "3345")
6. If NOT found, return exactly "NOT_FOUND"

EXAMPLE:
If you see: "Cash and cash equivalents as at April 1, (opening balance) 3,345"
Return: "3,345"

If you see: "Cash and cash equivalents as at April 1, (opening balance) 1,121.43"  
Return: "1,121.43"

If you don't find any opening balance line, return: "NOT_FOUND"`,

  cb_py: `TASK: Extract Previous Year Closing Balance from Previous Year Document

You are analyzing a previous year Q4 or annual report document. Find the closing balance for "Cash and cash equivalents".

Look for these EXACT patterns in the Cash Flow Statement:
- "Cash and cash equivalents as at March 31, (closing balance)"
- "Cash and cash equivalents as at [date], (closing balance)"
- "Cash and cash equivalents" with "closing balance"
- Any line mentioning "closing balance" for cash

CRITICAL INSTRUCTIONS:
1. Search ONLY in the Cash Flow Statement section
2. Look for the line that shows closing balance (usually at the very bottom)
3. Extract ONLY the numerical value from the corresponding column
4. Do NOT provide any analysis or explanation
5. If found, return just the number (e.g., "1,121" or "1121")
6. If NOT found, return exactly "NOT_FOUND"

EXAMPLE:
If you see: "Cash and cash equivalents as at March 31, (closing balance) 1,121"
Return: "1,121"

If you see: "Cash and cash equivalents as at March 31, (closing balance) 3,345.67"  
Return: "3,345.67"

If you don't find any closing balance line, return: "NOT_FOUND"`,

  // Additional prompts for missing checklist items
  previous_auditor_different: `You are reviewing the "Independent Auditor's Report" attached to the Standalone Financial Results for the period ended.

Your task is to check if there is a mention of a different auditor from the previous period.

Look for:
- References to previous period auditor
- Statements indicating auditor change
- Any disclosure about auditor transition
- Previous year auditor information

If the previous period auditor is different from the current auditor, verify that this change is properly mentioned in the report.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether the auditor change is properly disclosed
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  other_auditors_mention: `You are reviewing the "Independent Auditor's Report" attached to the Standalone Financial Results for the period ended.

Your task is to check if components reviewed/audited by other auditors are mentioned in the review/audit report.

Look for:
- References to other auditors
- Statements about components audited by different firms
- Disclosures about subsidiary or associate auditors
- Any mention of joint audits or component auditors

If there are components reviewed/audited by other auditors, verify that this is properly mentioned in the report.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether other auditors are properly disclosed
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  first_year_firm_previous_auditor: `You are reviewing the "Independent Auditor's Report" attached to the Standalone Financial Results for the period ended.

Your task is to check if there is a paragraph about who has done review/audit in previous year and if the report is modified for first year firm.

Look for:
- References to previous year auditor
- Statements about first-time audit engagement
- Disclosures about auditor change in first year
- Any modifications to the report due to first year firm

If this is a first year engagement or there was an auditor change, verify that appropriate disclosures are made.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether first year firm disclosures are adequate
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  goodwill_impaired: `You are reviewing the financial results document to check if goodwill is impaired and if there is a note to explain that.

Your task:
1. Look for goodwill impairment in the financial statements
2. Check if goodwill has been impaired during the period
3. Verify if there is an explanatory note about the impairment
4. Check the adequacy of the impairment disclosure

Look in:
- Statement of Profit and Loss for impairment charges
- Notes to accounts for goodwill impairment explanations
- Balance sheet for goodwill amounts
- Any other relevant sections

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether goodwill impairment is properly disclosed
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  associates_joint_ventures: `You are reviewing the financial results document to check if the company has associates or joint ventures.

Your task:
1. Look for references to associates and joint ventures
2. Check the balance sheet for investments in associates/JVs
3. Look for segment reporting that includes associates/JVs
4. Check notes for disclosures about associates/JVs

Look in:
- Balance sheet for investments
- Notes to accounts for associate/JV disclosures
- Segment reporting sections
- Any other relevant sections

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether associates/JVs are properly disclosed
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  audit_report_qualified: `You are reviewing the "Independent Auditor's Report" attached to the Standalone Financial Results for the period ended.

Your task is to check if the audit report is qualified.

Look for:
- Qualified opinion language
- Phrases like "subject to", "except for", "except as mentioned"
- Any qualifications or limitations in the opinion
- References to material uncertainties

The audit report should clearly state if it's qualified, unqualified, or adverse.

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether the audit report qualification status is clear
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  eom_paragraphs_present: `You are reviewing the “Independent Auditor’s Report” attached to the Standalone Financial Results for the relevant period.

Your objective is to determine whether Emphasis of Matter (EOM) paragraphs are present and properly disclosed.

Step-by-Step Review Instructions

Carefully scan the entire Auditor’s Report for:

A section titled “Emphasis of Matter”

Paragraphs beginning with:

“We draw attention to…”

“Without qualifying our opinion, we draw attention…”

“We draw attention to Note…”

Any paragraph explicitly highlighting significant matters (e.g., litigation, going concern, impairment, regulatory matters) without modifying the opinion

Confirm whether such paragraph(s):

Exist and are clearly labeled or worded as emphasis

Do not constitute a qualification

Compliance Logic

Compliant =

EOM paragraph(s) are present and properly structured; OR

No EOM paragraph exists, and the audit opinion is standard (i.e., no emphasis required).

Non-Compliant =

EOM paragraph is referenced but incomplete; OR

Significant matter appears emphasized informally without proper EOM structure; OR

Required emphasis appears missing where clearly referenced.

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

EOM Identification

State whether an EOM section was found.

Quote the exact paragraph(s), if present.

Structure Assessment

Explain whether the paragraph is properly structured as an Emphasis of Matter (clearly identified and separated from opinion).

Conclusion Explanation

Clearly state whether the audit report properly discloses EOM (or correctly contains none).

If Non-Compliant

Specify what is missing (e.g., heading missing, improper structure, incomplete paragraph).

Provide recommendation (e.g., add a clearly titled “Emphasis of Matter” section following the Opinion paragraph).

Page References

Include page number or section reference.

If unavailable, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for audit or regulatory documentation.`,

  material_reclassification: `You are reviewing the financial results document to check if there are material reclassifications of past results.

Your task:
1. Look for reclassification disclosures in notes
2. Check if prior period figures have been restated
3. Look for explanations of reclassification reasons
4. Verify if the impact is quantified

Look in:
- Notes to accounts for reclassification disclosures
- Previous period figures for restatements
- Any explanatory notes about changes
- Comparative figures

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether reclassifications are properly disclosed
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  profit_loss_status: `You are reviewing the "Independent Auditor's Report" attached to the Standalone Financial Results for the period ended.

Your task is to check if the profit/loss status is correctly stated in the audit report based on the actual financial results.

Your task:
1. Look at the Profit After Tax (PAT) in the financial results
2. Check if PAT is positive or negative
3. Verify if the audit report correctly refers to "profit" or "loss"
4. Ensure consistency between financial results and audit opinion

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether profit/loss status is correctly stated
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  foreign_country_components: `You are reviewing the financial results document to check if there are foreign country components.

Your task:
1. Look for foreign subsidiaries or operations
2. Check for foreign exchange disclosures
3. Look for segment reporting with foreign components
4. Check for foreign currency translation disclosures

Look in:
- Notes to accounts for foreign operations
- Segment reporting for geographical segments
- Balance sheet for foreign investments
- Any foreign exchange related disclosures

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether foreign components are properly disclosed
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  cfo_pat_depreciation_ratio: `You are reviewing the financial results document to verify whether Cash Flow from Operating Activities (CFO) is at least 50% of the sum of Profit After Tax (PAT) and Depreciation/Other Non-Cash Provisions.

Your task:
1. In the Statement of Cash Flows, identify the figure for:
   - Cash Flow from Operating Activities (CFO) (typically the first subtotal after operating adjustments)

2. In the Statement of Profit and Loss, identify the figures for:
   - Profit After Tax (PAT) (Net Profit after taxes)
   - Depreciation and Amortisation Expense
   - Other Non-Cash Provisions, if disclosed separately (e.g., impairment, provisions for doubtful debts, employee benefit provisions)

3. Compute the benchmark:
   Benchmark = PAT + Depreciation + Other Non-Cash Provisions

4. Check whether:
   CFO ≥ 0.5 × Benchmark

Decision Rule:
- If CFO is greater than or equal to 50% of Benchmark, mark as Compliant
- If CFO is less than 50% of Benchmark, raise a Warning: CFO less than 50% of PAT + Depreciation/Non-Cash Provisions

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Warning: CFO < 50% of PAT + Depreciation/Non-Cash Provisions"
2) Then provide detailed analysis:
   - Quote specific figures from the document that support your calculation
   - Show the calculation: CFO = [amount], Benchmark = PAT + Depreciation + Other Non-Cash Provisions = [amount], 50% of Benchmark = [amount]
   - Explain why the document is compliant or raises a warning
   - If warning, specify the exact percentage and recommend review
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  cce_tallying_check: `You are reviewing the financial document to verify CCE (Cash and Cash Equivalents) tallying between Statement of Cash Flows and Statement of Assets and Liabilities for both periods.

CONTEXT / WHERE TO CHECK:
This is a single-document analysis. The document should contain BOTH:
1. Statement of Cash Flows (a.k.a. "Cash Flow Statement" / "Statement of Cashflows")
2. Statement of Assets and Liabilities (Balance Sheet)

PERIODS TO COMPARE:
- Current period-end (e.g., 30 September 20XX)
- Comparative year-end (e.g., 31 March 20XX)

DATA TO EXTRACT:
From Statement of Cash Flows section:
- "Cash and cash equivalents at end of the period" (a.k.a. "Closing cash and cash equivalents")
- Extract amounts for both period-end dates

From Statement of Assets and Liabilities section:
- "Cash and cash equivalents" under Current Assets
- Extract balances as at the same two dates

NORMALIZATION RULES:
- Ensure amounts are compared in the same reporting unit (e.g., "Rs in lakhs/crores/thousands")
- If units differ between statements, scale accordingly
- Ignore thousand separators and whitespace; treat parentheses as negative values
- Currency must be the same (e.g., INR). If not, mark as Non-Compliant

CHECKS (apply separately for each scope present — Standalone and Consolidated):
For each of the two dates:
- Let CF_CCE[Date] = closing "Cash and cash equivalents at end of the period" from Cash Flow Statement
- Let BS_CCE[Date] = "Cash and cash equivalents" from Statement of Assets and Liabilities as at the same Date
- After unit normalization, verify CF_CCE[Date] = BS_CCE[Date]
- Accept a rounding tolerance of ±1 unit of the reported unit (e.g., ±1 lakh if "Rs in lakhs")
- If either figure is missing for a required date, treat as mismatch

COMPLIANCE LOGIC:
- Compliant = For every scope present (Standalone/Consolidated), both dates match within tolerance
- Non-Compliant = Any mismatch, missing figure, different currency, or inability to align units for any required comparison

DETAILED ANALYSIS REQUIREMENTS:
If both statements are found in the document, provide:
- Current Period CF CCE: [Amount from Cash Flow Statement]
- Current Period BS CCE: [Amount from Balance Sheet] 
- Current Period Match: [Yes/No with tolerance details]
- Previous Period CF CCE: [Amount from Cash Flow Statement]
- Previous Period BS CCE: [Amount from Balance Sheet]
- Previous Period Match: [Yes/No with tolerance details]
- Unit Consistency: [Same/Different with details]
- Currency Consistency: [Same/Different with details]
- Scope Analyzed: [Standalone/Consolidated/Both]
- Specific page references and line items quoted
- Explanation of any differences found and their significance
- Recommendations for resolution if non-compliant

If required statements are missing from the document:
- Clearly state which statements are missing
- Explain why both statements are required for this check
- Provide specific guidance on what needs to be present in the document
- Suggest what to look for in the document

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant"
2) Then provide detailed analysis as specified above

Keep the response structured and informative with clear evidence from the document.`,

  balance_sheet_tallying_check: `You are reviewing the Statement of Assets and Liabilities (Balance Sheet) to verify that both sides tally (Assets = Equity + Liabilities) for both periods.

CONTEXT / WHERE TO CHECK:
Locate the Statement of Assets and Liabilities (Balance Sheet) for every scope presented (Standalone and/or Consolidated).

PERIODS TO VERIFY:
For each reporting date shown in the statement (typically 30 September 20XX and 31 March 20XX), perform the checks below separately.

NORMALIZATION RULES:
- Compare amounts in the same reporting unit (e.g., "Rs in lakhs/crores/thousands"); scale if needed
- Treat parentheses as negative values; ignore thousand separators and whitespace
- Currency must match across the statement

HOW TO COMPUTE TOTALS (avoid double-counting):

Assets side:
- If category subtotals are present, sum Subtotal: Non-current assets + Subtotal: Current assets (and any other asset category subtotals, if presented)
- If subtotals are not present, sum only leaf-level asset line items; exclude rows that start with "Total", "Sub-total", "Less:", or similar

Equity & Liabilities side:
- If category subtotals are present, sum Total Equity (include Non-controlling interests, if shown) + Subtotal: Non-current liabilities + Subtotal: Current liabilities (and any other liability category subtotals, if presented)
- If subtotals are not present, sum only leaf-level items under Equity and Liabilities; exclude rows that start with "Total", "Sub-total", "Less:", or similar
- If the statement also provides printed "Total Assets" and "Total Equity and Liabilities", use them for a cross-check but still compute independently as above

CHECKS (per scope and per date):
1. Compute ASSETS_SUM using the Assets-side rule
2. Compute EQLIAB_SUM using the Equity & Liabilities-side rule
3. Verify ASSETS_SUM = EQLIAB_SUM after unit normalization, within a rounding tolerance of ±1 unit of the reported unit (e.g., ±1 lakh if "Rs in lakhs")
4. If printed totals exist, verify they each match the corresponding computed sums within the same tolerance
5. If any required figure is missing, units/currency can't be aligned, or equality fails, treat as a mismatch

COMPLIANCE LOGIC:
- Compliant = For every scope present (Standalone/Consolidated) and every date shown, ASSETS_SUM equals EQLIAB_SUM within tolerance (and printed totals, if present, also align)
- Non-Compliant = Any mismatch, missing figures, or unresolvable unit/currency differences for any required comparison
- If the Statement of Assets and Liabilities cannot be found, mark Non-Compliant

Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant"
2) Then provide detailed analysis:
   - Current Period Assets Total: [Computed amount]
   - Current Period Equity + Liabilities Total: [Computed amount]
   - Current Period Match: [Yes/No]
   - Current Period Difference: [Difference amount or "N/A" if match]
   - Previous Period Assets Total: [Computed amount]
   - Previous Period Equity + Liabilities Total: [Computed amount]
   - Previous Period Match: [Yes/No]
   - Previous Period Difference: [Difference amount or "N/A" if match]
   - Unit Consistency: [Same/Different]
   - Currency Consistency: [Same/Different]
   - Scope Analyzed: [Standalone/Consolidated/Both]
   - Printed Totals Verification: [Match/No Match/Not Available]
   - Explain any differences found and their significance
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  cashflow_net_change_check: `Verification Prompt: Net Increase/(Decrease) Reconciles with Opening vs Closing CCE

Context / Where to Check:

Statement of Cash Flows for every scope presented (Standalone and/or Consolidated).

Perform the check for each column/period shown (e.g., half-year ended 30 September 20XX and year/previous half-year ended 31 March/30 September 20XX).

Lines to Extract (per scope, per period):

Opening CCE — “Cash and cash equivalents at the beginning of the period/year”.

Closing CCE — “Cash and cash equivalents at the end of the period/year”.

Net change line — “Net increase/(decrease) in cash and cash equivalents” (a.k.a. “Net (decrease)/increase …”, “Net change in cash and cash equivalents”).

Reconciling items (if present) between the net change line and closing CCE, e.g.:

“Effect of exchange rate changes on cash and cash equivalents”

“Effect of foreign exchange/translation”

“Reclassification/adjustments related to CCE (e.g., bank overdrafts in CCE, assets held for sale, acquisitions/disposals)”

Normalization Rules:

Compare amounts in the same reporting unit (e.g., “Rs in lakhs/crores/thousands”); scale if needed.

Treat parentheses as negative; ignore thousand separators/whitespace; treat “—” or “-” as zero.

Currency must match across the statement; if not, treat as mismatch.

Computation & Checks (per scope, per period):

Compute DIFF = Closing CCE − Opening CCE.

Let NET = value of “Net increase/(decrease) in cash and cash equivalents.”

Let FX = sum of any exchange-rate effect lines (0 if absent).

Let OTHER = sum of any other CCE reconciling lines (0 if absent).

Verify DIFF = NET + FX + OTHER within a rounding tolerance of ±1 unit of the reported unit (e.g., ±1 lakh if “Rs in lakhs”).

If the Net change line or either Opening/Closing CCE is missing, mark as mismatch.

Compliance Logic:

Compliant = For every scope present and every period column, Step 5 holds within tolerance and all required lines exist with aligned units/currency.

Non-Compliant = Any failure of Step 5, missing required line(s), or unresolvable unit/currency differences for any scope/period.

Final Output: Output format (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote specific text from the document that supports your conclusion
   - Explain whether foreign components are properly disclosed
   - If non-compliant, specify what is missing
   - Provide specific recommendations for improvement
   - Include page references if available
`,

  sre_2410_mention: `You are reviewing the Independent Auditor’s Report or Independent Auditor’s Review Report to verify the mandatory mention of SRE 2410 (Standard on Review Engagements 2410) or an equivalent reference.

Follow the structured validation below.

Step-by-Step Review Instructions

Identify Type of Engagement

Confirm whether the report is a:

Review Report (Limited Review), or

Audit Report

This determines whether reference to SRE 2410 is applicable (typically mandatory for review engagements).

Search for Explicit Standard Reference

Carefully scan the entire report, especially:

Auditor’s Responsibility section

Scope paragraph

Basis for Conclusion / Basis for Opinion

Introductory engagement description

Look specifically for:

“SRE 2410”

“Standard on Review Engagements 2410”

“SAE 2410” (if applicable wording used)

Any equivalent explicit standard reference containing “2410”

The reference must clearly relate to the engagement standard applied.

Compliance Logic

Compliant:

The report explicitly mentions:

“SRE 2410”, OR

“Standard on Review Engagements 2410”, OR

“SAE 2410”, OR

An equivalent explicit standard reference to 2410.

Non-Compliant:

No mention of SRE 2410 (or equivalent) anywhere in the report.

Only general wording such as “conducted in accordance with applicable standards” without reference to 2410.

OUTPUT FORMAT (STRICT)

First line:
Compliant
or
Non-Compliant

Detailed Analysis:

Engagement Type Identified

State whether the report is a Review or Audit report.

Standard Reference Found (or Not Found)

If found:

Quote the exact wording mentioning SRE 2410 (or equivalent).

Specify the section/paragraph where it appears.

If not found:

Clearly state that no reference to SRE 2410 (or equivalent) was identified after reviewing the full report.

Compliance Explanation

Explain why the document satisfies or fails the requirement.

If Non-Compliant

Specify that the report must include explicit reference to:
“Standard on Review Engagements (SRE) 2410, Review of Interim Financial Information Performed by the Independent Auditor of the Entity.”

Recommend inserting the reference in the Auditor’s Responsibility or Scope section.

Page References

Include page number or section heading where the reference appears.

If not found, state “N/A”.

Ensure the response is structured, evidence-based, and suitable for regulatory and audit documentation.`,

  sre_2400_mention: `You are reviewing the Independent Auditor's Report or the Independent Auditor's Review Report to verify the mandatory mention of SRE 2400 (Standard on Review Engagements 2400).

TASK: Check if the audit report explicitly mentions "SRE 2400" or "Standard on Review Engagements 2400" or "SAE 2400" (Standard on Auditing Engagements 2400) or any equivalent reference.

CRITICAL REQUIREMENT:
The audit report MUST explicitly reference SRE 2400 in the context of the review/audit engagement. This reference is typically found in:
- The Auditor's Responsibility section
- The scope paragraph
- Any section discussing the standards applied to the engagement

WHAT TO LOOK FOR:
- Exact text: "SRE 2400"
- Exact text: "Standard on Review Engagements 2400"
- Exact text: "SAE 2400" (if applicable)
- Any explicit reference to "SRE 2400" or equivalent standard number

COMPLIANCE CRITERIA:
✅ COMPLIANT: The audit report explicitly mentions SRE 2400 (or equivalent standard reference) in the appropriate section(s) of the report.

❌ NON-COMPLIANT: The audit report does NOT mention SRE 2400 or any equivalent standard reference. The absence of this mandatory reference constitutes non-compliance.

OUTPUT FORMAT (STRICT):
1) First line: exactly one of "Compliant" or "Non-Compliant".
2) Then provide detailed analysis:
   - Quote the exact text where SRE 2400 is mentioned (if found), or state that it is not mentioned
   - Specify the section/paragraph where the reference appears (if found)
   - Explain why the document is compliant or non-compliant
   - If non-compliant, specify what needs to be added
   - Provide specific recommendations for improvement
   - Include page references if available

Keep the response structured and informative with clear evidence from the document.`,

  castings_check_vertical: `🔹 VERTICAL CHECK PROMPT
(Column-wise Arithmetic Integrity | Structure-Aware)

Objective

Verify internal arithmetic consistency within each reporting period column of a financial statement by validating totals, subtotals, and derived figures based only on relationships explicitly present in the statement.

No cross-period comparison is allowed.

Do not assume a standard P&L structure.

This is a structure-aware, column-wise validation only.

Input Context

You are analyzing a Quarterly Financial Results statement (Standalone or Consolidated) that presents multiple reporting periods as separate columns in the same table.

Step 1: Identify Reporting Periods

Detect and list all reporting periods exactly as shown in the statement.

Treat each reporting period independently.

Do not infer or add missing periods.

Step 2: Identify Aggregated Line Items

For each reporting period column:

Scan the statement to identify line items that appear to be:

Totals

Subtotals

Derived results

Use only:

Labels (e.g., “Total”, “Profit”, “Net”, “Before”, “After”)

Indentation or grouping

Table structure or formatting

Do NOT rely on predefined accounting templates.

Step 3: Infer Component Relationships

For each identified total or subtotal:

Identify its immediate component line items explicitly presented in the statement.

Use only components shown directly above or structurally grouped with the total.

Do NOT assume missing components.

If the relationship is ambiguous, mark it as Skipped and explain why.

Step 4: Perform Vertical Arithmetic Checks

For each inferred relationship and for each reporting period independently:

Recalculate the total using identified components.

Compare recalculated value with reported value.

Flag a mismatch if:

Absolute Difference > ±0.01 (₹ crores)

Step 5: Attribution Check (Only If Explicitly Presented)

If the statement explicitly presents profit attribution (typically in consolidated results):

Verify whether:

Profit attributable to owners

Profit attributable to non-controlling interest

Reconcile to:

Total profit for the period

If attribution data is not presented → Skip this check.

Rules (STRICT)

Do NOT compare values across different reporting periods.

Do NOT assume standard formulas (e.g., Revenue – Expenses).

Perform a check only if both total and all components are explicitly present.

If any component is missing or unclear → Mark as Skipped.

Do NOT infer accounting intent or regulatory compliance.

This is strictly an arithmetic validation exercise.

🧾 OUTPUT FORMAT (STRICT)

Overall Status

Compliant
or
Check

(Compliant = All checks Pass or Skipped
Check = Any Fail detected)

Identified Reporting Periods

(List exactly as shown in the document)

Vertical Check Results — TABLE FORMAT (MANDATORY)

Reporting Period | Total / Subtotal Checked | Components Used | Expected Value | Reported Value | Difference | Status

Include one row per check per period.

Status must be one of:

Pass
Fail
Skipped

Quoted Evidence

Quote exact line items and figures used in each calculation.

Include page references if available.

Explanation

For each Fail or Skipped item:

Clearly explain the reason:

Arithmetic mismatch

Missing component

Ambiguous structure

Rounding beyond tolerance

🧠 Interpretation Guardrails

This is a pure arithmetic integrity check.

Do NOT infer accounting errors or regulatory non-compliance.

Do NOT perform horizontal, trend, or comparative analysis.`,

  castings_check_horizontal: `HORIZONTAL CHECK PROMPT
(Row-wise Period Consistency | Structure-Aware | Selective)

Objective

Verify consistency, completeness, and comparability of financial data across reporting periods presented within the same financial statement, by performing row-wise checks across period columns.

This check validates comparative integrity, not arithmetic aggregation.
No vertical (within-period) arithmetic is allowed.

Input Context

You are analyzing a Quarterly Financial Results statement (Standalone or Consolidated) that presents multiple reporting periods as columns in the same table (e.g., current quarter, previous quarter, same quarter last year, nine months, year).

Step 1: Identify Reporting Periods

Detect and list all reporting periods exactly as shown in the statement.

For each period, identify:

Period type (Quarter / Nine Months / Year)

Duration (3M / 9M / 12M)

Financial year

Use only periods explicitly present in the statement.

Step 2: Determine Comparable Period Groups

Group periods that are strictly comparable, based on:

Same period type

Same duration

Examples:

Quarter ↔ Quarter

Nine Months ↔ Nine Months

Year ↔ Year

🚫 Do not compare:

Quarter ↔ Nine Months

Quarter ↔ Year

Nine Months ↔ Year

If a period lacks a comparable counterpart, note it but do not infer or create one.

Step 3: Identify Eligible Line Items

From the statement, identify line items suitable for horizontal comparison, such as:

Revenue

Expenses

Profit / Loss

EPS

Other operating or financial performance items

Explicit Exclusions (DO NOT CHECK)

Do not perform horizontal checks for line items that are inherently static or structural, including (but not limited to):

Paid-up Share Capital

Share premium

Face value disclosures

Accounting policy headings

Header or descriptive rows

Rationale: These items are not expected to change quarter-to-quarter and are outside the scope of horizontal performance consistency.

Step 4: Perform Horizontal Checks (Row-wise)

For each eligible line item and each comparable period group:

Compare values across the period columns

Check for:

Missing comparative figures

Abrupt or unusual changes

Inconsistent presentation (value present in one period but missing in another)

This is a sanity and completeness check, not a performance or trend analysis.

Step 5: Explanation & Disclosure Awareness

If material variation exists, check whether:

An explanatory note is provided

The variation is explicitly disclosed

Do not assume reasons for changes.

If no explanation is present, flag for review.

Rules (STRICT)

Do not perform arithmetic aggregation.

Do not compare across different period types or durations.

Do not include excluded/static line items (e.g., Paid-up Share Capital).

Do not infer intent, accounting judgment, or regulatory non-compliance.

Use only information present in the statement and its notes.

🧾 Output Format (STRICT)
1) Overall Status

Compliant or Check

2) Identified Reporting Periods

(List exactly as shown)

3) Comparable Period Groups

(Clearly show which periods are compared together)

4) Horizontal Check Results — TABLE FORMAT (MANDATORY)
Line Item	Period Group Compared	Values Observed	Observation	Status

Status must be one of:

Pass

Check

Skipped (Not Applicable)

5) Quoted Evidence

Quote exact figures used in comparisons

Include page references, if available

6) Explanation

Explain:

Why a line item was flagged

Whether disclosure is missing or unclear

Why certain line items (e.g., Paid-up Share Capital) were excluded

🧠 Interpretation Guardrails

This is a comparability and completeness check only

Do not interpret business performance

Do not infer regulatory non-compliance`
};

// ===================================================================
// BALANCE VERIFICATION PROCESSING FUNCTION
// ===================================================================

/**
 * Process balance verification using internal extraction prompts and if-else logic
 */
async function processBalanceVerification(
  currentDocument: File,
  previousDocument: File,
  checkId: string,
  model: string,
  temperature: number,
  abortSignal?: AbortSignal
): Promise<CheckResult> {
  try {
    console.log(`Processing ${checkId} balance verification with simple extraction and if-else logic`);
    console.log(`Current Document: ${currentDocument.name}`);
    console.log(`Previous Document: ${previousDocument.name}`);

    // Step 1: Extract opening balance from current document
    const openingBalancePrompt = checkId === 'q2_balance_verification' 
      ? CHECK_PROMPTS['q2_ob'] 
      : CHECK_PROMPTS['q4_ob'];
    
    const openingResult = await processCustomDocumentCheck(
      currentDocument,
      `${checkId}_ob`,
      openingBalancePrompt,
      model,
      temperature,
      abortSignal
    );

    // Step 2: Extract closing balance from previous year document
    const closingResult = await processCustomDocumentCheck(
      previousDocument,
      `${checkId}_cb_py`,
      CHECK_PROMPTS['cb_py'],
      model,
      temperature,
      abortSignal
    );

    // Step 3: Extract numerical values from AI responses
    console.log('Opening result explanation:', openingResult.explanation);
    console.log('Closing result explanation:', closingResult.explanation);
    
    const openingAmount = extractAmountFromResponse(openingResult.explanation || '');
    const closingAmount = extractAmountFromResponse(closingResult.explanation || '');

    console.log('Extracted opening amount:', openingAmount);
    console.log('Extracted closing amount:', closingAmount);

    // Step 4: Compare amounts using if-else logic (not AI)
    let isCompliant = false;
    let comparisonResult = '';

    if (openingAmount === null || closingAmount === null) {
      isCompliant = false;
      comparisonResult = `Could not extract amounts from documents. Opening: ${openingAmount}, Closing: ${closingAmount}`;
    } else if (openingAmount === closingAmount) {
      isCompliant = true;
      comparisonResult = `Amounts match exactly: ${openingAmount} = ${closingAmount}`;
    } else {
      isCompliant = false;
      comparisonResult = `Amounts do not match: ${openingAmount} ≠ ${closingAmount}`;
    }

    // Step 5: Create comprehensive result
    const explanation = `
**Balance Verification Analysis:**

**Current Document (${currentDocument.name}):**
- Opening Balance: ${openingAmount || 'Not found'}
- AI Response: "${openingResult.explanation || 'No response'}"

**Previous Year Document (${previousDocument.name}):**
- Closing Balance: ${closingAmount || 'Not found'}
- AI Response: "${closingResult.explanation || 'No response'}"

**Comparison Result:**
${comparisonResult}

**Detailed Analysis:**
- Opening Balance Extraction: ${openingResult.explanation || 'Failed'}
- Closing Balance Extraction: ${closingResult.explanation || 'Failed'}
- Final Verification: ${isCompliant ? 'PASSED' : 'FAILED'}

**Debug Information:**
- Opening Amount Extracted: ${openingAmount}
- Closing Amount Extracted: ${closingAmount}
    `.trim();

    return {
      isCompliant,
      explanation,
      detail: explanation
    };

  } catch (error) {
    console.error(`Error processing balance verification ${checkId}:`, error);
    return {
      isCompliant: false,
      explanation: `Error processing balance verification: ${error instanceof Error ? error.message : 'Unknown error'}`,
      detail: `Error processing balance verification: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Extract numerical amount from AI response
 */
function extractAmountFromResponse(response: string): number | null {
  console.log('Extracting amount from response:', response);
  
  // Check if AI returned "NOT_FOUND"
  if (response.toLowerCase().includes('not_found') || response.toLowerCase().includes('not found')) {
    console.log('AI returned NOT_FOUND');
    return null;
  }
  
  // Look for numerical patterns in the response
  const patterns = [
    /(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/g,  // 1,121.43 or 1121.43
    /(\d+\.\d{2})/g,                        // 1121.43
    /(\d+)/g                                // 1121
  ];

  for (const pattern of patterns) {
    const matches = response.match(pattern);
    if (matches && matches.length > 0) {
      // Convert to number, removing commas
      const amount = parseFloat(matches[0].replace(/,/g, ''));
      if (!isNaN(amount)) {
        console.log('Extracted amount:', amount);
        return amount;
      }
    }
  }

  console.log('No amount found in response');
  return null;
}

// ===================================================================
// MAIN COMPONENT
// ===================================================================

// Currency Unit Comparison Function (similar to processFaceValueComparison)
const processCurrencyUnitComparison = async (
  mainDocument: File,
  comparisonDocument: File,
  checkId: string,
  model: string,
  temperature: number,
  abortSignal?: AbortSignal
): Promise<any> => {
  console.log(`Processing ${checkId} currency unit comparison with simple extraction and if-else logic`);
  console.log(`Main Document: ${mainDocument.name}`);
  console.log(`Comparison Document: ${comparisonDocument.name}`);

  try {
    // Step 1: Extract currency unit from main document
    const mainResult = await processCustomDocumentCheck(
      mainDocument,
      checkId,
      CHECK_PROMPTS['currency_unit_main_document'],
      model,
      temperature,
      abortSignal
    );

    console.log('Main document currency unit extraction result:', mainResult);

    // Step 2: Extract currency unit from comparison document (consolidated/standalone)
    const comparisonResult = await processCustomDocumentCheck(
      comparisonDocument,
      checkId,
      CHECK_PROMPTS['currency_unit_consolidated_standalone'],
      model,
      temperature,
      abortSignal
    );

    console.log('Comparison document currency unit extraction result:', comparisonResult);

    // Step 3: Extract currency units from both results
    const mainCurrencyUnit = extractCurrencyUnitFromResponse(mainResult.detail);
    const comparisonCurrencyUnit = extractCurrencyUnitFromResponse(comparisonResult.detail);

    console.log('Extracted currency units:');
    console.log('- Main document:', mainCurrencyUnit);
    console.log('- Comparison document:', comparisonCurrencyUnit);

    // Step 4: Compare currency units using if-else logic with detailed feedback
    let isCompliant = false;
    let comparisonDetails = '';
    let reasoning = '';
    let evidence: string[] = [];
    let recommendations: string[] = [];

    if (mainCurrencyUnit === null && comparisonCurrencyUnit === null) {
      isCompliant = false;
      comparisonDetails = '❌ NON-COMPLIANT: Currency unit not found in either document.';
      reasoning = 'Both documents lack clear currency unit disclosure, which is required for consistency verification.';
      evidence = [
        'Main document: No currency unit found',
        'Comparison document: No currency unit found'
      ];
      recommendations = [
        'Add currency unit disclosure to both documents (e.g., "₹ in Crores")',
        'Ensure consistent currency unit reporting across all related documents'
      ];
    } else if (mainCurrencyUnit === null) {
      isCompliant = false;
      comparisonDetails = `❌ NON-COMPLIANT: Currency unit not found in main document, but found "${comparisonCurrencyUnit}" in comparison document.`;
      reasoning = 'Main document lacks currency unit disclosure while comparison document has it, creating inconsistency.';
      evidence = [
        'Main document: No currency unit found',
        `Comparison document: "${comparisonCurrencyUnit}"`
      ];
      recommendations = [
        'Add currency unit disclosure to main document to match comparison document',
        'Ensure consistent currency unit reporting across all related documents'
      ];
    } else if (comparisonCurrencyUnit === null) {
      isCompliant = false;
      comparisonDetails = `❌ NON-COMPLIANT: Currency unit found in main document: "${mainCurrencyUnit}", but not found in comparison document.`;
      reasoning = 'Comparison document lacks currency unit disclosure while main document has it, creating inconsistency.';
      evidence = [
        `Main document: "${mainCurrencyUnit}"`,
        'Comparison document: No currency unit found'
      ];
      recommendations = [
        'Add currency unit disclosure to comparison document to match main document',
        'Ensure consistent currency unit reporting across all related documents'
      ];
    } else if (mainCurrencyUnit.toLowerCase() === comparisonCurrencyUnit.toLowerCase()) {
      isCompliant = true;
      comparisonDetails = `✅ COMPLIANT: Currency units match: "${mainCurrencyUnit}" used in both documents.`;
      reasoning = 'Currency units are consistent between both documents, indicating proper disclosure and standardization.';
      evidence = [
        `Main document: "${mainCurrencyUnit}"`,
        `Comparison document: "${comparisonCurrencyUnit}"`
      ];
      recommendations = [
        'Currency units match correctly between documents',
        'Continue maintaining consistent currency unit disclosure'
      ];
    } else {
      isCompliant = false;
      comparisonDetails = `❌ NON-COMPLIANT: Currency units do not match: Main document uses "${mainCurrencyUnit}", Comparison document uses "${comparisonCurrencyUnit}".`;
      reasoning = 'Currency units differ between documents, which may indicate disclosure inconsistencies or different reporting standards.';
      evidence = [
        `Main document: "${mainCurrencyUnit}"`,
        `Comparison document: "${comparisonCurrencyUnit}"`
      ];
      recommendations = [
        'Standardize currency units across all related documents',
        'Verify if different units are intentional (e.g., different subsidiaries)',
        'Ensure consistent currency unit reporting for better comparability'
      ];
    }

    // Step 5: Create combined result
    const combinedDetails = `
MAIN DOCUMENT ANALYSIS:
${mainResult.detail}

COMPARISON DOCUMENT ANALYSIS:
${comparisonResult.detail}

CURRENCY UNIT COMPARISON RESULT:
${comparisonDetails}
    `.trim();

    const finalResult = {
      isCompliant: isCompliant,
      explanation: comparisonDetails,
      detail: combinedDetails,
      confidence: Math.min(mainResult.confidence || 0.8, comparisonResult.confidence || 0.8),
      feedback: {
        reasoning: reasoning,
        evidence: evidence,
        recommendations: recommendations
      }
    };

    console.log('Final currency unit comparison result:', finalResult);
    return finalResult;

  } catch (error) {
    console.error('Error in processCurrencyUnitComparison:', error);
    throw error;
  }
};

// Helper function to extract currency unit from AI response
function extractCurrencyUnitFromResponse(response: string): string | null {
  console.log('Extracting currency unit from response:', response);
  
  // Check if AI returned "NOT_FOUND"
  if (response.toLowerCase().includes('not_found') || response.toLowerCase().includes('not found')) {
    console.log('AI returned NOT_FOUND');
    return null;
  }
  
  // Look for currency unit patterns in the response
  const patterns = [
    // New structured format from improved prompts
    /currency\s*unit:\s*([^\n\r]+)/gi,
    // Common currency unit patterns
    /₹\s*in\s*(crores?|lakhs?|thousands?|millions?)/gi,
    /amounts?\s*in\s*(crores?|lakhs?|thousands?|millions?)/gi,
    /in\s*(crores?|lakhs?|thousands?|millions?)/gi,
    /\(₹\s*in\s*(crores?|lakhs?|thousands?|millions?)\)/gi,
    // Additional patterns for better detection
    /statement.*?₹\s*in\s*(crores?|lakhs?|thousands?|millions?)/gi,
    /financial\s*results.*?₹\s*in\s*(crores?|lakhs?|thousands?|millions?)/gi,
    /\(amounts?\s*in\s*(crores?|lakhs?|thousands?|millions?)\)/gi,
    // Direct patterns
    /(crores?|lakhs?|thousands?|millions?)/gi
  ];

  for (const pattern of patterns) {
    const matches = response.match(pattern);
    if (matches && matches.length > 0) {
      // Extract the currency unit
      const unitMatch = matches[0].match(/(crores?|lakhs?|thousands?|millions?)/gi);
      if (unitMatch) {
        const unit = unitMatch[0].toLowerCase();
        console.log('Extracted currency unit:', unit, 'from pattern:', pattern);
        return unit;
      }
    }
  }

  console.log('No currency unit found in response');
  return null;
}

// Face Value Comparison Function (similar to processBalanceVerification)
const processFaceValueComparison = async (
  mainDocument: File,
  comparisonDocument: File,
  checkId: string,
  model: string,
  temperature: number
): Promise<any> => {
  console.log(`Processing ${checkId} face value comparison with simple extraction and if-else logic`);
  console.log(`Main Document: ${mainDocument.name}`);
  console.log(`Comparison Document: ${comparisonDocument.name}`);

  try {
    // Step 1: Extract face value from main document
    const mainResult = await processCustomDocumentCheck(
      mainDocument,
      checkId,
      CHECK_PROMPTS['face_value_main_document'],
      model,
      temperature
    );

    console.log('Main document face value extraction result:', mainResult);

    // Step 2: Extract face value from comparison document (consolidated/standalone)
    const comparisonResult = await processCustomDocumentCheck(
      comparisonDocument,
      checkId,
      CHECK_PROMPTS['face_value_consolidated_standalone'],
      model,
      temperature
    );

    console.log('Comparison document face value extraction result:', comparisonResult);

    // Step 3: Extract face values from both results
    const mainFaceValue = extractFaceValueFromResponse(mainResult.detail);
    const comparisonFaceValue = extractFaceValueFromResponse(comparisonResult.detail);

    console.log('Extracted face values:');
    console.log('- Main document:', mainFaceValue);
    console.log('- Comparison document:', comparisonFaceValue);

    // Step 4: Compare face values using if-else logic
    let isCompliant = false;
    let comparisonDetails = '';

    if (mainFaceValue === null && comparisonFaceValue === null) {
      isCompliant = false;
      comparisonDetails = 'Face value not found in either document.';
    } else if (mainFaceValue === null) {
      isCompliant = false;
      comparisonDetails = `Face value not found in main document, but found in comparison document: ₹${comparisonFaceValue} per share.`;
    } else if (comparisonFaceValue === null) {
      isCompliant = false;
      comparisonDetails = `Face value found in main document: ₹${mainFaceValue} per share, but not found in comparison document.`;
    } else if (mainFaceValue === comparisonFaceValue) {
      isCompliant = true;
      comparisonDetails = `Face values match: ₹${mainFaceValue} per share in both documents.`;
    } else {
      isCompliant = false;
      comparisonDetails = `Face values do not match: Main document shows ₹${mainFaceValue} per share, Comparison document shows ₹${comparisonFaceValue} per share.`;
    }

    // Step 5: Create combined result
    const combinedDetails = `
MAIN DOCUMENT ANALYSIS:
${mainResult.detail}

COMPARISON DOCUMENT ANALYSIS:
${comparisonResult.detail}

FACE VALUE COMPARISON RESULT:
${comparisonDetails}
    `.trim();

    const finalResult = {
      isCompliant: isCompliant,
      explanation: comparisonDetails,
      detail: combinedDetails,
      confidence: Math.min(mainResult.confidence || 0.8, comparisonResult.confidence || 0.8),
      feedback: {
        reasoning: `Face value comparison between main document (${mainDocument.name}) and comparison document (${comparisonDocument.name}).`,
        evidence: [
          mainFaceValue !== null ? `Main document face value: ₹${mainFaceValue} per share` : 'Face value not found in main document',
          comparisonFaceValue !== null ? `Comparison document face value: ₹${comparisonFaceValue} per share` : 'Face value not found in comparison document'
        ],
        recommendations: isCompliant 
          ? ['Face values match correctly between documents.'] 
          : ['Ensure face values are consistently disclosed and match across all related documents.']
      }
    };

    console.log('Final face value comparison result:', finalResult);
    return finalResult;

  } catch (error) {
    console.error('Error in processFaceValueComparison:', error);
    throw error;
  }
};

// Standalone vs Consolidated Notes Comparison Function
const processStandaloneConsolidatedNotesComparison = async (
  mainDocument: File,
  comparisonDocument: File,
  checkId: string,
  model: string,
  temperature: number
): Promise<any> => {
  console.log(`Processing ${checkId} standalone vs consolidated notes comparison`);
  console.log(`Main Document: ${mainDocument.name}`);
  console.log(`Comparison Document: ${comparisonDocument.name}`);

  try {
    // Step 1: Extract notes from standalone document (main document)
    const standaloneResult = await processCustomDocumentCheck(
      mainDocument,
      checkId,
      CHECK_PROMPTS['standalone_notes_extraction'],
      model,
      temperature
    );

    console.log('Standalone document notes extraction result:', standaloneResult);

    // Step 2: Extract notes from consolidated document (comparison document)
    const consolidatedResult = await processCustomDocumentCheck(
      comparisonDocument,
      checkId,
      CHECK_PROMPTS['consolidated_notes_extraction'],
      model,
      temperature
    );

    console.log('Consolidated document notes extraction result:', consolidatedResult);

    // Step 3: Perform detailed comparison - inject both extraction results into the prompt
    const standaloneNotes = standaloneResult.detail || standaloneResult.explanation || '';
    const consolidatedNotes = consolidatedResult.detail || consolidatedResult.explanation || '';
    const comparisonPrompt = `${applyResultsAnalyzerStatusGuidance(CHECK_PROMPTS['standalone_consolidated_notes_comparison'])}

STANDALONE DOCUMENT NOTES EXTRACTION (from Step 1):
${standaloneNotes}

CONSOLIDATED DOCUMENT NOTES EXTRACTION (from Step 2):
${consolidatedNotes}

Using the above extracted notes from BOTH documents, perform the one-way comparison as instructed. Do NOT claim the standalone document is missing — its notes have been extracted and provided above.`;

    const comparisonResult = await processCustomDocumentCheck(
      comparisonDocument,
      checkId,
      comparisonPrompt,
      model,
      temperature
    );

    console.log('Notes comparison result:', comparisonResult);

    // Step 4: Extract compliance status from comparison result
    // Check for "Check" status first, then compliant
    const responseText = comparisonResult.detail || comparisonResult.explanation || '';
    const firstLine = responseText.split('\n')[0]?.toLowerCase().trim() || '';
    const isCheck = firstLine === 'check' || firstLine.includes('check');
    const isCompliant = !isCheck && firstLine.includes('compliant') && !firstLine.includes('non-compliant');

    // Step 5: Create combined result
    const combinedDetails = `
STANDALONE DOCUMENT NOTES ANALYSIS:
${standaloneResult.detail}

CONSOLIDATED DOCUMENT NOTES ANALYSIS:
${consolidatedResult.detail}

NOTES COMPARISON RESULT:
${comparisonResult.detail}
    `.trim();

    const finalResult = {
      isCompliant: isCompliant,
      isWarning: isCheck || !isCompliant,
      explanation: comparisonResult.detail.split('\n')[0] || 'Notes comparison completed',
      detail: combinedDetails,
      confidence: Math.min(
        standaloneResult.confidence || 0.8, 
        consolidatedResult.confidence || 0.8,
        comparisonResult.confidence || 0.8
      ),
      feedback: {
        reasoning: `Notes comparison between standalone document (${mainDocument.name}) and consolidated document (${comparisonDocument.name}).`,
        evidence: [
          `Standalone document notes extracted: ${standaloneResult.detail.split('\n')[1] || 'Notes found'}`,
          `Consolidated document notes extracted: ${consolidatedResult.detail.split('\n')[1] || 'Notes found'}`,
          `Comparison result: ${comparisonResult.detail.split('\n')[0] || 'Comparison completed'}`
        ],
        recommendations: isCompliant 
          ? ['All standalone notes are properly reflected in the consolidated document with appropriate adjustments.'] 
          : ['Review and ensure all standalone notes are included in the consolidated document with suitable consolidation adjustments.']
      }
    };

    console.log('Final notes comparison result:', finalResult);
    return finalResult;

  } catch (error) {
    console.error('Error in processStandaloneConsolidatedNotesComparison:', error);
    throw error;
  }
};

// CCE Tallying Check Function
const processCCETallyingCheck = async (
  document: File,
  checkId: string,
  model: string,
  temperature: number,
  abortSignal?: AbortSignal
): Promise<CheckResult> => {
  try {
    console.log(`Processing CCE tallying check: ${checkId}`);
    console.log(`Document: ${document.name}`);

    // Process single document containing both statements
    const result = await processCustomDocumentCheck(
      document,
      checkId,
      CHECK_PROMPTS['cce_tallying_check'],
      model,
      temperature,
      abortSignal
    );

    console.log('CCE tallying result:', result);

    // Extract compliance status from result
    const isCompliant = result.detail.toLowerCase().includes('compliant') && 
                      !result.detail.toLowerCase().includes('non-compliant');

    const finalResult = {
      isCompliant: isCompliant,
      explanation: result.detail.split('\n')[0] || 'CCE tallying check completed',
      detail: result.detail,
      confidence: result.confidence || 0.8,
      feedback: {
        reasoning: `CCE tallying verification between Statement of Cash Flows and Statement of Assets and Liabilities for both periods from single document (${document.name}).`,
        evidence: [
          `Document analyzed: ${document.name}`,
          `CCE tallying result: ${result.detail.split('\n')[0] || 'Analysis completed'}`
        ],
        recommendations: isCompliant 
          ? ['CCE amounts from Cash Flow Statement match with Balance Sheet for both periods within acceptable tolerance.'] 
          : ['Review CCE amounts for discrepancies between Cash Flow Statement and Balance Sheet. Ensure proper reconciliation and investigate any material differences.']
      }
    };

    console.log('Final CCE tallying result:', finalResult);
    return finalResult;

  } catch (error) {
    console.error('Error in processCCETallyingCheck:', error);
    throw error;
  }
};

// Balance Sheet Tallying Check Function
const processBalanceSheetTallyingCheck = async (
  document: File,
  checkId: string,
  model: string,
  temperature: number,
  abortSignal?: AbortSignal
): Promise<CheckResult> => {
  try {
    console.log(`Processing balance sheet tallying check: ${checkId}`);
    console.log(`Document: ${document.name}`);

    // Process the balance sheet tallying check
    const result = await processCustomDocumentCheck(
      document,
      checkId,
      CHECK_PROMPTS['balance_sheet_tallying_check'],
      model,
      temperature,
      abortSignal
    );

    console.log('Balance sheet tallying result:', result);

    // Extract compliance status from result
    const isCompliant = result.detail.toLowerCase().includes('compliant') && 
                      !result.detail.toLowerCase().includes('non-compliant');

    // Create enhanced result with additional feedback
    const enhancedResult = {
      isCompliant: isCompliant,
      explanation: result.detail.split('\n')[0] || 'Balance sheet tallying check completed',
      detail: result.detail,
      confidence: result.confidence || 0.8,
      feedback: {
        reasoning: `Balance sheet tallying verification for ${document.name} to ensure Assets = Equity + Liabilities for both periods.`,
        evidence: [
          `Balance sheet analysis completed: ${result.detail.split('\n')[1] || 'Analysis completed'}`,
          `Tallying result: ${result.detail.split('\n')[0] || 'Check completed'}`
        ],
        recommendations: isCompliant 
          ? ['Balance sheet sides tally correctly for both periods within acceptable tolerance.'] 
          : ['Review balance sheet for discrepancies between Assets and Equity + Liabilities. Ensure proper reconciliation and investigate any material differences.']
      }
    };

    console.log('Final balance sheet tallying result:', enhancedResult);
    return enhancedResult;

  } catch (error) {
    console.error('Error in processBalanceSheetTallyingCheck:', error);
    throw error;
  }
};

// Cash Flow Net Change Check Function
const processCashFlowNetChangeCheck = async (
  document: File,
  checkId: string,
  model: string,
  temperature: number,
  abortSignal?: AbortSignal
): Promise<CheckResult> => {
  try {
    console.log(`Processing cash flow net change check: ${checkId}`);
    console.log(`Document: ${document.name}`);

    // Process the cash flow net change check
    const result = await processCustomDocumentCheck(
      document,
      checkId,
      CHECK_PROMPTS['cashflow_net_change_check'],
      model,
      temperature,
      abortSignal
    );

    console.log('Cash flow net change result:', result);

    // Extract compliance status from result
    const isCompliant = result.detail.toLowerCase().includes('compliant') && 
                      !result.detail.toLowerCase().includes('non-compliant');

    // Create enhanced result with additional feedback
    const enhancedResult = {
      isCompliant: isCompliant,
      explanation: result.detail.split('\n')[0] || 'Cash flow net change check completed',
      detail: result.detail,
      confidence: result.confidence || 0.8,
      feedback: {
        reasoning: `Cash flow net change verification for ${document.name} to ensure OCF + ICF + FCF = Closing CCE - Opening CCE for both periods.`,
        evidence: [
          `Cash flow analysis completed: ${result.detail.split('\n')[1] || 'Analysis completed'}`,
          `Net change verification result: ${result.detail.split('\n')[0] || 'Check completed'}`
        ],
        recommendations: isCompliant 
          ? ['Cash flow net change calculation matches CCE movement for both periods within acceptable tolerance.'] 
          : ['Review cash flow statement for discrepancies between computed net change and CCE movement. Ensure proper reconciliation and investigate any material differences.']
      }
    };

    console.log('Final cash flow net change result:', enhancedResult);
    return enhancedResult;

  } catch (error) {
    console.error('Error in processCashFlowNetChangeCheck:', error);
    throw error;
  }
};

// Helper function to extract face value from AI response
function extractFaceValueFromResponse(response: string): number | null {
  console.log('Extracting face value from response:', response);
  
  // Check if AI returned "NOT_FOUND"
  if (response.toLowerCase().includes('not_found') || response.toLowerCase().includes('not found')) {
    console.log('AI returned NOT_FOUND');
    return null;
  }
  
  // Look for face value patterns in the response - expanded patterns
  const patterns = [
    // New structured format from improved prompts
    /face\s*value:\s*₹(\d+(?:\.\d+)?)/gi,
    /face\s*value:\s*(\d+(?:\.\d+)?)/gi,
    // Specific face value patterns
    /face\s*value.*?₹(\d+(?:\.\d+)?)/gi,
    /face\s*value.*?(\d+(?:\.\d+)?)\s*per\s*share/gi,
    /face\s*value.*?(\d+(?:\.\d+)?)\s*each/gi,
    /₹(\d+(?:\.\d+)?)\s*per\s*share/gi,
    /₹(\d+(?:\.\d+)?)\s*each/gi,
    /equity\s*share.*?₹(\d+(?:\.\d+)?)/gi,
    /fv\s*of\s*₹(\d+(?:\.\d+)?)/gi,
    /face\s*value\s*is\s*₹(\d+(?:\.\d+)?)/gi,
    /face\s*value\s*₹(\d+(?:\.\d+)?)/gi,
    // Generic patterns
    /₹(\d+(?:\.\d+)?)/g,
    /(\d+(?:\.\d+)?)\s*per\s*share/gi
  ];

  for (const pattern of patterns) {
    const matches = response.match(pattern);
    if (matches && matches.length > 0) {
      // Extract the first numeric value
      const valueMatch = matches[0].match(/(\d+(?:\.\d+)?)/);
      if (valueMatch) {
        const value = parseFloat(valueMatch[1]);
        console.log('Extracted face value:', value, 'from pattern:', pattern);
        return value;
      }
    }
  }

  // Try to find any number that could be a face value (common values: 1, 2, 5, 10, etc.)
  const numberMatches = response.match(/(\d+(?:\.\d+)?)/g);
  if (numberMatches) {
    // Common face values in Indian companies
    const commonFaceValues = [1, 2, 5, 10, 25, 50, 100];
    for (const match of numberMatches) {
      const value = parseFloat(match);
      if (commonFaceValues.includes(value)) {
        console.log('Found potential face value from common values:', value);
        return value;
      }
    }
  }

  console.log('No face value found in response');
  return null;
}

const formatTextForHTML = (text: string): string => {
  if (!text) return '';
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (t: string): string => {
    let h = esc(t);
    h = h.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    h = h.replace(/__(.+?)__/g, '<span style="text-decoration:underline;text-underline-offset:2px">$1</span>');
    h = h.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
    h = h.replace(/`(.+?)`/g, '<code class="inline-code">$1</code>');
    h = h.replace(/\bNon-Compliant\b/g, '<span class="status-tag nc">Non-Compliant</span>');
    h = h.replace(/(?<![-\w])Compliant\b/g, '<span class="status-tag c">Compliant</span>');
    return h;
  };
  const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l.trim());
  const isTableSep = (l: string) => /^\s*\|?(\s*:?-+:?\s*\|)+\s*$/.test(l.trim());
  const lines = text.split(/\r?\n/);
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i], tr = line.trim();
    if (/^-{3,}$|^\*{3,}$|^_{3,}$/.test(tr)) { out.push('<hr class="section-divider"/>'); i++; continue; }
    const hm = tr.match(/^(#{1,4})\s+(.+)$/);
    if (hm) {
      const lvl = hm[1].length;
      const cls = ['h-xl','h-lg','h-md','h-sm'][lvl-1];
      out.push(`<div class="fmt-heading ${cls}">${inline(hm[2])}</div>`);
      i++; continue;
    }
    if (isTableRow(line)) {
      const tl: string[] = [];
      while (i < lines.length && (isTableRow(lines[i]) || isTableSep(lines[i]) || !lines[i].trim())) { if (lines[i].trim()) tl.push(lines[i]); i++; }
      let hp = false, thead = '', tbody = '', ri = 0;
      for (const row of tl) {
        if (isTableSep(row)) continue;
        const cells = row.trim().replace(/^\|/,'').replace(/\|$/,'').split('|').map(c => inline(c.trim()));
        if (!hp) { thead = `<thead><tr>${cells.map(c=>`<th>${c}</th>`).join('')}</tr></thead>`; hp = true; }
        else { tbody += `<tr class="${ri%2?'alt':''}">${cells.map(c=>`<td>${c}</td>`).join('')}</tr>`; ri++; }
      }
      out.push(`<div class="fmt-table-wrap"><table class="fmt-table">${thead}<tbody>${tbody}</tbody></table></div>`);
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/,'')); i++; }
      out.push(`<ul class="fmt-list">${items.map(it=>`<li>${inline(it)}</li>`).join('')}</ul>`);
      continue;
    }
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+[.)]\s+/,'')); i++; }
      out.push(`<ol class="fmt-list ordered">${items.map(it=>`<li>${inline(it)}</li>`).join('')}</ol>`);
      continue;
    }
    i++;
    if (tr) out.push(`<p class="fmt-para">${inline(tr)}</p>`);
  }
  return out.join('');
};

// Generate HTML report
const generateHTMLReport = (analysisData: any): string => {
  const timestamp = new Date(analysisData.timestamp);
  const formattedDate = format(timestamp, "PPP 'at' p");
  
      // Helper function to get sort priority: 0 = Non-Compliant, 1 = Check, 2 = Compliant
      const getSortPriority = (checkId: string, result: any): number => {
        const checkStatusIds = ['eps_pat_ratio_comparison', 'subsidiary_word_highlighting', 'castings_check_horizontal', 'basic_logic_issues', 'tax_pbt_ratio_highlighting', 'standalone_consolidated_notes_interlink', 'profit_loss_quarter_ytd_consistency', 'line_items_comparison', 'qualification_eom_consistency'];
        const isSpecialCheck = checkStatusIds.includes(checkId) && !result.compliant;
        const isCheck = result.isWarning || isSpecialCheck;
        
        if (result.isDataUnavailable) return 3; // Data Unavailable
        if (!result.compliant && !isCheck) return 0; // Non-Compliant first
        if (isCheck) return 1; // Check second
        return 4; // Compliant last
      };
  
  // Generate AI Analysis Results HTML with expand/collapse
  const aiResultsHTML = Object.entries(analysisData.results)
    .filter(([checkId]) => !checkId.startsWith('user_question_'))
    .sort(([checkIdA, resultA], [checkIdB, resultB]) => {
      return getSortPriority(checkIdA, resultA) - getSortPriority(checkIdB, resultB);
    })
    .map(([checkId, result]: [string, any]) => {
      // Use metadata from results if available, otherwise fallback to lookup
      const checkTitle = result.checklistMetadata?.name || checkId.replace(/_/g, ' ');
      const checkDescription = result.checklistMetadata?.description || '';
      const checkCategory = result.checklistMetadata?.category || '';
      const isSpecialCheck = (checkId === 'eps_pat_ratio_comparison' || checkId === 'subsidiary_word_highlighting' || checkId === 'castings_check_horizontal' || checkId === 'basic_logic_issues' || checkId === 'tax_pbt_ratio_highlighting' || checkId === 'standalone_consolidated_notes_interlink' || checkId === 'profit_loss_quarter_ytd_consistency' || checkId === 'line_items_comparison' || checkId === 'qualification_eom_consistency') && !result.compliant;
      const isCheck = result.isWarning || isSpecialCheck;
      const compliantClass = result.isDataUnavailable ? 'data-unavailable' : isCheck ? 'check' : (result.compliant ? 'compliant' : 'non-compliant');
      const compliantText = result.isDataUnavailable ? 'Data Unavailable' : isCheck ? 'Check' : (result.compliant ? 'Compliant' : 'Non-Compliant');
      
      return `
        <div class="result-card">
          <div class="result-header" onclick="toggleCard('${checkId}')">
            <div class="result-title-section">
              <h4 class="result-title">${checkTitle}</h4>
              ${checkCategory ? `<span class="result-category">${checkCategory}</span>` : ''}
              ${checkDescription ? `<p class="result-description">${checkDescription}</p>` : ''}
            </div>
            <div class="header-actions">
              <span class="badge ${compliantClass}">${compliantText}</span>
              <button class="expand-btn" onclick="event.stopPropagation(); toggleCard('${checkId}')">
                <svg class="expand-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          <div class="result-content" id="content-${checkId}" style="display: none;">
            <div class="check-status-bar">
              <span class="status-pill ${compliantClass === 'data-unavailable' ? 's-data-unavailable' : compliantClass === 'check' ? 's-check' : compliantClass === 'compliant' ? 's-compliant' : 's-non-compliant'}">${compliantText}</span>
              ${result.confidence ? `<span class="meta-item">Confidence: ${Math.round((result.confidence || 0) * 100)}%</span>` : ''}
              ${result.model_used ? `<span class="meta-item">Model: ${result.model_used}</span>` : ''}
            </div>

            <details open class="section-accordion acc-analysis">
              <summary class="section-head">
                <span class="section-icon">📋</span>
                Analysis Details
                <svg class="toggle-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </summary>
              <div class="section-body">${formatTextForHTML(result.details)}</div>
            </details>
            
            <details class="section-accordion acc-reasoning">
              <summary class="section-head">
                <span class="section-icon">💡</span>
                Reasoning
                <svg class="toggle-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </summary>
              <div class="section-body">${formatTextForHTML(result.feedback.reasoning)}</div>
            </details>
            
            ${result.feedback.evidence.length > 0 ? `
            <details class="section-accordion acc-evidence">
              <summary class="section-head">
                <span class="section-icon">🔍</span>
                Evidence Found <span style="font-weight:400;color:#64748b;font-size:0.8rem;margin-left:4px">(${result.feedback.evidence.length} items)</span>
                <svg class="toggle-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </summary>
              <div class="section-body">
                <ul class="fmt-list">${result.feedback.evidence.map((ev: string) => `<li>${formatTextForHTML(ev)}</li>`).join('')}</ul>
              </div>
            </details>
            ` : ''}
            
            ${result.feedback.recommendations && result.feedback.recommendations.length > 0 ? `
            <details class="section-accordion acc-recs">
              <summary class="section-head">
                <span class="section-icon">⚡</span>
                Recommendations <span style="font-weight:400;color:#64748b;font-size:0.8rem;margin-left:4px">(${result.feedback.recommendations.length})</span>
                <svg class="toggle-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </summary>
              <div class="section-body">
                <ul class="fmt-list">${result.feedback.recommendations.map((rec: string) => `<li>${formatTextForHTML(rec)}</li>`).join('')}</ul>
              </div>
            </details>
            ` : ''}
          </div>
        </div>
      `;
    }).join('');

  // Generate User Questions Results HTML with expand/collapse
  const userQuestionsHTML = Object.entries(analysisData.results)
    .filter(([checkId]) => checkId.startsWith('user_question_'))
    .map(([checkId, result]: [string, any]) => {
      const questionId = checkId.replace('user_question_', '');
      const question = USER_QUESTIONS.find(q => q.id === questionId);
      
      // Extract the actual user answer from the result
      const userAnswer = (result as any).userAnswer || result.details?.replace('User Answer: ', '')?.split(' - ')[0] || 'N/A';
      const answerClass = userAnswer === 'yes' ? 'compliant' : userAnswer === 'no' ? 'non-compliant' : 'warning';
      const answerText = userAnswer === 'yes' ? 'Yes' : userAnswer === 'no' ? 'No' : 'N/A';
      
      return `
        <div class="result-card">
          <div class="result-header" onclick="toggleCard('${checkId}')">
            <h4 class="result-title">${question ? question.question : questionId.replace(/_/g, ' ')}</h4>
            <div class="header-actions">
              <span class="badge ${answerClass}">${answerText}</span>
              <button class="expand-btn" onclick="event.stopPropagation(); toggleCard('${checkId}')">
                <svg class="expand-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          <div class="result-content" id="content-${checkId}" style="display: none;">
            <div class="check-status-bar">
              <span class="status-pill ${answerClass === 'compliant' ? 's-compliant' : answerClass === 'non-compliant' ? 's-non-compliant' : 's-check'}">User: ${answerText}</span>
            </div>

            <details open class="section-accordion acc-analysis">
              <summary class="section-head">
                <span class="section-icon">📝</span>
                User Response
                <svg class="toggle-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </summary>
              <div class="section-body"><strong>${answerText}</strong>${result.details?.includes(' - ') ? ' - ' + result.details.split(' - ').slice(1).join(' - ') : ''}</div>
            </details>
            
            <details class="section-accordion acc-reasoning">
              <summary class="section-head">
                <span class="section-icon">💡</span>
                Reasoning
                <svg class="toggle-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </summary>
              <div class="section-body">${formatTextForHTML(result.feedback.reasoning)}</div>
            </details>
            
            ${result.feedback.evidence.length > 0 ? `
            <details class="section-accordion acc-evidence">
              <summary class="section-head">
                <span class="section-icon">🔍</span>
                Additional Notes
                <svg class="toggle-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </summary>
              <div class="section-body">
                <ul class="fmt-list">${result.feedback.evidence.map((ev: string) => `<li>${formatTextForHTML(ev)}</li>`).join('')}</ul>
              </div>
            </details>
            ` : ''}
            
            ${result.feedback.recommendations && result.feedback.recommendations.length > 0 ? `
            <details class="section-accordion acc-recs">
              <summary class="section-head">
                <span class="section-icon">⚡</span>
                Recommendations
                <svg class="toggle-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </summary>
              <div class="section-body">
                <ul class="fmt-list">${result.feedback.recommendations.map((rec: string) => `<li>${formatTextForHTML(rec)}</li>`).join('')}</ul>
              </div>
            </details>
            ` : ''}
          </div>
        </div>
      `;
    }).join('');

  // Generate User Answers Summary HTML (removed as requested)
  const userAnswersSummaryHTML = '';

  return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Results Analysis Report</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            background-color: #f8fafc;
            padding: 20px;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            overflow: hidden;
        }
        
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px;
            text-align: center;
        }
        
        .header h1 {
            font-size: 2.5rem;
            margin-bottom: 10px;
            font-weight: 700;
        }
        
        .header p {
            font-size: 1.1rem;
            opacity: 0.9;
        }
        
        .content {
            padding: 30px;
        }
        
        .summary-section {
            background: #f8fafc;
            border-radius: 8px;
            padding: 25px;
            margin-bottom: 30px;
        }
        
        .partial-notice {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 15px;
            color: #856404;
        }
        
        .summary-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            margin: 20px 0;
        }
        
        .stat-card {
            background: white;
            padding: 20px;
            border-radius: 8px;
            text-align: center;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .stat-card.compliant { border-left: 4px solid #10b981; }
        .stat-card.non-compliant { border-left: 4px solid #ef4444; }
        .stat-card.check { border-left: 4px solid #f97316; }
        .stat-card.data-unavailable { border-left: 4px solid #fb923c; }
        .stat-card.partial { border-left: 4px solid #f59e0b; }
        .stat-card.percentage { border-left: 4px solid #8b5cf6; }
        
        .stat-number {
            font-size: 2.5rem;
            font-weight: bold;
            margin-bottom: 5px;
        }
        
        .stat-card.compliant .stat-number { color: #10b981; }
        .stat-card.non-compliant .stat-number { color: #ef4444; }
        .stat-card.check .stat-number { color: #f97316; }
        .stat-card.data-unavailable .stat-number { color: #fb923c; }
        .stat-card.partial .stat-number { color: #f59e0b; }
        .stat-card.percentage .stat-number { color: #8b5cf6; }
        
        .stat-label {
            color: #6b7280;
            font-size: 0.9rem;
        }
        
        .progress-bar {
            width: 100%;
            height: 12px;
            background-color: #e5e7eb;
            border-radius: 6px;
            overflow: hidden;
            margin: 20px 0;
        }
        
        .progress-fill {
            height: 100%;
            transition: width 0.3s ease;
        }
        
        .progress-fill.low { background-color: #ef4444; }
        .progress-fill.medium { background-color: #f59e0b; }
        .progress-fill.high { background-color: #10b981; }
        
        .section-title {
            font-size: 1.5rem;
            font-weight: 600;
            margin: 30px 0 20px 0;
            color: #1f2937;
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 10px;
        }
        
        .subsection-title {
            font-size: 1.1rem;
            font-weight: 600;
            margin: 20px 0 15px 0;
            color: #374151;
        }
        
        .results-controls {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 30px 0 20px 0;
            flex-wrap: wrap;
            gap: 15px;
        }
        
        .control-buttons {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
        }
        
        .control-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background: white;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.875rem;
            font-weight: 500;
            color: #374151;
            cursor: pointer;
            transition: all 0.2s;
        }
        
        .control-btn:hover {
            background: #f9fafb;
            border-color: #9ca3af;
        }
        
        .btn-icon {
            width: 16px;
            height: 16px;
        }
        
        .result-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            margin-bottom: 20px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .result-header {
            background: #f9fafb;
            padding: 15px 20px;
            border-bottom: 1px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;
            transition: background-color 0.2s;
        }
        
        .result-header:hover {
            background: #f3f4f6;
        }
        
        .result-title-section {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
        
        .result-title {
            font-size: 1.1rem;
            font-weight: 600;
            color: #1f2937;
            margin: 0;
        }
        
        .result-category {
            font-size: 0.75rem;
            color: #6b7280;
            background: #f3f4f6;
            padding: 2px 8px;
            border-radius: 12px;
            width: fit-content;
        }
        
        .result-description {
            font-size: 0.875rem;
            color: #6b7280;
            margin: 8px 0 0 0;
            line-height: 1.5;
        }
        
        .header-actions {
            display: flex;
            align-items: center;
            gap: 12px;
        }
        
        .badge {
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 0.8rem;
            font-weight: 600;
            text-transform: uppercase;
        }
        
        .expand-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background-color 0.2s;
        }
        
        .expand-btn:hover {
            background: #e5e7eb;
        }
        
        .expand-icon {
            width: 16px;
            height: 16px;
            transition: transform 0.2s;
        }
        
        .expand-icon.expanded {
            transform: rotate(90deg);
        }
        
        .badge.compliant {
            background-color: #dcfce7;
            color: #166534;
        }
        
        .badge.non-compliant {
            background-color: #fee2e2;
            color: #991b1b;
        }
        
        .badge.check {
            background-color: #f97316;
            color: #ffffff;
        }
        
        .badge.data-unavailable {
            background-color: #fed7aa;
            color: #9a3412;
        }
        
        .result-content {
            padding: 20px;
        }
        
        .result-content h5 {
            font-size: 1rem;
            font-weight: 600;
            margin: 15px 0 8px 0;
            color: #374151;
        }
        
        .result-content p, .result-content li {
            color: #6b7280;
            margin-bottom: 8px;
        }
        
        .result-content ul {
            margin-left: 20px;
        }
        
        .check-description {
            background: #eff6ff;
            padding: 15px;
            border-radius: 6px;
            margin-bottom: 15px;
            border-left: 4px solid #3b82f6;
        }
        
        /* --- Nested accordion sections --- */
        .section-accordion {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            margin-bottom: 12px;
            overflow: hidden;
            transition: box-shadow 0.2s;
        }
        .section-accordion:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
        .section-accordion[open] > .section-head .toggle-chevron { transform: rotate(90deg); }

        .section-head {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 16px;
            cursor: pointer;
            font-weight: 600;
            font-size: 0.9rem;
            color: #1e293b;
            user-select: none;
            list-style: none;
            transition: background 0.15s;
        }
        .section-head::-webkit-details-marker { display: none; }
        .section-head:hover { filter: brightness(0.97); }

        .section-head .section-icon {
            width: 28px; height: 28px; border-radius: 6px;
            display: flex; align-items: center; justify-content: center;
            font-size: 0.85rem; flex-shrink: 0;
        }
        .toggle-chevron {
            margin-left: auto; width: 16px; height: 16px; color: #94a3b8;
            transition: transform 0.2s ease;
        }

        .section-body { padding: 0 16px 16px 16px; }

        .acc-analysis .section-head { background: #f8fafc; }
        .acc-analysis .section-icon { background: #e2e8f0; color: #475569; }
        .acc-reasoning .section-head { background: #eff6ff; }
        .acc-reasoning .section-icon { background: #dbeafe; color: #2563eb; }
        .acc-evidence .section-head { background: #f0fdf4; }
        .acc-evidence .section-icon { background: #dcfce7; color: #16a34a; }
        .acc-recs .section-head { background: #fffbeb; }
        .acc-recs .section-icon { background: #fef3c7; color: #d97706; }

        /* --- Status bar --- */
        .check-status-bar {
            display: flex; align-items: center; gap: 16px;
            padding: 10px 16px; margin-bottom: 12px;
            background: #f8fafc; border: 1px solid #e2e8f0;
            border-radius: 8px; font-size: 0.85rem;
        }
        .check-status-bar .status-pill {
            padding: 3px 12px; border-radius: 20px; font-weight: 700; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.03em;
        }
        .status-pill.s-compliant { background: #dcfce7; color: #166534; }
        .status-pill.s-non-compliant { background: #fee2e2; color: #991b1b; }
        .status-pill.s-check { background: #fff7ed; color: #c2410c; }
        .status-pill.s-data-unavailable { background: #fed7aa; color: #9a3412; }
        .check-status-bar .meta-item { color: #64748b; font-size: 0.8rem; }

        /* --- Rich text formatting --- */
        .fmt-heading { margin: 14px 0 6px; color: #1e293b; }
        .h-xl { font-size: 1.1rem; font-weight: 700; border-bottom: 2px solid #e5e7eb; padding-bottom: 4px; }
        .h-lg { font-size: 1rem; font-weight: 600; }
        .h-md { font-size: 0.95rem; font-weight: 600; color: #475569; }
        .h-sm { font-size: 0.85rem; font-weight: 500; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }

        .fmt-table-wrap { overflow-x: auto; margin: 12px 0; border: 1px solid #e2e8f0; border-radius: 8px; }
        .fmt-table { width: 100%; border-collapse: collapse; }
        .fmt-table th { padding: 10px 14px; text-align: left; font-weight: 600; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; background: #f8fafc; border-bottom: 2px solid #e2e8f0; }
        .fmt-table td { padding: 8px 14px; border-bottom: 1px solid #f1f5f9; font-size: 0.88rem; color: #334155; }
        .fmt-table tr.alt { background: #f8fafc; }
        .fmt-table tr:hover { background: #eff6ff; }

        .fmt-list { margin: 8px 0 8px 8px; padding: 0; list-style: none; }
        .fmt-list li { display: flex; align-items: flex-start; gap: 8px; margin: 5px 0; font-size: 0.88rem; color: #374151; line-height: 1.6; }
        .fmt-list li::before { content: ''; flex-shrink: 0; width: 6px; height: 6px; margin-top: 8px; border-radius: 50%; background: #94a3b8; }
        .fmt-list.ordered li::before { display: none; }
        .fmt-list.ordered { counter-reset: ol-counter; }
        .fmt-list.ordered li { counter-increment: ol-counter; }
        .fmt-list.ordered li::after { content: none; }

        .fmt-para { margin: 6px 0; font-size: 0.88rem; line-height: 1.7; color: #374151; }

        .section-divider { border: none; border-top: 1px solid #e5e7eb; margin: 14px 0; }

        .inline-code { background: #f1f5f9; padding: 1px 6px; border-radius: 4px; font-size: 0.83em; color: #1e293b; font-family: ui-monospace, monospace; }
        .status-tag { padding: 1px 8px; border-radius: 12px; font-weight: 600; font-size: 0.78rem; white-space: nowrap; }
        .status-tag.c { background: #dcfce7; color: #166534; }
        .status-tag.nc { background: #fee2e2; color: #991b1b; }

        .details-text { color: #374151; line-height: 1.6; }
        
        .user-answers-section {
            background: #f8fafc;
            border-radius: 8px;
            padding: 25px;
            margin-bottom: 30px;
        }
        
        .user-answers-list {
            margin-top: 20px;
        }
        
        .user-answer-item {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            padding: 15px;
            margin-bottom: 10px;
        }
        
        .question-text {
            margin-bottom: 10px;
        }
        
        .answer-info {
            display: flex;
            align-items: center;
            gap: 10px;
        }
        
        .answer-badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.8rem;
            font-weight: 600;
        }
        
        .answer-badge.yes {
            background-color: #dcfce7;
            color: #166534;
        }
        
        .answer-badge.no {
            background-color: #fee2e2;
            color: #991b1b;
        }
        
        .answer-badge.na {
            background-color: #f3f4f6;
            color: #374151;
        }
        
        .answer-note {
            font-size: 0.8rem;
            color: #6b7280;
            font-style: italic;
        }
        
        .footer {
            background: #f9fafb;
            padding: 20px;
            text-align: center;
            color: #6b7280;
            border-top: 1px solid #e5e7eb;
        }
        
        @media print {
            body {
                background: white;
                padding: 0;
            }
            
            .container {
                box-shadow: none;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Results Analysis Report</h1>
            <p>Analysis completed on ${formattedDate}</p>
        </div>
        
        <div class="content">
            <div class="summary-section">
                <h2>Summary${analysisData.summary.isPartial ? ' (Partial Results)' : ''}</h2>
                ${analysisData.summary.isPartial ? `
                <div class="partial-notice">
                    <p><strong>Note:</strong> This analysis was stopped early. Showing results for ${analysisData.summary.completedChecks} out of ${analysisData.summary.totalSelectedChecks} selected checks.</p>
                </div>
                ` : ''}
                <div class="summary-stats">
                    <div class="stat-card compliant">
                        <div class="stat-number">${analysisData.summary.compliant || 0}</div>
                        <div class="stat-label">Compliant</div>
                    </div>
                    <div class="stat-card non-compliant">
                        <div class="stat-number">${analysisData.summary.nonCompliant || 0}</div>
                        <div class="stat-label">Non-Compliant</div>
                    </div>
                    <div class="stat-card check">
                        <div class="stat-number">${analysisData.summary.check || 0}</div>
                        <div class="stat-label">Check</div>
                    </div>
                    <div class="stat-card data-unavailable">
                        <div class="stat-number">${analysisData.summary.dataUnavailable || 0}</div>
                        <div class="stat-label">Data Unavailable</div>
                    </div>
                    <div class="stat-card percentage">
                        <div class="stat-number">${analysisData.summary.compliancePercentage || 0}%</div>
                        <div class="stat-label">Compliance Rate</div>
                    </div>
                </div>
                
                <div class="progress-bar">
                    <div class="progress-fill ${analysisData.summary.compliancePercentage < 50 ? 'low' : analysisData.summary.compliancePercentage < 80 ? 'medium' : 'high'}" 
                         style="width: ${analysisData.summary.compliancePercentage}%"></div>
                </div>
                
            </div>
            
            ${userAnswersSummaryHTML}
            
            <div class="results-controls">
                <h3 class="section-title">Detailed Results</h3>
                <div class="control-buttons">
                    <button class="control-btn" onclick="expandAllCards()">Expand All</button>
                    <button class="control-btn" onclick="collapseAllCards()">Collapse All</button>
                </div>
            </div>
            
            <div class="results-section">
                <h4 class="subsection-title">AI Analysis Results</h4>
                ${aiResultsHTML}
            </div>
            
            ${userQuestionsHTML ? `
            <div class="results-section">
                <h4 class="subsection-title">Auditor Declared Questions Results</h4>
                ${userQuestionsHTML}
            </div>
            ` : ''}
        </div>
        
        <div class="footer">
            <p>Generated on ${formattedDate} | Results Analysis Report</p>
        </div>
    </div>
    
    <script>
        // Expand/Collapse functionality
        function toggleCard(checkId) {
            const content = document.getElementById('content-' + checkId);
            const expandIcon = document.querySelector('[onclick*="' + checkId + '"] .expand-icon');
            
            if (content.style.display === 'none' || content.style.display === '') {
                content.style.display = 'block';
                if (expandIcon) {
                    expandIcon.classList.add('expanded');
                }
            } else {
                content.style.display = 'none';
                if (expandIcon) {
                    expandIcon.classList.remove('expanded');
                }
            }
        }
        
        function expandAllCards() {
            const allContents = document.querySelectorAll('[id^="content-"]');
            const allIcons = document.querySelectorAll('.expand-icon');
            
            allContents.forEach(content => {
                content.style.display = 'block';
            });
            
            allIcons.forEach(icon => {
                icon.classList.add('expanded');
            });
        }
        
        function collapseAllCards() {
            const allContents = document.querySelectorAll('[id^="content-"]');
            const allIcons = document.querySelectorAll('.expand-icon');
            
            allContents.forEach(content => {
                content.style.display = 'none';
            });
            
            allIcons.forEach(icon => {
                icon.classList.remove('expanded');
            });
        }
        
        
        function showNotification(title, message) {
            // Create notification element
            const notification = document.createElement('div');
            notification.style.cssText = \`
                position: fixed;
                top: 20px;
                right: 20px;
                background: #10b981;
                color: white;
                padding: 12px 20px;
                border-radius: 6px;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                z-index: 1000;
                font-size: 14px;
                max-width: 300px;
            \`;
            
            notification.innerHTML = \`
                <div style="font-weight: 600; margin-bottom: 4px;">\${title}</div>
                <div style="opacity: 0.9;">\${message}</div>
            \`;
            
            document.body.appendChild(notification);
            
            // Remove notification after 3 seconds
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 3000);
        }
    </script>
</body>
</html>
  `;
};

const ResultsAnalyzer = () => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State management
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisData, setAnalysisData] = useState<AnalysisResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const cancellationRef = useRef<AbortController | null>(null);
  const [showCheckSelection, setShowCheckSelection] = useState(false);
  const [processingProgress, setProcessingProgress] = useState<ProcessingProgress>({
    completed: 0,
    total: 1,
    currentCheck: "Ready to start...",
    phase: 'initializing'
  });

  // Form state
  const [parameters, setParameters] = useState<ResultsAnalysisParameters>({});
  const [selectedQuarter, setSelectedQuarter] = useState<string>('');
  const [selectedType, setSelectedType] = useState<'consolidated' | 'standalone' | 'Normal' | ''>('');
  
  // Company name dialog state
  const [companyNameDialogOpen, setCompanyNameDialogOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyNameError, setCompanyNameError] = useState("");
  
  // Auditor comments state
  const [auditorComments, setAuditorComments] = useState<Record<string, string>>({});

  // Document file state
  const [resultsReport, setResultsReport] = useState<File | null>(null);
  const [secondDocument, setSecondDocument] = useState<File | null>(null);
  const [consolidatedStandaloneDocument, setConsolidatedStandaloneDocument] = useState<File | null>(null);
  const [previousYearDocument, setPreviousYearDocument] = useState<File | null>(null);
  
  // Show company name dialog when analysis completes
  React.useEffect(() => {
    if (analysisComplete && analysisData && !companyName) {
      setTimeout(() => {
        setCompanyNameDialogOpen(true);
      }, 500);
    }
  }, [analysisComplete, analysisData]);

  // Check selection state
  const [availableChecks, setAvailableChecks] = useState<CheckOption[]>(AVAILABLE_CHECKS);
  const [selectedCheckIds, setSelectedCheckIds] = useState<string[]>([]);
  
  // Collapse/expand state for result cards
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  
  // User declared questions state
  const [userAnswers, setUserAnswers] = useState<UserQuestionAnswer[]>([]);
  const [showUserQuestions, setShowUserQuestions] = useState(false);
  const [questionsValid, setQuestionsValid] = useState(false);

  // Debug user answers changes
  const handleUserAnswersChange = (answers: UserQuestionAnswer[]) => {
    console.log('🔍 ResultsAnalyzer: Received user answers change:', answers);
    console.log('🔍 ResultsAnalyzer: User answers count:', answers.length);
    setUserAnswers(answers);
  };

  // Audit questions state
  const [auditAnswers, setAuditAnswers] = useState<Record<string, string>>({});
  const [showAuditQuestions, setShowAuditQuestions] = useState(false);

  // Masked PDF confirmation dialog state
  const [showMaskedPdfConfirmation, setShowMaskedPdfConfirmation] = useState(false);
  const [maskedConfirmationText, setMaskedConfirmationText] = useState("");

  // Function to filter questions based on dependencies
  const getFilteredQuestions = (questions: any[], answers: Record<string, string>) => {
    return questions.filter(question => {
      // If question has no dependencies, show it
      if (!question.dependsOn) return true;
      
      // Check if the dependency condition is met
      const dependencyAnswer = answers[question.dependsOn];
      return dependencyAnswer === question.showWhen;
    });
  };

  // Function to get relevant audit checks based on answers
  const getRelevantAuditChecks = (answers: Record<string, string>, selectedType: string): CheckOption[] => {
    const relevantCheckIds: string[] = [];
    
    // Map answers to check IDs - only for active questions in AUDIT_QUESTIONS
    const answerToCheckMap: Record<string, string[]> = {  
      'major_events_occurred': ['major_events_explanation_notes', 'major_events_notes'],
      'audit_report_qualified': ['qualified_audit_report_impact_disclosure', 'qualified_audit_explanation'],
      'material_reclassification': ['material_reclassification_explanation'],
      'foreign_country_components': ['foreign_component_translation_disclosure'],
      'goodwill_impaired': ['goodwill_impairment_note'],
      'associates_joint_ventures': ['associates_jv_oci', 'associates_jv_audit_report_mention'],
    };

    // Get filtered questions based on dependencies
    const relevantQuestions = getFilteredQuestions(AUDIT_QUESTIONS, answers);

    // Get relevant check IDs based on answers
    relevantQuestions.forEach(question => {
      const answer = answers[question.id];
      if (answer && answer === 'Yes') {
        const checkIds = answerToCheckMap[question.id] || [];
        relevantCheckIds.push(...checkIds);
      }
    });

    // Return the relevant checks
    return AVAILABLE_CHECKS.filter(check => relevantCheckIds.includes(check.id));
  };

  // Function to filter checks based on selected quarter and type
  const getFilteredChecks = (quarter: string, type: string): CheckOption[] => {
    // If no quarter or type selected, return all checks
    if (!quarter && !type) return AVAILABLE_CHECKS;
    
    // If only quarter is selected, return quarter-specific checks
    if (quarter && !type) {
      const quarterCheckIds = QUARTER_CHECKS[quarter] || [];
      // Some checks require multiple documents (e.g., standalone vs consolidated comparison).
      // Hide them until the required documents are uploaded.
      return AVAILABLE_CHECKS.filter(check =>
        quarterCheckIds.includes(check.id) &&
        (check.id !== 'face_value_share' || !!consolidatedStandaloneDocument)
      );
    }
    
    // If only type is selected, return type-specific checks
    if (!quarter && type) {
      const typeCheckIds = TYPE_CHECKS[type] || [];
      return AVAILABLE_CHECKS.filter(check =>
        typeCheckIds.includes(check.id) &&
        (check.id !== 'face_value_share' || !!consolidatedStandaloneDocument)
      );
    }
    
    // When BOTH quarter AND type are selected, apply intersection filtering
    const quarterCheckIds = QUARTER_CHECKS[quarter] || [];
    const typeCheckIds = TYPE_CHECKS[type] || [];
    
    // Get intersection of quarter and type checks - only show checks that match BOTH quarter AND type
    const commonCheckIds = quarterCheckIds.filter(id => typeCheckIds.includes(id));
    
    // Get base filtered checks from the intersection
    const baseChecks = AVAILABLE_CHECKS.filter(check =>
      commonCheckIds.includes(check.id) &&
      (check.id !== 'face_value_share' || !!consolidatedStandaloneDocument)
    );
    
    // Add relevant audit checks based on answers
    const auditChecks = getRelevantAuditChecks(auditAnswers, type);
    
    // Add multi-document checks based on available documents
    const multiDocumentChecks: CheckOption[] = [];
    
    // Add Q2/Q4 balance verification checks if second document is available
    if (isMultiDocumentRequired && resultsReport && secondDocument) {
      if (quarter === 'Q2') {
        const q2BalanceCheck = AVAILABLE_CHECKS.find(check => check.id === 'q2_balance_verification');
        if (q2BalanceCheck) multiDocumentChecks.push(q2BalanceCheck);
      } else if (quarter === 'Q4') {
        const q4BalanceCheck = AVAILABLE_CHECKS.find(check => check.id === 'q4_balance_verification');
        if (q4BalanceCheck) multiDocumentChecks.push(q4BalanceCheck);
      }
    }
    
    // Add consolidated/standalone interlink checks if consolidated/standalone document is available
    if ((type === 'consolidated' || type === 'standalone') && consolidatedStandaloneDocument) {
      const faceValueCheck = AVAILABLE_CHECKS.find(check => check.id === 'face_value_share');
      if (faceValueCheck) {
        // Create a copy with updated description for multi-document use
        const multiDocFaceValueCheck = {
          ...faceValueCheck,
          description: 'Compare face value of shares between main document and consolidated/standalone document (interlinked multi-document)'
        };
        multiDocumentChecks.push(multiDocFaceValueCheck);
      }
      
      // Add currency unit consistency check if consolidated/standalone document is uploaded
      const currencyUnitCheck = AVAILABLE_CHECKS.find(check => check.id === 'currency_unit_consistency');
      if (currencyUnitCheck) {
        // Create a copy with updated description for multi-document use
        const multiDocCurrencyUnitCheck = {
          ...currencyUnitCheck,
          description: 'Compare currency measurement units between main document and consolidated/standalone document (interlinked multi-document)'
        };
        multiDocumentChecks.push(multiDocCurrencyUnitCheck);
      }
      
      // Add standalone vs consolidated notes interlink + EPS PAT ratio only for companies with components
      const hasComponents = auditAnswers['company_has_components'] !== 'No';
      if (hasComponents) {
        const notesInterlinkCheck = AVAILABLE_CHECKS.find(check => check.id === 'standalone_consolidated_notes_interlink');
        if (notesInterlinkCheck) {
          const multiDocNotesInterlinkCheck = {
            ...notesInterlinkCheck,
            description: 'Verify all notes in standalone document are repeated in consolidated document with suitable changes (interlinked multi-document)'
          };
          multiDocumentChecks.push(multiDocNotesInterlinkCheck);
        }
        
        const epsPatRatioCheck = AVAILABLE_CHECKS.find(check => check.id === 'eps_pat_ratio_comparison');
        if (epsPatRatioCheck) {
          const multiDocEpsPatRatioCheck = {
            ...epsPatRatioCheck,
            description: 'Compare ratio of EPS to Consolidated PAT with standalone ratio of EPS to Standalone PAT (interlinked multi-document)'
          };
          multiDocumentChecks.push(multiDocEpsPatRatioCheck);
        }
      }
      
      // Add Qualification/EOM Consistency check (always available when consolidated/standalone document is uploaded)
      const qualEomCheck = AVAILABLE_CHECKS.find(check => check.id === 'qualification_eom_consistency');
      if (qualEomCheck) {
        const multiDocQualEomCheck = {
          ...qualEomCheck,
          description: 'Check if qualification/EOM in standalone audit report is also present in consolidated audit report (interlinked multi-document)'
        };
        multiDocumentChecks.push(multiDocQualEomCheck);
      }
      
      // Add line items comparison check if previous year end document is uploaded
      const lineItemsComparisonCheck = AVAILABLE_CHECKS.find(check => check.id === 'line_items_comparison');
      if (lineItemsComparisonCheck) {
        // Create a copy with updated description for multi-document use
        const multiDocLineItemsComparisonCheck = {
          ...lineItemsComparisonCheck,
          description: 'Compare line items between Previous Year End and Current Quarter Statements of Results (interlinked multi-document)'
        };
        multiDocumentChecks.push(multiDocLineItemsComparisonCheck);
      }
    }
    
    // Add conditional checks based on audit answers
    const conditionalChecks: CheckOption[] = [];
    
    if (auditAnswers && typeof auditAnswers === 'object' && Object.keys(auditAnswers).length > 0) {
      const associatesAnswer = auditAnswers['associates_joint_ventures'];
      if (associatesAnswer && associatesAnswer === 'Yes') {
        const associatesJvOciCheck = AVAILABLE_CHECKS.find(check => check.id === 'associates_jv_oci');
        if (associatesJvOciCheck) {
          conditionalChecks.push(associatesJvOciCheck);
        }
      }

      const reclassAnswer = auditAnswers['material_reclassification'];
      if (reclassAnswer && reclassAnswer === 'Yes') {
        const reclassCheck = AVAILABLE_CHECKS.find(check => check.id === 'material_reclassification_explanation');
        if (reclassCheck) {
          conditionalChecks.push(reclassCheck);
        }
      }

      const pkfAnswer = auditAnswers['pkf_auditor_co_reviewer'];
      if (pkfAnswer === 'Auditor') {
        const sre2400Check = AVAILABLE_CHECKS.find(check => check.id === 'sre_2400_mention');
        if (sre2400Check) conditionalChecks.push(sre2400Check);
      } else if (pkfAnswer === 'Co-reviewer') {
        const sre2410Check = AVAILABLE_CHECKS.find(check => check.id === 'sre_2410_mention');
        if (sre2410Check) conditionalChecks.push(sre2410Check);
      }
    }
    
    // Debug logging
    console.log('Filtering checks:', {
      quarter,
      type,
      isMultiDocumentRequired,
      hasResultsReport: !!resultsReport,
      hasSecondDocument: !!secondDocument,
      multiDocumentChecks: multiDocumentChecks.map(c => c.id),
      conditionalChecks: conditionalChecks.map(c => c.id),
      auditAnswers: auditAnswers
    });
    
    // Combine base checks with audit checks, multi-document checks, and conditional checks, avoiding duplicates
    const allCheckIds = new Set([
      ...baseChecks.map(c => c.id), 
      ...auditChecks.map(c => c.id),
      ...multiDocumentChecks.map(c => c.id),
      ...conditionalChecks.map(c => c.id)
    ]);
    
    return AVAILABLE_CHECKS.filter(check => allCheckIds.has(check.id));
  };

  // Set default answers for audit questions when type is selected
  React.useEffect(() => {
    if (selectedType) {
      const defaultAnswers: Record<string, string> = {};
      AUDIT_QUESTIONS.forEach(question => {
        if (question.type === 'select') {
          defaultAnswers[question.id] = question.options?.[0] || '';
        } else {
          defaultAnswers[question.id] = 'Yes';
        }
      });
      
      setAuditAnswers(defaultAnswers);
    }
  }, [selectedType]);

  // Update answers when dependencies change
  React.useEffect(() => {
    const filteredQuestions = getFilteredQuestions(AUDIT_QUESTIONS, auditAnswers);
    const currentQuestionIds = filteredQuestions.map(q => q.id);
    
    // Remove answers for questions that are no longer visible
    const updatedAnswers = { ...auditAnswers };
    Object.keys(updatedAnswers).forEach(questionId => {
      if (!currentQuestionIds.includes(questionId)) {
        delete updatedAnswers[questionId];
      }
    });
    
    if (Object.keys(updatedAnswers).length !== Object.keys(auditAnswers).length) {
      setAuditAnswers(updatedAnswers);
    }
  }, [auditAnswers]);

  // Update available checks when quarter, type, audit answers, or documents change
  React.useEffect(() => {
    const filteredChecks = getFilteredChecks(selectedQuarter, selectedType);
    
    // Filter checks based on audit_reviewed answer
    const auditReviewedAnswer = auditAnswers['audit_reviewed'];
    let finalFilteredChecks = filteredChecks;
    
    if (auditReviewedAnswer === 'Audited') {
      // If Audited, remove auditor_review_note from available checks
      finalFilteredChecks = filteredChecks.filter(check => check.id !== 'auditor_review_note');
    } else if (auditReviewedAnswer === 'Reviewed') {
      // If Reviewed, remove independent_auditors_report from available checks
      finalFilteredChecks = filteredChecks.filter(check => check.id !== 'independent_auditors_report');
    }
    
    setAvailableChecks(finalFilteredChecks);
    
    // Filter selected check IDs to only include those still available
    const filteredCheckIds = selectedCheckIds.filter(id => 
      finalFilteredChecks.some(check => check.id === id)
    );
    setSelectedCheckIds(filteredCheckIds);
  }, [selectedQuarter, selectedType, auditAnswers, resultsReport, secondDocument, consolidatedStandaloneDocument]);

  // Track which multi-doc checks have been auto-selected so we don't re-select after user deselects
  const autoSelectedMultiDocRef = useRef<Set<string>>(new Set());

  // Auto-select ALL interlinked multi-document checks when second document is uploaded.
  // This single effect handles all multi-doc checks instead of fragmented individual effects.
  React.useEffect(() => {
    const hasBothDocs = !!resultsReport && !!consolidatedStandaloneDocument;
    const isStandaloneOrConsolidated = selectedType === 'standalone' || selectedType === 'consolidated';

    if (!hasBothDocs || !isStandaloneOrConsolidated) return;

    const hasComponents = auditAnswers['company_has_components'] !== 'No';

    // All interlinked checks that should auto-select when second doc is uploaded
    const interlinkChecks = [
      { id: 'face_value_share', requiresComponents: false },
      { id: 'currency_unit_consistency', requiresComponents: false },
      { id: 'qualification_eom_consistency', requiresComponents: false },
      { id: 'eps_pat_ratio_comparison', requiresComponents: true },
      { id: 'standalone_consolidated_notes_interlink', requiresComponents: true },
    ];

    setSelectedCheckIds(prev => {
      const next = [...prev];
      let changed = false;

      for (const { id, requiresComponents } of interlinkChecks) {
        if (autoSelectedMultiDocRef.current.has(id)) continue;
        if (requiresComponents && !hasComponents) continue;

        const checkDef = AVAILABLE_CHECKS.find(c => c.id === id);
        if (!checkDef || !checkDef.isSelected) continue;
        if (!availableChecks.some(c => c.id === id)) continue;

        if (!next.includes(id)) {
          next.push(id);
          changed = true;
        }
        autoSelectedMultiDocRef.current.add(id);
      }

      return changed ? next : prev;
    });
  }, [availableChecks, consolidatedStandaloneDocument, resultsReport, selectedType, auditAnswers]);

  // Auto-select line items comparison when previous year document is uploaded
  React.useEffect(() => {
    if (!resultsReport || !previousYearDocument) return;
    if (autoSelectedMultiDocRef.current.has('line_items_comparison')) return;

    const checkDef = AVAILABLE_CHECKS.find(c => c.id === 'line_items_comparison');
    if (!checkDef) return;
    if (!availableChecks.some(c => c.id === 'line_items_comparison')) return;

    autoSelectedMultiDocRef.current.add('line_items_comparison');
    setSelectedCheckIds(prev => prev.includes('line_items_comparison') ? prev : [...prev, 'line_items_comparison']);
  }, [availableChecks, previousYearDocument, resultsReport]);

  // Auto-select SRE check based on PKF auditor/co-reviewer pre-review answer
  React.useEffect(() => {
    const pkfAnswer = auditAnswers['pkf_auditor_co_reviewer'];
    if (!pkfAnswer) return;

    setSelectedCheckIds(prev => {
      let next = [...prev];
      if (pkfAnswer === 'Auditor') {
        if (!next.includes('sre_2400_mention')) next.push('sre_2400_mention');
        next = next.filter(id => id !== 'sre_2410_mention');
      } else if (pkfAnswer === 'Co-reviewer') {
        if (!next.includes('sre_2410_mention')) next.push('sre_2410_mention');
        next = next.filter(id => id !== 'sre_2400_mention');
      }
      return next;
    });
  }, [auditAnswers]);

  // Handle file upload
  // Read the file's bytes NOW (while the picked File reference is still fresh) and return
  // an in-memory copy. Storing the raw disk-backed File and reading it minutes later
  // throws "A requested file or directory could not be found" (a stale-handle NotFoundError),
  // which is what broke the analysis. The in-memory copy can always be re-read.
  const materializeFile = async (file: File): Promise<File> => {
    const buf = await file.arrayBuffer();
    return new File([buf], file.name, { type: file.type || 'application/pdf', lastModified: file.lastModified });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];

    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid File Type",
          description: "Please upload a PDF file",
          variant: "destructive",
        });
        input.value = '';
        return;
      }

      if (file.size > 100 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "File size should be less than 100MB",
          variant: "destructive",
        });
        input.value = '';
        return;
      }

      try {
        setResultsReport(await materializeFile(file));
      } catch {
        toast({ title: "Could not read file", description: "Please choose the PDF again.", variant: "destructive" });
        input.value = '';
        return;
      }
      // Initialize selected checks with defaults when file is uploaded
      const initiallyAvailableChecks = getFilteredChecks(selectedQuarter, selectedType);
      const defaultSelectedIds = initiallyAvailableChecks
        .filter(check => check.isSelected)
        .map(check => check.id);
      setSelectedCheckIds(defaultSelectedIds);
    }

    input.value = '';
  };

  // Remove uploaded file
  const removeFile = () => {
    setResultsReport(null);
    setShowCheckSelection(false);
    setSelectedCheckIds([]);
  };

  // Handle check selection
  const handleCheckToggle = (checkId: string, checked: boolean) => {
    if (checked) {
      setSelectedCheckIds(prev => [...prev, checkId]);
    } else {
      setSelectedCheckIds(prev => prev.filter(id => id !== checkId));
    }
  };

  // Handle category toggle
  const handleCategoryToggle = (categoryName: string, checked: boolean) => {
    const categoryChecks = availableChecks.filter(check => check.category === categoryName);
    const categoryCheckIds = categoryChecks.map(check => check.id);
    
    if (checked) {
      setSelectedCheckIds(prev => [...new Set([...prev, ...categoryCheckIds])]);
    } else {
      setSelectedCheckIds(prev => prev.filter(id => !categoryCheckIds.includes(id)));
    }
  };

  // Group checks by category
  const groupChecksByCategory = (checks: CheckOption[]): CheckCategory[] => {
    const categories: Record<string, CheckOption[]> = {};

    checks.forEach(check => {
      if (!categories[check.category]) {
        categories[check.category] = [];
      }
      categories[check.category].push(check);
    });

    // Filter selected IDs to only include those in available checks
    const validSelectedIds = selectedCheckIds.filter(id => 
      checks.some(check => check.id === id)
    );

    return Object.entries(categories).map(([name, checks]) => ({
      name,
      checks,
      totalSelected: checks.filter(c => validSelectedIds.includes(c.id)).length,
      totalAvailable: checks.length
    }));
  };

  // Show check selection
  const handleShowCheckSelection = () => {
    if (!resultsReport) {
      toast({
        title: "No Document",
        description: "Please upload a document first",
        variant: "destructive",
      });
      return;
    }

    // Note: Second document and consolidated/standalone document are now optional
    // Checks will only be available if the corresponding documents are uploaded

    setShowCheckSelection(true);
  };

  // Extract detailed feedback from analysis text
  const extractDetailedFeedback = (analysisText: string, isCompliant: boolean) => {
    // Parse the AI response to extract structured information
    const lines = analysisText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    // Extract reasoning from the analysis text
    let reasoning = "Analysis completed based on document review.";
    
    // Look for reasoning patterns in the text
    const reasoningPatterns = [
      /(?:because|due to|as a result|explain|reason|why)[^.!?]*(?:[.!?]|$)/gi,
      /(?:analysis shows|review indicates|document reveals)[^.!?]*(?:[.!?]|$)/gi,
      /(?:the document|this is|it is)[^.!?]*(?:[.!?]|$)/gi
    ];
    
    for (const pattern of reasoningPatterns) {
      const matches = analysisText.match(pattern);
      if (matches && matches.length > 0) {
        reasoning = matches[0].trim();
        break;
      }
    }
    
    // Extract evidence - look for specific findings, quotes, or references
    const evidence: string[] = [];
    
    // Clean up the analysis text first to remove jumbled formatting
    const cleanText = analysisText
      .replace(/\*\*[^*]*\*\*/g, '') // Remove markdown bold
      .replace(/\*[^*]*\*/g, '') // Remove markdown italic
      .replace(/- \*\*[^*]*\*\*: [^.]*\./g, '') // Remove structured format lines
      .replace(/\*\*[^*]*\*\*: [^.]*\./g, '') // Remove other structured format
      .replace(/\[[^\]]*\]/g, '') // Remove brackets
      .replace(/\*\*Number of Times it Appears:\*\* \d+/g, '') // Remove repetition info
      .replace(/\*\*Type of Error:\*\* [^.]*\./g, '') // Remove error type info
      .replace(/\*\*Example Location:\*\* [^.]*\./g, '') // Remove location info
      .replace(/\*\*Incorrect Spelling:\*\* [^.]*\./g, '') // Remove spelling info
      .replace(/\*\*Suggested Correction:\*\* [^.]*\./g, '') // Remove correction info
      .replace(/\*\*Specific Recommendations for Correction:\*\* \d+\./g, '') // Remove rec numbers
      .replace(/\*\*[^*]*\*\*: [^.]*\./g, '') // Remove any remaining structured format
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
    
    // Look for quoted text or specific values in the cleaned text
    const quotedMatches = cleanText.match(/["'][^"']*["']/g);
    if (quotedMatches) {
      // Filter out very long or jumbled quotes
      const cleanQuotes = quotedMatches
        .map(q => q.replace(/["']/g, '').trim())
        .filter(q => q.length > 1 && q.length < 50 && !q.includes('**') && !q.includes('Number of Times'))
        .slice(0, 5);
      evidence.push(...cleanQuotes);
    }
    
    // Look for specific technical terms and values
    const technicalMatches = cleanText.match(/(?:rs\.?|₹|inr|audit|board|committee|eps|pbt|tax|revenue|profit|period ended|for the period|statement of|balance sheet|cash flow|page \d+|march|june|september|december)[^.!?]*(?:[.!?]|$)/gi);
    if (technicalMatches) {
      const cleanTechnical = technicalMatches
        .map(m => m.trim())
        .filter(m => m.length < 100 && !m.includes('**') && !m.includes('Number of Times'))
        .slice(0, 5);
      evidence.push(...cleanTechnical);
    }
    
    // Look for specific numbers, dates, or percentages
    const numberMatches = cleanText.match(/(?:\d+[.,]?\d*[%]?|june|july|august|september|october|november|december|january|february|march|april|may)[^.!?]*(?:[.!?]|$)/gi);
    if (numberMatches) {
      const cleanNumbers = numberMatches
        .map(m => m.trim())
        .filter(m => m.length < 50 && !m.includes('**') && !m.includes('Number of Times'))
        .slice(0, 3);
      evidence.push(...cleanNumbers);
    }
    
    // Extract recommendations from the AI response
    const recommendations: string[] = [];
    
    // Look for recommendation patterns in the cleaned text
    const recommendationPatterns = [
      /(?:recommend|suggest|should|must|need to|required to)[^.!?]*(?:[.!?]|$)/gi,
      /(?:improvement|enhancement|better|fix|correct)[^.!?]*(?:[.!?]|$)/gi
    ];
    
    for (const pattern of recommendationPatterns) {
      const matches = cleanText.match(pattern);
      if (matches && matches.length > 0) {
        const cleanRecs = matches
          .map(m => m.trim())
          .filter(m => m.length < 200 && !m.includes('**') && !m.includes('Number of Times'))
          .slice(0, 3);
        recommendations.push(...cleanRecs);
      }
    }
    
    // If no specific recommendations found, generate based on compliance status
    if (recommendations.length === 0) {
      if (!isCompliant) {
        if (analysisText.toLowerCase().includes("missing") || analysisText.toLowerCase().includes("not found") || analysisText.toLowerCase().includes("absent")) {
          recommendations.push("Ensure all required disclosures are included in the document");
        }
        if (analysisText.toLowerCase().includes("inconsistent") || analysisText.toLowerCase().includes("mismatch") || analysisText.toLowerCase().includes("differ")) {
          recommendations.push("Review and align all related figures and statements for consistency");
        }
        if (analysisText.toLowerCase().includes("format") || analysisText.toLowerCase().includes("structure") || analysisText.toLowerCase().includes("layout")) {
          recommendations.push("Follow the standard format requirements for financial reporting");
        }
        if (analysisText.toLowerCase().includes("date") || analysisText.toLowerCase().includes("timeline") || analysisText.toLowerCase().includes("period")) {
          recommendations.push("Verify all dates and timelines are accurate and properly sequenced");
        }
        if (analysisText.toLowerCase().includes("currency") || analysisText.toLowerCase().includes("unit") || analysisText.toLowerCase().includes("denomination")) {
          recommendations.push("Ensure consistent currency and unit labeling across all statements");
        }
        if (analysisText.toLowerCase().includes("audit") || analysisText.toLowerCase().includes("review") || analysisText.toLowerCase().includes("opinion")) {
          recommendations.push("Verify audit report format and content meet regulatory requirements");
        }
      } else {
        recommendations.push("Continue maintaining current compliance standards");
        if (analysisText.toLowerCase().includes("good") || analysisText.toLowerCase().includes("proper") || analysisText.toLowerCase().includes("correct") || analysisText.toLowerCase().includes("adequate")) {
          recommendations.push("Document serves as a good example for future reporting");
        }
        if (analysisText.toLowerCase().includes("consistent") || analysisText.toLowerCase().includes("aligned")) {
          recommendations.push("Maintain consistency across all future reports");
        }
      }
    }
    
    return {
      reasoning: reasoning,
      evidence: evidence.slice(0, 5), // Limit to 5 pieces of evidence
      recommendations: recommendations.slice(0, 3) // Limit to 3 recommendations
    };
  };

  // Test Firebase connectivity
  const testFirebaseConnection = async () => {
    try {
      console.log('🔍 Firebase Test: Testing Firebase connection...');
      
      if (!currentUser || !currentUser.uid) {
        throw new Error('User not authenticated');
      }

      // Test by trying to fetch analysis history
      const testHistory = await getAnalysisHistory(currentUser.uid);
      console.log('✅ Firebase Test: Connection successful. Found', testHistory.length, 'existing analyses');
      
      return true;
    } catch (error) {
      console.error('❌ Firebase Test: Connection failed:', error);
      return false;
    }
  };

  // Handle analysis start - shows confirmation dialog first
  const handleStartAnalysis = async () => {
    if (!currentUser) {
      toast({
        title: "Authentication Required",
        description: "Please log in to perform analysis",
        variant: "destructive",
      });
      return;
    }

    if (!selectedQuarter) {
      toast({
        title: "Quarter Not Selected",
        description: "Please select a quarter (Q1, Q2, Q3, or Q4) to proceed",
        variant: "destructive",
      });
      return;
    }

    if (!selectedType) {
      toast({
        title: "Type Not Selected",
        description: "Please select either Consolidated or Standalone results type to proceed",
        variant: "destructive",
      });
      return;
    }

    // Filter selected IDs to only include those in available checks
    const validSelectedIds = selectedCheckIds.filter(id => 
      availableChecks.some(check => check.id === id)
    );

    if (validSelectedIds.length === 0) {
      toast({
        title: "No Checks Selected",
        description: "Please select at least one check to run",
        variant: "destructive",
      });
      return;
    }

    // Validate that all user questions are answered
    console.log('Validation check:', { questionsValid, userAnswersLength: userAnswers.length, userAnswers });
    if (!questionsValid) {
      toast({
        title: "Incomplete Questions",
        description: `All ${USER_QUESTIONS.length} Auditor Declared Questions must be answered before proceeding`,
        variant: "destructive",
      });
      return;
    }

    // Show masked PDF confirmation dialog
    setMaskedConfirmationText("");
    setShowMaskedPdfConfirmation(true);
  };

  // Actually start the analysis after confirmation
  const proceedWithAnalysis = async () => {
    // Close the dialog
    setShowMaskedPdfConfirmation(false);
    setMaskedConfirmationText("");

    // Test Firebase connection before starting analysis
    console.log('🔍 Analysis Start: Testing Firebase connection...');
    const firebaseConnected = await testFirebaseConnection();
    
    if (!firebaseConnected) {
      toast({
        title: "Database Connection Error",
        description: "Cannot connect to database. Please check your internet connection and try again.",
        variant: "destructive",
      });
      return;
    }
    
    console.log('✅ Analysis Start: Firebase connection verified');

    // Filter selected IDs to only include those in available checks
    const validSelectedIds = selectedCheckIds.filter(id => 
      availableChecks.some(check => check.id === id)
    );

    // SRE checks are conditionally available via getFilteredChecks and auto-selected via useEffect.
    // Do NOT force-add them here — respect the user's selection.
    const pkfAnswer = auditAnswers['pkf_auditor_co_reviewer'];
    let checksToRun = [...validSelectedIds];
    
    // Ensure the wrong SRE check is removed if user somehow has both selected
    if (pkfAnswer === 'Auditor') {
      checksToRun = checksToRun.filter(id => id !== 'sre_2410_mention');
    } else if (pkfAnswer === 'Co-reviewer') {
      checksToRun = checksToRun.filter(id => id !== 'sre_2400_mention');
    }

    // Filter checks based on audit_reviewed answer
    const auditReviewedAnswer = auditAnswers['audit_reviewed'];
    if (auditReviewedAnswer === 'Audited') {
      // If Audited, remove auditor_review_note check
      checksToRun = checksToRun.filter(id => id !== 'auditor_review_note');
      console.log('Removed auditor_review_note check because results are Audited');
    } else if (auditReviewedAnswer === 'Reviewed') {
      // If Reviewed, remove independent_auditors_report check
      checksToRun = checksToRun.filter(id => id !== 'independent_auditors_report');
      console.log('Removed independent_auditors_report check because results are Reviewed');
    }

    setIsProcessing(true);
    setIsCancelled(false);
    setAnalysisComplete(false);
    setAnalysisData(null);
    cancellationRef.current = new AbortController();
    setProcessingProgress({
      completed: 0,
      total: checksToRun.length,
      currentCheck: "Initializing analysis...",
      phase: 'processing'
    });

    toast({
      title: "Starting Analysis",
      description: `Processing ${checksToRun.length} checks with individual model settings...`,
    });

    try {
      console.log('Starting analysis with user answers:', userAnswers);
      console.log('Checks to run:', checksToRun);
      const results: AnalysisResult['results'] = {};
      let completed = 0;

      // Helper to infer compliance from text content if API flag seems wrong
      const inferComplianceFromText = (text?: string): boolean | null => {
        if (!text) return null;
        const t = text.toLowerCase();
        const firstLine = t.split('\n')[0]?.trim() || '';
        // If the model is explicitly returning a "Check"/"Warning" style status, don't infer compliance.
        // ("check" may also appear as a verb in explanations, so only treat it as a status when it is the first line)
        if (firstLine === 'check' || firstLine.startsWith('check ') || firstLine.startsWith('warning')) return null;
        // explicit non-compliance markers first
        if (/(^|\W)(non[-\s]?compliant|not\s+compliant|non\s+compliance|❌)/.test(t)) return false;
        if (/(^|\W)(compliant|✅)/.test(t)) return true;
        return null;
      };

      for (const checkId of checksToRun) {
        // Check for cancellation before each check
        if (cancellationRef.current?.signal.aborted || isCancelled) {
          console.log('Analysis cancelled by user');
          break;
        }

        // Skip subsidiary_word_highlighting if document type is not consolidated
        if (checkId === 'subsidiary_word_highlighting' && selectedType !== 'consolidated') {
          console.log(`Skipping ${checkId} - only runs for consolidated documents`);
          continue;
        }

        setProcessingProgress(prev => ({
          ...prev,
          currentCheck: `Running ${checkId.replace(/_/g, ' ')}...`
        }));

        try {
          // Find the check configuration to get model and temperature
          const checkConfig = AVAILABLE_CHECKS.find(check => check.id === checkId);
          const basePrompt = CHECK_PROMPTS[checkId] || CHECK_PROMPTS['results_verification'];
          const prompt = applyResultsAnalyzerStatusGuidance(basePrompt);
          
          // For multi-document checks, we need to process both documents together
          const isMultiDocumentCheck = ['q2_balance_verification', 'q4_balance_verification', 'face_value_share', 'currency_unit_consistency', 'eps_pat_ratio_comparison', 'standalone_consolidated_notes_interlink', 'line_items_comparison', 'qualification_eom_consistency'].includes(checkId);
          
          let result;
            if (checkId === 'q2_balance_verification' || checkId === 'q4_balance_verification') {
            // For balance verification checks, use custom logic with internal extraction
            if (secondDocument) {
              console.log(`Calling processBalanceVerification with:`);
              console.log(`- Current Document (resultsReport): ${resultsReport?.name}`);
              console.log(`- Previous Document (secondDocument): ${secondDocument?.name}`);
              result = await processBalanceVerification(
            resultsReport!,
                secondDocument,
                checkId,
                checkConfig?.model || 'gemini-2.5-pro',
                checkConfig?.temperature || 0.1,
                cancellationRef.current?.signal
              );
            } else {
              result = {
                compliant: false,
                details: 'Previous year document not uploaded. Balance verification requires both current and previous year documents.',
                confidence: 0,
                feedback: {
                  reasoning: 'Balance verification check requires previous year document for comparison.',
                  evidence: [],
                  recommendations: ['Please upload the previous year document to perform balance verification.']
                }
              };
            }
          } else if (checkId === 'face_value_share') {
            // For face value comparison checks
            if (consolidatedStandaloneDocument) {
              console.log(`Calling processFaceValueComparison with:`);
              console.log(`- Main Document (resultsReport): ${resultsReport?.name}`);
              console.log(`- Comparison Document (consolidatedStandaloneDocument): ${consolidatedStandaloneDocument?.name}`);
              result = await processFaceValueComparison(
                resultsReport!,
                consolidatedStandaloneDocument,
                checkId,
                checkConfig?.model || 'gemini-2.5-pro',
                checkConfig?.temperature || 0.1
              );
            } else {
              result = {
                isCompliant: false,
                explanation: 'Consolidated/Standalone document not uploaded. Face value comparison requires both main and consolidated/standalone documents.',
                confidence: 0,
                feedback: {
                  reasoning: 'Face value comparison check requires consolidated/standalone document for comparison.',
                  evidence: [],
                  recommendations: ['Please upload the consolidated/standalone document to perform face value comparison.']
                }
              };
            }
          } else if (checkId === 'currency_unit_consistency') {
            // For currency unit consistency checks
            if (consolidatedStandaloneDocument) {
              console.log(`Calling processCurrencyUnitComparison with:`);
              console.log(`- Main Document (resultsReport): ${resultsReport?.name}`);
              console.log(`- Comparison Document (consolidatedStandaloneDocument): ${consolidatedStandaloneDocument?.name}`);
              result = await processCurrencyUnitComparison(
                resultsReport!,
                consolidatedStandaloneDocument,
                checkId,
                checkConfig?.model || 'gemini-2.5-pro',
                checkConfig?.temperature || 0.1,
                cancellationRef.current?.signal
              );
            } else {
              result = {
                isCompliant: false,
                explanation: 'Consolidated/Standalone document not uploaded. Currency unit consistency check requires both main and consolidated/standalone documents.',
                confidence: 0,
                feedback: {
                  reasoning: 'Currency unit consistency check requires consolidated/standalone document for comparison.',
                  evidence: [],
                  recommendations: ['Please upload the consolidated/standalone document to perform currency unit consistency check.']
                }
              };
            }
          } else if (checkId === 'standalone_consolidated_notes_interlink') {
            // For standalone vs consolidated notes interlink checks
            if (consolidatedStandaloneDocument) {
              console.log(`Calling processStandaloneConsolidatedNotesComparison with:`);
              console.log(`- Main Document (resultsReport): ${resultsReport?.name}`);
              console.log(`- Comparison Document (consolidatedStandaloneDocument): ${consolidatedStandaloneDocument?.name}`);
              result = await processStandaloneConsolidatedNotesComparison(
                resultsReport!,
                consolidatedStandaloneDocument,
                checkId,
                checkConfig?.model || 'gemini-2.5-pro',
                checkConfig?.temperature || 0.1
              );
            } else {
              result = {
                isCompliant: false,
                explanation: 'Consolidated/Standalone document not uploaded. Standalone vs Consolidated notes interlink check requires both main and consolidated/standalone documents.',
                confidence: 0,
                feedback: {
                  reasoning: 'Standalone vs Consolidated notes interlink check requires consolidated/standalone document for comparison.',
                  evidence: [],
                  recommendations: ['Please upload the consolidated/standalone document to perform notes interlink comparison.']
                }
              };
            }
          } else if (checkId === 'cce_tallying_check') {
            // For CCE tallying checks (single document)
            console.log(`Calling processCCETallyingCheck with:`);
            console.log(`- Document (resultsReport): ${resultsReport?.name}`);
            result = await processCCETallyingCheck(
              resultsReport!,
              checkId,
              checkConfig?.model || 'gemini-2.5-pro',
              checkConfig?.temperature || 0.1,
              cancellationRef.current?.signal
            );
          } else if (checkId === 'balance_sheet_tallying_check') {
            // For balance sheet tallying checks
            console.log(`Calling processBalanceSheetTallyingCheck with:`);
            console.log(`- Document (resultsReport): ${resultsReport?.name}`);
            result = await processBalanceSheetTallyingCheck(
              resultsReport!,
              checkId,
              checkConfig?.model || 'gemini-2.5-pro',
              checkConfig?.temperature || 0.1,
              cancellationRef.current?.signal
            );
          } else if (checkId === 'cashflow_net_change_check') {
            // For cash flow net change checks
            console.log(`Calling processCashFlowNetChangeCheck with:`);
            console.log(`- Document (resultsReport): ${resultsReport?.name}`);
            result = await processCashFlowNetChangeCheck(
              resultsReport!,
              checkId,
              checkConfig?.model || 'gemini-2.5-pro',
              checkConfig?.temperature || 0.1,
              cancellationRef.current?.signal
            );
          } else if (checkId === 'eps_pat_ratio_comparison') {
            // For EPS-to-PAT ratio comparison checks - Three-step approach
            if (consolidatedStandaloneDocument) {
              console.log(`Processing EPS-to-PAT ratio comparison with three-step approach:`);
              console.log(`- Main Document (resultsReport): ${resultsReport?.name}`);
              console.log(`- Comparison Document (consolidatedStandaloneDocument): ${consolidatedStandaloneDocument?.name}`);
              
              try {
                // Step 1: Extract data from standalone document
                console.log('Step 1: Extracting data from standalone document...');
                const standaloneResult = await processCustomDocumentCheck(
                  resultsReport!,
                  'eps_pat_standalone_extraction',
                  CHECK_PROMPTS['eps_pat_standalone_extraction'],
                  checkConfig?.model || 'gemini-2.5-pro',
                  checkConfig?.temperature || 0.1,
                  cancellationRef.current?.signal
                );

                // Step 2: Extract data from consolidated document
                console.log('Step 2: Extracting data from consolidated document...');
                const consolidatedResult = await processCustomDocumentCheck(
                  consolidatedStandaloneDocument,
                  'eps_pat_consolidated_extraction',
                  CHECK_PROMPTS['eps_pat_consolidated_extraction'],
                  checkConfig?.model || 'gemini-2.5-pro',
                  checkConfig?.temperature || 0.1,
                  cancellationRef.current?.signal
                );

                // Step 3: Compare and classify the extracted data
                console.log('Step 3: Comparing and classifying extracted data...');
                const comparisonPrompt = `${applyResultsAnalyzerStatusGuidance(CHECK_PROMPTS['eps_pat_ratio_classification'])}

STANDALONE DATA EXTRACTION RESULT:
${standaloneResult.explanation}

CONSOLIDATED DATA EXTRACTION RESULT:
${consolidatedResult.explanation}`;

                const comparisonResult = await processCustomDocumentCheck(
                  resultsReport!, // Use main document as base for comparison
                  'eps_pat_ratio_classification',
                  comparisonPrompt,
                  checkConfig?.model || 'gemini-2.5-pro',
                  checkConfig?.temperature || 0.1,
                  cancellationRef.current?.signal
                );

                // Parse compliance status from comparison result - should return "Check" or "Compliant"
                const responseText = comparisonResult.explanation || '';
                const firstLine = responseText.split('\n')[0]?.toLowerCase().trim() || '';
                const isCheck = firstLine === 'check' || firstLine.includes('check');
                const isCompliant = !isCheck && firstLine.includes('compliant') && !firstLine.includes('non-compliant');
                
                // EPS-to-PAT ratio comparison: only Compliant or Check (never Non-Compliant)
                result = {
                  isCompliant: isCompliant,
                  isWarning: isCheck || !isCompliant,
                  explanation: comparisonResult.explanation,
                  confidence: comparisonResult.confidence || 0.8,
                  feedback: {
                    reasoning: `Three-step analysis completed. Standalone data: ${standaloneResult.explanation.split('\n')[0] || 'Extracted'}, Consolidated data: ${consolidatedResult.explanation.split('\n')[0] || 'Extracted'}, Comparison: ${comparisonResult.explanation.split('\n')[0] || 'Completed'}`,
                    evidence: [
                      `Standalone extraction: ${standaloneResult.explanation.split('\n')[1] || 'Data found'}`,
                      `Consolidated extraction: ${consolidatedResult.explanation.split('\n')[1] || 'Data found'}`,
                      `Comparison result: ${comparisonResult.explanation.split('\n')[0] || 'Analysis completed'}`
                    ],
                    recommendations: ['Review EPS-to-PAT ratio comparison between standalone and consolidated results for any discrepancies.']
                  }
                };
              } catch (error) {
                console.error('Error in three-step EPS-to-PAT ratio comparison:', error);
                result = {
                  isCompliant: false,
                  explanation: 'Error occurred during three-step analysis. Please try again.',
                  confidence: 0,
                  feedback: {
                    reasoning: 'Three-step analysis failed due to processing error.',
                    evidence: [],
                    recommendations: ['Please ensure both documents are properly formatted and try again.']
                  }
                };
              }
            } else {
              result = {
                isCompliant: false,
                explanation: 'Consolidated/Standalone document not uploaded. EPS-to-PAT ratio comparison requires both standalone and consolidated documents.',
                confidence: 0,
                feedback: {
                  reasoning: 'EPS-to-PAT ratio comparison check requires consolidated/standalone document for comparison.',
                  evidence: [],
                  recommendations: ['Please upload the consolidated/standalone document to perform EPS-to-PAT ratio comparison.']
                }
              };
            }
          } else if (checkId === 'qualification_eom_consistency') {
            // For Qualification/EOM Consistency - Three-step approach (like EPS PAT ratio)
            if (consolidatedStandaloneDocument) {
              console.log(`Processing Qualification/EOM Consistency with three-step approach:`);
              console.log(`- Main Document (resultsReport/standalone): ${resultsReport?.name}`);
              console.log(`- Comparison Document (consolidated): ${consolidatedStandaloneDocument?.name}`);
              
              try {
                // Step 1: Extract qualification/EOM data from standalone audit report
                console.log('Step 1: Extracting qualification/EOM data from standalone audit report...');
                const standaloneResult = await processCustomDocumentCheck(
                  resultsReport!,
                  'qualification_eom_standalone_extraction',
                  CHECK_PROMPTS['qualification_eom_standalone_extraction'],
                  checkConfig?.model || 'gemini-2.5-pro',
                  checkConfig?.temperature || 0.3,
                  cancellationRef.current?.signal
                );

                // Step 2: Extract qualification/EOM data from consolidated audit report
                console.log('Step 2: Extracting qualification/EOM data from consolidated audit report...');
                const consolidatedResult = await processCustomDocumentCheck(
                  consolidatedStandaloneDocument,
                  'qualification_eom_consolidated_extraction',
                  CHECK_PROMPTS['qualification_eom_consolidated_extraction'],
                  checkConfig?.model || 'gemini-2.5-pro',
                  checkConfig?.temperature || 0.3,
                  cancellationRef.current?.signal
                );

                // Step 3: Compare the extracted data
                console.log('Step 3: Comparing qualification/EOM data between standalone and consolidated...');
                const standaloneData = standaloneResult.detail || standaloneResult.explanation || '';
                const consolidatedData = consolidatedResult.detail || consolidatedResult.explanation || '';
                const comparisonPrompt = `${applyResultsAnalyzerStatusGuidance(CHECK_PROMPTS['qualification_eom_comparison'])}

STANDALONE AUDIT REPORT — QUALIFICATION/EOM EXTRACTION (from Step 1):
${standaloneData}

CONSOLIDATED AUDIT REPORT — QUALIFICATION/EOM EXTRACTION (from Step 2):
${consolidatedData}

Using the above extracted data from BOTH audit reports, perform the consistency comparison as instructed. Do NOT claim any document is missing — both have been extracted and provided above.`;

                const comparisonResult = await processCustomDocumentCheck(
                  resultsReport!,
                  'qualification_eom_comparison',
                  comparisonPrompt,
                  checkConfig?.model || 'gemini-2.5-pro',
                  checkConfig?.temperature || 0.3,
                  cancellationRef.current?.signal
                );

                const responseText = comparisonResult.detail || comparisonResult.explanation || '';
                const firstLine = responseText.split('\n')[0]?.toLowerCase().trim() || '';
                const isCheck = firstLine === 'check' || firstLine.includes('check');
                const isCompliant = !isCheck && firstLine.includes('compliant') && !firstLine.includes('non-compliant');

                const combinedDetails = `
STANDALONE AUDIT REPORT — QUALIFICATION/EOM FINDINGS:
${standaloneData}

CONSOLIDATED AUDIT REPORT — QUALIFICATION/EOM FINDINGS:
${consolidatedData}

QUALIFICATION/EOM CONSISTENCY COMPARISON RESULT:
${responseText}
                `.trim();

                result = {
                  isCompliant: isCompliant,
                  isWarning: isCheck || !isCompliant,
                  detail: combinedDetails,
                  explanation: combinedDetails,
                  confidence: comparisonResult.confidence || 0.8,
                  feedback: {
                    reasoning: `Three-step qualification/EOM consistency analysis. Standalone report: ${resultsReport!.name}, Consolidated report: ${consolidatedStandaloneDocument.name}.`,
                    evidence: [
                      `Standalone extraction: ${(standaloneData).split('\n')[0] || 'Extracted'}`,
                      `Consolidated extraction: ${(consolidatedData).split('\n')[0] || 'Extracted'}`,
                      `Comparison result: ${responseText.split('\n')[0] || 'Completed'}`
                    ],
                    recommendations: isCompliant 
                      ? ['All standalone qualifications/EOMs are consistently reflected in the consolidated report.']
                      : ['Review flagged inconsistencies between standalone and consolidated audit report qualifications/EOMs.']
                  }
                };
              } catch (error) {
                console.error('Error in three-step Qualification/EOM consistency check:', error);
                result = {
                  isCompliant: false,
                  isWarning: true,
                  explanation: `Error processing Qualification/EOM consistency check: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  detail: `Error processing Qualification/EOM consistency check: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  confidence: 0,
                  feedback: {
                    reasoning: 'Three-step analysis failed due to processing error.',
                    evidence: [],
                    recommendations: ['Please ensure both documents are properly formatted and try again.']
                  }
                };
              }
            } else {
              result = {
                isCompliant: false,
                isWarning: true,
                explanation: 'Consolidated/Standalone document not uploaded. Qualification/EOM consistency check requires both standalone and consolidated documents.',
                detail: 'Consolidated/Standalone document not uploaded. Qualification/EOM consistency check requires both standalone and consolidated documents.',
                confidence: 0,
                feedback: {
                  reasoning: 'Qualification/EOM consistency check requires consolidated/standalone document for comparison.',
                  evidence: [],
                  recommendations: ['Please upload the consolidated/standalone document to perform Qualification/EOM consistency comparison.']
                }
              };
            }
          } else if (checkId === 'line_items_comparison') {
            // For line items comparison checks - Three-step approach
            if (previousYearDocument) {
              console.log(`Processing line items comparison with three-step approach:`);
              console.log(`- Main Document (resultsReport): ${resultsReport?.name}`);
              console.log(`- Previous Year Document (previousYearDocument): ${previousYearDocument?.name}`);
              
              try {
                // Step 1: Extract line items from previous year end document
                console.log('Step 1: Extracting line items from previous year end document...');
                const previousYearResult = await processCustomDocumentCheck(
                  previousYearDocument,
                  'line_items_previous_year_extraction',
                  CHECK_PROMPTS['line_items_previous_year_extraction'],
                  checkConfig?.model || 'gemini-2.5-pro',
                  checkConfig?.temperature || 0.1,
                  cancellationRef.current?.signal
                );

                // Step 2: Extract line items from current quarter document
                console.log('Step 2: Extracting line items from current quarter document...');
                const currentQuarterResult = await processCustomDocumentCheck(
                  resultsReport!,
                  'line_items_current_quarter_extraction',
                  CHECK_PROMPTS['line_items_current_quarter_extraction'],
                  checkConfig?.model || 'gemini-2.5-pro',
                  checkConfig?.temperature || 0.1,
                  cancellationRef.current?.signal
                );

                // Step 3: Compare the extracted line items
                console.log('Step 3: Comparing line items between documents...');
                const comparisonPrompt = `${applyResultsAnalyzerStatusGuidance(CHECK_PROMPTS['line_items_comparison'])}

PREVIOUS YEAR END LINE ITEMS EXTRACTION RESULT:
${previousYearResult.detail || previousYearResult.explanation}

CURRENT QUARTER LINE ITEMS EXTRACTION RESULT:
${currentQuarterResult.detail || currentQuarterResult.explanation}

Using the above extracted line items from BOTH documents, perform the comparison as instructed. Do NOT claim any document is missing — both have been extracted and provided above.`;

                const comparisonResult = await processCustomDocumentCheck(
                  resultsReport!,
                  'line_items_comparison',
                  comparisonPrompt,
                  checkConfig?.model || 'gemini-2.5-pro',
                  checkConfig?.temperature || 0.1,
                  cancellationRef.current?.signal
                );

                const comparisonText = comparisonResult.detail || comparisonResult.explanation || '';
                const firstLine = comparisonText.split('\n')[0]?.toLowerCase().trim() || '';
                const isCheck = firstLine === 'check' || firstLine.includes('check');
                const isCompliant = !isCheck && firstLine.includes('compliant') && !firstLine.includes('non-compliant');

                const combinedDetails = `
PREVIOUS YEAR END LINE ITEMS:
${previousYearResult.detail || previousYearResult.explanation}

CURRENT QUARTER LINE ITEMS:
${currentQuarterResult.detail || currentQuarterResult.explanation}

LINE ITEMS COMPARISON RESULT:
${comparisonText}
                `.trim();

                result = {
                  isCompliant: isCompliant,
                  isWarning: isCheck || !isCompliant,
                  detail: combinedDetails,
                  explanation: combinedDetails,
                  confidence: comparisonResult.confidence,
                  model_used: checkConfig?.model || 'gemini-2.5-pro',
                  feedback: {
                    reasoning: `Line items comparison between previous year (${previousYearDocument.name}) and current quarter (${resultsReport!.name}).`,
                    evidence: [
                      `Previous Year line items extracted: ${(previousYearResult.explanation || '').split('\n')[0] || 'Extracted'}`,
                      `Current Quarter line items extracted: ${(currentQuarterResult.explanation || '').split('\n')[0] || 'Extracted'}`,
                      `Comparison result: ${comparisonText.split('\n')[0] || 'Completed'}`
                    ],
                    recommendations: isCompliant 
                      ? ['All previous year line items are present in the current quarter results.']
                      : ['Review flagged line item differences between previous year and current quarter results.']
                  }
                };
              } catch (error) {
                console.error('Error in line items comparison:', error);
                result = {
                  isCompliant: false,
                  explanation: `Error processing line items comparison: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  detail: `Error processing line items comparison: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  confidence: 0,
                  model_used: checkConfig?.model || 'gemini-2.5-pro',
                  feedback: {
                    reasoning: 'Error occurred during processing',
                    evidence: [],
                    recommendations: ['Please check the uploaded documents and try again.']
                  }
                };
              }
            } else {
              result = {
                isCompliant: false,
                explanation: 'Previous Year End document not uploaded for line items comparison',
                detail: 'Previous Year End document not uploaded for line items comparison',
                confidence: 0,
                model_used: checkConfig?.model || 'gemini-2.5-pro',
                feedback: {
                  reasoning: 'Multi-document check requires both main document and previous year end document',
                  evidence: [],
                  recommendations: ['Please upload the previous year end document to perform line items comparison.']
                }
              };
            }
          } else if (isMultiDocumentCheck && secondDocument) {
              // For other multi-document checks, use the multi-document processor
              result = await processMultiDocumentCheck(
                resultsReport!,
                secondDocument,
            checkId,
            prompt,
            checkConfig?.model || 'gemini-2.5-pro',
            checkConfig?.temperature || 0.1,
            cancellationRef.current?.signal
          );
          } else if (checkId === 'company_name_consistency' || checkId === 'company_word_highlighting') {
            // Special handling for company checks and spelling check - always set as warning
            console.log(`Processing ${checkId} check with warning status handling`);
            console.log(`Using model: ${checkConfig?.model || 'gemini-2.5-pro'}, temperature: ${checkConfig?.temperature || 0.1}`);
            console.log(`Document being processed: ${resultsReport?.name} (${resultsReport?.size} bytes)`);
            
            // Skip company name checks as they are not needed for current checklist
            let enhancedPrompt = prompt;
            
            result = await processCustomDocumentCheck(
              resultsReport!,
              checkId,
              enhancedPrompt,
              checkConfig?.model || 'gemini-2.5-pro',
              checkConfig?.temperature || 0.1,
              cancellationRef.current?.signal
            );
            
            // Always set as warning for these checks
            result.isCompliant = false;
            result.isWarning = true;
            
            console.log(`Result for ${checkId}:`, result);
          } else if (checkId === 'cfo_pat_depreciation_ratio') {
            // Special handling for CFO check - needs to handle warning status properly
            console.log(`Processing CFO vs PAT+Depreciation ratio check`);
            console.log(`Using model: ${checkConfig?.model || 'gemini-2.5-pro'}, temperature: ${checkConfig?.temperature || 0.1}`);
            console.log(`Document being processed: ${resultsReport?.name} (${resultsReport?.size} bytes)`);
            
            result = await processCustomDocumentCheck(
              resultsReport!,
              checkId,
              prompt,
              checkConfig?.model || 'gemini-2.5-pro',
              checkConfig?.temperature || 0.1,
              cancellationRef.current?.signal
            );
            
            // Check if the result contains a warning message
            const responseText = result.explanation || result.detail || '';
            if (responseText.toLowerCase().includes('warning: cfo < 50%')) {
              result.isCompliant = false;
              result.isWarning = true;
            } else if (responseText.toLowerCase().includes('compliant')) {
              result.isCompliant = true;
              result.isWarning = false;
            }
            
            console.log(`Result for ${checkId}:`, result);
          } else {
            // For single document checks, process normally
            console.log(`Processing single document check: ${checkId}`);
            console.log(`Using model: ${checkConfig?.model || 'gemini-2.5-pro'}, temperature: ${checkConfig?.temperature || 0.1}`);
            console.log(`Document being processed: ${resultsReport?.name} (${resultsReport?.size} bytes)`);
            console.log(`Prompt preview: ${prompt.substring(0, 200)}...`);
            
            result = await processCustomDocumentCheck(
              resultsReport!,
              checkId,
              prompt,
              checkConfig?.model || 'gemini-2.5-pro',
              checkConfig?.temperature || 0.1,
              cancellationRef.current?.signal
            );
            
            console.log(`Result for ${checkId}:`, result);
          }

          let detailsText = result.detail || result.explanation || result.details || "Analysis completed";
          let compliantFlag = result.isCompliant !== undefined ? result.isCompliant : result.compliant;
          // Only use heuristic inference as a FALLBACK when the AI didn't set a clear status.
          // Never override an already-determined isCompliant value, as the inference scans
          // the full text and can be tripped by mentions like "no non-compliant items found".
          if (compliantFlag === undefined || compliantFlag === null) {
            if (!result.isWarning) {
              const inferred = inferComplianceFromText(detailsText);
              if (inferred !== null) {
                compliantFlag = inferred;
              } else {
                compliantFlag = false;
              }
            } else {
              compliantFlag = false;
            }
          }

          // Only enhance if the response is truly empty or very short
          if (!detailsText || detailsText.trim().length < 20) {
            const checkName = checkId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            detailsText = `Analysis for ${checkName}: ${detailsText || 'No detailed analysis available.'}`;
          }

          // Extract detailed feedback from the analysis
          // Use existing feedback if available (for multi-document checks), otherwise extract from text
          const feedback = result.feedback || extractDetailedFeedback(detailsText, compliantFlag);

          // Get checklist metadata for reference
          const checklistMetadata = checkConfig ? {
            name: checkConfig.name,
            description: checkConfig.description,
            category: checkConfig.category,
            estimatedTime: checkConfig.estimatedTime
          } : null;

          results[checkId] = {
            compliant: compliantFlag,
            isWarning: result.isWarning || false,
            details: detailsText,
            confidence: result.confidence || 0.8,
            model_used: checkConfig?.model || 'gemini-2.5-pro',
            checklistMetadata, // Add checklist metadata for reference
            feedback: feedback
          };

          completed++;
          
          // Check for cancellation after each check
          if (cancellationRef.current?.signal.aborted || isCancelled) {
            console.log('Analysis cancelled by user after check completion');
            break;
          }
          
          setProcessingProgress(prev => ({
            ...prev,
            completed
          }));

        } catch (error) {
          console.error(`Error processing ${checkId}:`, error);
          const checkConfig = AVAILABLE_CHECKS.find(check => check.id === checkId);
          // Get checklist metadata for reference
          const checklistMetadata = checkConfig ? {
            name: checkConfig.name,
            description: checkConfig.description,
            category: checkConfig.category,
            estimatedTime: checkConfig.estimatedTime
          } : null;

          results[checkId] = {
            compliant: false,
            details: `Error processing check: ${error instanceof Error ? error.message : String(error)}`,
            confidence: 0,
            model_used: checkConfig?.model || 'gemini-2.5-pro',
            checklistMetadata, // Add checklist metadata for reference
            feedback: {
              reasoning: 'An error occurred during processing',
              evidence: [],
              recommendations: ['Please try again or contact support if the issue persists']
            }
          };
          completed++;
          
          // Check for cancellation after error handling
          if (cancellationRef.current?.signal.aborted || isCancelled) {
            console.log('Analysis cancelled by user after error handling');
            break;
          }
        }
      }

      // Re-evaluate compliance statuses by reading the analysis text
      try {
        setProcessingProgress(prev => ({
          ...prev,
          currentCheck: "Re-evaluating compliance statuses from analysis text...",
          phase: 'validation'
        }));

        const correctedStatuses = await reEvaluateComplianceStatuses(
          results,
          cancellationRef.current?.signal
        );

        const checkOnlyIds = ['eps_pat_ratio_comparison', 'line_items_comparison', 'standalone_consolidated_notes_interlink', 'subsidiary_word_highlighting', 'castings_check_horizontal', 'basic_logic_issues', 'tax_pbt_ratio_highlighting', 'profit_loss_quarter_ytd_consistency', 'qualification_eom_consistency'];
        
        let correctedCount = 0;
        for (const [checkId, newStatus] of Object.entries(correctedStatuses)) {
          if (results[checkId]) {
            const oldCompliant = results[checkId].compliant;
            const oldWarning = results[checkId].isWarning;
            
            // Force NON-COMPLIANT → CHECK for checks that should never be non-compliant
            const effectiveStatus = (newStatus === 'NON-COMPLIANT' && checkOnlyIds.includes(checkId)) ? 'CHECK' : newStatus;

            if (effectiveStatus === 'COMPLIANT') {
              results[checkId].compliant = true;
              results[checkId].isWarning = false;
              results[checkId].isDataUnavailable = false;
            } else if (effectiveStatus === 'NON-COMPLIANT') {
              results[checkId].compliant = false;
              results[checkId].isWarning = false;
              results[checkId].isDataUnavailable = false;
            } else if (effectiveStatus === 'CHECK') {
              results[checkId].compliant = false;
              results[checkId].isWarning = true;
              results[checkId].isDataUnavailable = false;
            } else if (newStatus === 'DATA_UNAVAILABLE') {
              results[checkId].compliant = false;
              results[checkId].isWarning = false;
              results[checkId].isDataUnavailable = true;
            }

            if (results[checkId].compliant !== oldCompliant || results[checkId].isWarning !== oldWarning || results[checkId].isDataUnavailable) {
              correctedCount++;
              console.log(`Status corrected for ${checkId}: ${oldWarning ? 'CHECK' : oldCompliant ? 'COMPLIANT' : 'NON-COMPLIANT'} → ${newStatus}`);
            }
          }
        }

        console.log(`Re-evaluation complete: ${correctedCount} statuses corrected out of ${Object.keys(correctedStatuses).length} evaluated`);
      } catch (reEvalError) {
        console.error('Error during status re-evaluation (continuing with original statuses):', reEvalError);
      }

      // Add user questions as additional check results
      const userQuestionResults: Record<string, any> = {};
      if (userAnswers && userAnswers.length > 0) {
        userAnswers.forEach((answer, index) => {
          const question = USER_QUESTIONS.find(q => q.id === answer.questionId);
          if (question) {
            // Convert user answers to compliance results
            // Yes = Compliant, No = Non-Compliant, N/A = Not counted (since all must be answered)
            const isCompliant = answer.answer === 'yes';
            
            userQuestionResults[`user_question_${answer.questionId}`] = {
              compliant: answer.answer === 'yes', // Keep for compatibility but don't use for display
              isUserQuestion: true, // Flag to identify user questions
              userAnswer: answer.answer, // Store the actual user answer
              details: `User Answer: ${answer.answer.toUpperCase()}${answer.note ? ` - ${answer.note}` : ''}`,
              confidence: 1.0, // High confidence since all questions are mandatory
              model_used: 'user_input',
              feedback: {
                reasoning: `User declared: ${answer.answer.toUpperCase()}`,
                evidence: answer.note ? [answer.note] : [],
                recommendations: answer.answer === 'no' ? ['Review and address this concern'] : []
              }
            };
          }
        });
      }

      // Combine AI results with user question results
      const allResults = { ...results, ...userQuestionResults };
      
      console.log('🔍 User Questions Debug:');
      console.log('- User answers count:', userAnswers?.length || 0);
      console.log('- User answers data:', userAnswers);
      console.log('- User question results count:', Object.keys(userQuestionResults).length);
      console.log('- User question results keys:', Object.keys(userQuestionResults));
      console.log('- Total results count:', Object.keys(allResults).length);
      console.log('- Total results keys:', Object.keys(allResults));

      // Validate final compliance status using AI
      let validatedComplianceStatus;
      try {
        setProcessingProgress(prev => ({
          ...prev,
          currentCheck: "Validating final compliance status...",
          phase: 'validation'
        }));

        validatedComplianceStatus = await validateFinalComplianceStatus(
          allResults,
          cancellationRef.current?.signal
        );
        console.log('✅ Final compliance validation complete:', validatedComplianceStatus);
      } catch (validationError) {
        console.error('❌ Error during compliance validation:', validationError);
        // Continue with original calculation if validation fails
        validatedComplianceStatus = {
          isValidatedCompliant: false,
          validationExplanation: `Validation error: ${validationError instanceof Error ? validationError.message : 'Unknown error'}. Using calculated status.`
        };
      }

      // Separate AI checks from user questions
      const aiResults = Object.entries(allResults).filter(([_, result]) => !result.isUserQuestion);
      const userQuestionResultsEntries = Object.entries(allResults).filter(([_, result]) => result.isUserQuestion);
      
      // Calculate AI check summary
      const aiTotal = aiResults.length;
      // Check status is for specific checks or isWarning flag
      const checkStatusIds = ['eps_pat_ratio_comparison', 'subsidiary_word_highlighting', 'castings_check_horizontal', 'basic_logic_issues', 'tax_pbt_ratio_highlighting', 'standalone_consolidated_notes_interlink', 'profit_loss_quarter_ytd_consistency', 'line_items_comparison', 'qualification_eom_consistency'];
      
      // Data Unavailable items
      const aiDataUnavailable = aiResults.filter(([_, r]) => r.isDataUnavailable).length;

      // Checks with "Check" status (excluding data unavailable)
      const aiCheck = aiResults.filter(([checkId, r]) => 
        !r.isDataUnavailable && (
          r.isWarning || 
          (!r.compliant && (checkStatusIds.includes(checkId) || checkStatusIds.includes(r.checklistMetadata?.id || '')))
        )
      );
      
      // Count compliant (excluding data unavailable)
      const aiCompliant = aiResults.filter(([_, r]) => 
        r.compliant && !r.isWarning && !r.isDataUnavailable
      ).length;
      
      // Count non-compliant (excluding "Check" and "Data Unavailable")
      const aiNonCompliant = aiResults.filter(([checkId, r]) => 
        !r.compliant && !r.isWarning && !r.isDataUnavailable &&
        !(checkStatusIds.includes(checkId) || checkStatusIds.includes(r.checklistMetadata?.id || ''))
      ).length;
      
      const aiCheckCount = aiCheck.length;
      
      // Validation: verify counts sum to total
      const countSum = aiCompliant + aiNonCompliant + aiCheckCount + aiDataUnavailable;
      if (countSum !== aiTotal) {
        console.error(`⚠️ COUNT MISMATCH: Compliant(${aiCompliant}) + NonCompliant(${aiNonCompliant}) + Check(${aiCheckCount}) + DataUnavailable(${aiDataUnavailable}) = ${countSum}, but Total = ${aiTotal}`);
        // Log items that don't fall into any bucket for debugging
        const unaccounted = aiResults.filter(([checkId, r]) => {
          const inDataUnavailable = !!r.isDataUnavailable;
          const inCheck = !r.isDataUnavailable && (r.isWarning || (!r.compliant && (checkStatusIds.includes(checkId) || checkStatusIds.includes(r.checklistMetadata?.id || ''))));
          const inCompliant = r.compliant && !r.isWarning && !r.isDataUnavailable;
          const inNonCompliant = !r.compliant && !r.isWarning && !r.isDataUnavailable && !(checkStatusIds.includes(checkId) || checkStatusIds.includes(r.checklistMetadata?.id || ''));
          return !inDataUnavailable && !inCheck && !inCompliant && !inNonCompliant;
        });
        console.error('Unaccounted items:', unaccounted.map(([id, r]) => ({ id, compliant: r.compliant, isWarning: r.isWarning, isDataUnavailable: r.isDataUnavailable })));
      } else {
        console.log(`✅ Count validation: Compliant(${aiCompliant}) + NonCompliant(${aiNonCompliant}) + Check(${aiCheckCount}) + DataUnavailable(${aiDataUnavailable}) = ${countSum} = Total(${aiTotal})`);
      }
      
      // Compliance rate: compliant + check + data unavailable out of total
      const aiCompliancePercentage = aiTotal > 0 ? Math.round(((aiCompliant + aiCheckCount + aiDataUnavailable) / aiTotal) * 100) : 0;
      
      // Calculate user input summary
      const userYes = userQuestionResultsEntries.filter(([_, r]) => r.userAnswer === 'yes').length;
      const userNo = userQuestionResultsEntries.filter(([_, r]) => r.userAnswer === 'no').length;
      const userNA = userQuestionResultsEntries.filter(([_, r]) => r.userAnswer === 'na' || r.userAnswer === 'n/a').length;

      const summary = {
        total: aiTotal, // Only AI checks count
        compliant: aiCompliant,
        nonCompliant: aiNonCompliant,
        check: aiCheckCount,
        dataUnavailable: aiDataUnavailable,
        compliancePercentage: aiCompliancePercentage,
        userInput: {
          yes: userYes,
          no: userNo,
          na: userNA,
          total: userQuestionResults.length
        },
        isPartial: isCancelled && completed < checksToRun.length,
        completedChecks: completed,
        totalSelectedChecks: checksToRun.length,
        validatedComplianceStatus
      };

      // Create analysis data
      const analysisId = `results_analysis_${Date.now()}`;
      const selectedTypeForParams: ResultsAnalysisParameters['selectedType'] | undefined =
        selectedType === '' ? undefined : selectedType;
      const analysisData: AnalysisResult = {
        id: analysisId,
        timestamp: Date.now(),
        parameters: { ...parameters, selectedQuarter, selectedType: selectedTypeForParams },
        document: resultsReport!,
        results: allResults, // Use combined results including user questions
        summary,
        userAnswers
      };

      // Save to Firebase - create compatible parameters for saveAnalysisResults
      try {
        // Validate user authentication before saving
        if (!currentUser || !currentUser.uid) {
          throw new Error("User not authenticated. Cannot save analysis results.");
        }

        console.log('🔍 Firebase Save: Starting save process...');
        console.log('🔍 Firebase Save: Current user:', { uid: currentUser.uid, email: currentUser.email });

        const compatibleParameters = {
          company_name: `Results Analysis - ${selectedType?.charAt(0).toUpperCase() + selectedType?.slice(1)} - Q${selectedQuarter}`,
          audit_date: new Date(),
          profit_or_loss: "Profit",
          company_listing_status: "Unlisted",
          top_1000_or_500: "No",
          audit_report_type: selectedType === 'consolidated' ? 'Consolidated' : selectedType === 'standalone' ? 'Standalone' : 'Normal',
          audit_opinion_type: "Unmodified",
          is_nbfc: "No",
          has_internal_auditor: "No",
          has_cost_auditor: "No",
          related_party_note_number: ""
        };

        // Map to storage schema expected by backend (Record<string, CheckResult>)
        const resultsForStorage: Record<string, CheckResult> = Object.fromEntries(
          Object.entries(allResults).map(([checkId, r]) => {
            const category = checkId.startsWith('user_question_') 
              ? 'User Declared Questions' 
              : AVAILABLE_CHECKS.find(c => c.id === checkId)?.category || 'Unknown';
            
            // Create the result object and only include userAnswer if it's defined
            const result: any = {
                isCompliant: r.compliant,
                explanation: r.details,
                confidence: r.confidence,
                detail: `Model: ${r.model_used}`,
                source: category,
                // User question specific fields
                isUserQuestion: r.isUserQuestion || checkId.startsWith('user_question_'),
                // Checklist metadata for reference
                checklistName: r.checklistMetadata?.name,
                checklistDescription: r.checklistMetadata?.description,
                checklistCategory: r.checklistMetadata?.category,
                estimatedTime: r.checklistMetadata?.estimatedTime
            };

            // Only add userAnswer if it's defined and not undefined
            if (r.userAnswer !== undefined && r.userAnswer !== null) {
              result.userAnswer = r.userAnswer;
            }

            return [checkId, result as CheckResult];
          })
        );

        console.log('🔍 Firebase Save: Parameters prepared:', compatibleParameters);
        console.log('🔍 Firebase Save: Results count:', Object.keys(resultsForStorage).length);
        console.log('🔍 Firebase Save: User answers count:', userAnswers?.length || 0);
        console.log('🔍 Firebase Save: Summary:', summary);
        
        // NOTE: Saving is now deferred until company name is confirmed in the dialog
        // Generate a temporary ID for the analysis
        const tempAnalysisId = `results_analysis_${Date.now()}`;
        analysisData.id = tempAnalysisId;
        
        console.log('🔍 Analysis complete - waiting for company name confirmation before saving');

        // Invalidate the analysis history cache to refresh the History page
        console.log('🔄 Firebase Save: Invalidating history cache...');
        await queryClient.invalidateQueries({
          queryKey: ["analysisHistory", currentUser.uid]
        });

        console.log('✅ Firebase Save: Cache invalidated successfully');

        toast({
          title: "Analysis Complete",
          description: `${aiTotal} AI checks completed with ${aiCompliancePercentage}% compliance rate. Analysis saved to history.`,
        });
      } catch (saveError) {
        console.error("❌ Firebase Save: Failed to save to Firebase:", saveError);
        
        // Show detailed error message
        const errorMessage = saveError instanceof Error ? saveError.message : String(saveError);
        console.error("❌ Firebase Save: Error details:", errorMessage);
        
        toast({
          title: "Analysis Complete (Save Error)",
          description: `Analysis completed but could not save to database: ${errorMessage}`,
          variant: "destructive",
        });
      }

      if (!isCancelled) {
        setAnalysisData(analysisData);
        setAnalysisComplete(true);
        setShowCheckSelection(false);
      } else {
        // If cancelled, check if we have partial results to show
        const hasPartialResults = completed > 0;
        if (hasPartialResults) {
          // Show partial results
          setAnalysisData(analysisData);
          setAnalysisComplete(true);
          setShowCheckSelection(false);
          
          toast({
            title: "Partial Results Available",
            description: `Showing results for ${completed} completed checks out of ${checksToRun.length} total checks.`,
            variant: "default",
          });
        } else {
          // No results to show, just hide check selection
          setShowCheckSelection(false);
        }
      }

    } catch (error) {
      console.error("Error during analysis:", error);
      toast({
        title: "Analysis Failed",
        description: `An error occurred: ${error instanceof Error ? error.message : String(error)}`,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Stop the current analysis
  const handleStopAnalysis = () => {
    if (cancellationRef.current) {
      cancellationRef.current.abort();
    }
    setIsCancelled(true);
    setIsProcessing(false);
    setProcessingProgress(prev => ({
      ...prev,
      currentCheck: "Analysis cancelled by user",
      phase: 'cancelled'
    }));
    
    // Check if we have any completed results to show
    const hasPartialResults = processingProgress.completed > 0;
    
    if (hasPartialResults) {
      toast({
        title: "Analysis Stopped",
        description: `Analysis stopped with ${processingProgress.completed} of ${processingProgress.total} checks completed. Partial results will be shown.`,
        variant: "default",
      });
    } else {
      toast({
        title: "Analysis Cancelled",
        description: "The analysis has been stopped successfully",
        variant: "default",
      });
    }
  };

  // Start a new analysis
  const handleStartNewAnalysis = () => {
    setAnalysisComplete(false);
    setAnalysisData(null);
    setIsProcessing(false);
    setIsCancelled(false);
    setResultsReport(null);
    setShowCheckSelection(false);
    setSelectedCheckIds([]);
    cancellationRef.current = null;
  };

  // Go back to form from check selection
  const handleGoBackToForm = () => {
    setShowCheckSelection(false);
  };

  // Collapse/expand functions
  const toggleCardExpansion = (checkId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(checkId)) {
        newSet.delete(checkId);
      } else {
        newSet.add(checkId);
      }
      return newSet;
    });
  };

  const expandAllCards = () => {
    if (analysisData) {
      const allCheckIds = Object.keys(analysisData.results);
      setExpandedCards(new Set(allCheckIds));
      setAllExpanded(true);
    }
  };

  const collapseAllCards = () => {
    setExpandedCards(new Set());
    setAllExpanded(false);
  };

  // Download results as HTML
  const downloadResultsAsHTML = () => {
    if (!analysisData) return;

    const htmlContent = generateHTMLReport(analysisData);
    
    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `results_analysis_${format(new Date(analysisData.timestamp), 'yyyy-MM-dd_HH-mm-ss')}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Download Started",
      description: "Results report has been downloaded as HTML file.",
    });
  };

  // Handle second document upload
  const handleSecondFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];

    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid File Type",
          description: "Please upload a PDF file",
          variant: "destructive",
        });
        input.value = '';
        return;
      }

      if (file.size > 100 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "File size should be less than 100MB",
          variant: "destructive",
        });
        input.value = '';
        return;
      }

      try {
        setSecondDocument(await materializeFile(file));
      } catch {
        toast({ title: "Could not read file", description: "Please choose the PDF again.", variant: "destructive" });
      }
    }

    input.value = '';
  };

  // Remove second document
  const removeSecondFile = () => {
    setSecondDocument(null);
  };

  // Handle consolidated/standalone document upload
  const handleConsolidatedStandaloneFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];

    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid File Type",
          description: "Please upload a PDF file",
          variant: "destructive",
        });
        input.value = '';
        return;
      }

      if (file.size > 100 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "File size should be less than 100MB",
          variant: "destructive",
        });
        input.value = '';
        return;
      }

      try {
        setConsolidatedStandaloneDocument(await materializeFile(file));
      } catch {
        toast({ title: "Could not read file", description: "Please choose the PDF again.", variant: "destructive" });
        input.value = '';
        return;
      }
      toast({
        title: "Document Uploaded",
        description: `${selectedType === 'consolidated' ? 'Consolidated' : 'Standalone'} document uploaded successfully.`,
      });
    }

    input.value = '';
  };

  // Remove consolidated/standalone document
  const removeConsolidatedStandaloneFile = () => {
    setConsolidatedStandaloneDocument(null);
  };

  // Handle previous year document upload
  const handlePreviousYearFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];

    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid File Type",
          description: "Please upload a PDF file",
          variant: "destructive",
        });
        input.value = '';
        return;
      }

      if (file.size > 100 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "File size should be less than 100MB",
          variant: "destructive",
        });
        input.value = '';
        return;
      }

      try {
        setPreviousYearDocument(await materializeFile(file));
      } catch {
        toast({ title: "Could not read file", description: "Please choose the PDF again.", variant: "destructive" });
        input.value = '';
        return;
      }
      toast({
        title: "Document Uploaded",
        description: "Previous year document uploaded successfully.",
      });
    }

    input.value = '';
  };

  // Remove previous year document
  const removePreviousYearFile = () => {
    setPreviousYearDocument(null);
  };

  // Check if multi-document upload is required
  const isMultiDocumentRequired = selectedQuarter === 'Q2' || selectedQuarter === 'Q4';

  // File upload component
  const FileUploader = () => {
    return (
      <div className="space-y-4">
        {/* First Document */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
            {isMultiDocumentRequired ? 'Current Quarter Report PDF *' : 'Results Report PDF *'}
        </Label>

        {resultsReport ? (
          <div className="border-2 border-green-300 bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-green-700">{resultsReport.name}</p>
                  <p className="text-xs text-green-600">
                    {(resultsReport.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={removeFile}
                disabled={isProcessing}
                className="text-red-500 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              className="hidden"
              id="results-report-file"
              disabled={isProcessing}
            />
            <label htmlFor="results-report-file" className="cursor-pointer">
              <div className="space-y-2">
                <Upload className="h-8 w-8 mx-auto text-gray-400" />
                  <p className="text-sm text-gray-600">
                    Click to upload {isMultiDocumentRequired ? 'Current Quarter Report PDF' : 'Results Report PDF'}
                  </p>
                <p className="text-xs text-gray-400">Maximum 100MB</p>
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-amber-800">
                        <p className="font-medium mb-2">⚠️ Upload Guidelines:</p>
                        <div className="space-y-1">
                          <p className="text-red-700 font-medium">❌ Don't: Do Not upload with the Company name in the draft financials</p>
                          <p className="text-green-700 font-medium">✅ Do: Upload financial results after removing company name</p>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
            </label>
            </div>
          )}
        </div>

        {/* Second Document - Only show for Q2 and Q4 */}
        {isMultiDocumentRequired && (
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Previous Year Q4/Annual Report PDF <span className="text-gray-500">(Optional)</span>
            </Label>

            {secondDocument ? (
              <div className="border-2 border-green-300 bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-8 w-8 text-green-500" />
                    <div>
                      <p className="text-sm font-medium text-green-700">{secondDocument.name}</p>
                      <p className="text-xs text-green-600">
                        {(secondDocument.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeSecondFile}
                    disabled={isProcessing}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleSecondFileUpload}
                  className="hidden"
                  id="second-document-file"
                  disabled={isProcessing}
                />
                <label htmlFor="second-document-file" className="cursor-pointer">
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-gray-400" />
                    <p className="text-sm text-gray-600">Click to upload Previous Year Q4/Annual Report PDF</p>
                    <p className="text-xs text-gray-400">Maximum 100MB</p>
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <p className="font-medium">⚠️ Upload financial results after removing company name</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Consolidated/Standalone Document - Only show for consolidated or standalone types */}
        {(selectedType === 'consolidated' || selectedType === 'standalone') && (
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              {selectedType === 'consolidated' ? 'Standalone' : 'Consolidated'} Document PDF <span className="text-gray-500">(Optional)</span>
            </Label>

            {consolidatedStandaloneDocument ? (
              <div className="border-2 border-green-300 bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">{consolidatedStandaloneDocument.name}</p>
                      <p className="text-sm text-green-600">
                        {(consolidatedStandaloneDocument.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={removeConsolidatedStandaloneFile}
                    className="text-red-600 hover:text-red-700"
                    disabled={isProcessing}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleConsolidatedStandaloneFileUpload}
                  className="hidden"
                  id="consolidated-standalone-document-file"
                  disabled={isProcessing}
                />
                <label htmlFor="consolidated-standalone-document-file" className="cursor-pointer">
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-gray-400" />
                    <p className="text-sm text-gray-600">
                      Click to upload {selectedType === 'consolidated' ? 'Standalone' : 'Consolidated'} Document PDF
                    </p>
                    <p className="text-xs text-gray-400">Maximum 100MB</p>
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <p className="font-medium">⚠️ Upload financial results after removing company name</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Previous Year End Document - Show for all quarters */}
        {(selectedQuarter === 'Q1' || selectedQuarter === 'Q2' || selectedQuarter === 'Q3' || selectedQuarter === 'Q4') && (
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Previous Year End Document PDF <span className="text-red-500">*</span>
            </Label>

            {previousYearDocument ? (
              <div className="border-2 border-green-300 bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-8 w-8 text-green-500" />
                    <div>
                      <p className="text-sm font-medium text-green-700">{previousYearDocument.name}</p>
                      <p className="text-xs text-green-600">
                        {(previousYearDocument.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removePreviousYearFile}
                    disabled={isProcessing}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePreviousYearFileUpload}
                  className="hidden"
                  id="previous-year-document-file"
                  disabled={isProcessing}
                />
                <label htmlFor="previous-year-document-file" className="cursor-pointer">
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-gray-400" />
                    <p className="text-sm text-gray-600">Click to upload Previous Year End Document PDF</p>
                    <p className="text-xs text-gray-400">Maximum 100MB</p>
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <p className="font-medium">⚠️ Upload financial results after removing company name</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Multi-document info */}
        {(isMultiDocumentRequired || (selectedType === 'consolidated' || selectedType === 'standalone') || (selectedQuarter === 'Q1' || selectedQuarter === 'Q2' || selectedQuarter === 'Q3' || selectedQuarter === 'Q4')) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Multi-Document Analysis Available</p>
                <div className="text-xs mt-1 space-y-1">
                  {isMultiDocumentRequired && (
                    <p>For {selectedQuarter} analysis, upload the previous year Q4/Annual report to enable interlinked balance verification checks.</p>
                  )}
                  {(selectedType === 'consolidated' || selectedType === 'standalone') && (
                    <p>Upload the {selectedType === 'consolidated' ? 'standalone' : 'consolidated'} document to enable face value comparison and notes interlink checks.</p>
                  )}
                  {(selectedQuarter === 'Q1' || selectedQuarter === 'Q2' || selectedQuarter === 'Q3' || selectedQuarter === 'Q4') && (
                    <p>Upload the Previous Year End document to enable line items comparison between Previous Year End and Current Quarter Statements of Results.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Check selection component
  const CheckSelection = () => {
    const categories = groupChecksByCategory(availableChecks);
    // Ensure selected count never exceeds available count
    const validSelectedIds = selectedCheckIds.filter(id => 
      availableChecks.some(check => check.id === id)
    );
    const totalSelected = validSelectedIds.length;
    const estimatedTimeSeconds = availableChecks
      .filter(check => validSelectedIds.includes(check.id))
      .reduce((acc, check) => acc + parseInt(check.estimatedTime), 0);

    return (
      <div className="space-y-6">
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <Settings className="h-5 w-5" />
              📋 Select Analysis Checks to Run
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{totalSelected}</div>
                <div className="text-sm text-gray-600">Checks Selected</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{availableChecks.length}</div>
                <div className="text-sm text-gray-600">Total Available</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 flex items-center justify-center gap-1">
                  <Clock className="h-5 w-5" />
                  {Math.ceil(estimatedTimeSeconds / 60)}m
                </div>
                <div className="text-sm text-gray-600">Est. Time</div>
              </div>
            </div>

            <Alert className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>💡 Tip:</strong> Essential checks are pre-selected. You can customize by selecting/deselecting specific checks or entire categories.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Filtering Status Card */}
        {(selectedQuarter || selectedType) && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filtering Status
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-blue-900">Active Filters</p>
                    <p className="text-sm text-blue-700">
                      {selectedQuarter && selectedType 
                        ? `${selectedQuarter} + ${selectedType}` 
                        : selectedQuarter || selectedType}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">{availableChecks.length}</p>
                    <p className="text-xs text-blue-600">Matching Checks</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-700">Quarter Checks</p>
                    <p className="text-gray-600">
                      {selectedQuarter ? `${QUARTER_CHECKS[selectedQuarter]?.length || 0} available` : 'Not selected'}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-700">Type Checks</p>
                    <p className="text-gray-600">
                      {selectedType ? `${TYPE_CHECKS[selectedType]?.length || 0} available` : 'Not selected'}
                    </p>
                  </div>
                </div>
                
                {selectedQuarter && selectedType ? (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>Combined Filtering:</strong> Showing {availableChecks.length} checks that match both {selectedQuarter} and {selectedType} criteria
                    </p>
                  </div>
                ) : selectedQuarter ? (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Quarter Filtering:</strong> Showing {availableChecks.length} {selectedQuarter}-specific checks. Select a type to further refine.
                    </p>
                  </div>
                ) : selectedType ? (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>Type Filtering:</strong> Showing {availableChecks.length} {selectedType}-specific checks. Select a quarter to further refine.
                    </p>
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        )}

        {categories.map(category => (
          <Card key={category.name}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    checked={category.totalSelected === category.totalAvailable}
                    onCheckedChange={(checked) => handleCategoryToggle(category.name, !!checked)}
                    disabled={isProcessing}
                  />
                  <CardTitle className="text-lg">{category.name}</CardTitle>
                  <Badge variant={category.totalSelected > 0 ? "default" : "outline"}>
                    {category.totalSelected}/{category.totalAvailable}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {category.checks.map(check => (
                  <div key={check.id} className={cn(
                    "flex items-start space-x-3 p-3 border rounded-lg transition-colors",
                    selectedCheckIds.includes(check.id) ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50"
                  )}>
                    <Checkbox
                      checked={selectedCheckIds.includes(check.id)}
                      onCheckedChange={(checked) => handleCheckToggle(check.id, !!checked)}
                      disabled={isProcessing}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">{check.name}</h4>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {check.estimatedTime}s
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{check.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        <div className="flex justify-between space-x-4">
          <Button
            variant="outline"
            onClick={handleGoBackToForm}
            disabled={isProcessing}
          >
            ← Back to Form
          </Button>

          <Button
            onClick={handleStartAnalysis}
            disabled={isProcessing || totalSelected === 0 || !selectedQuarter || !selectedType || 
                     (selectedCheckIds.includes('line_items_comparison') && !previousYearDocument)}
            size="lg"
            className="flex-1 md:flex-none"
          >
            {isProcessing ? (
              <span className="flex items-center">
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Running {totalSelected} Checks...
              </span>
            ) : (
              `🚀 Run ${totalSelected} Selected Checks (≈${Math.ceil(estimatedTimeSeconds / 60)}m)`
            )}
          </Button>
          
          {/* Show message when line items comparison is selected but no previous year document */}
          {selectedCheckIds.includes('line_items_comparison') && !previousYearDocument && (
            <div className="mt-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                <span>Please upload the Previous Year End Document to run the Line Items Comparison Check.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Format progress display
  const getProgressPercentage = () => {
    if (processingProgress.total === 0) return 0;
    return Math.round((processingProgress.completed / processingProgress.total) * 100);
  };

  const formatStructuredText = (text: string) => {
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (!text) return { __html: '' };

    const inline = (t: string): string => {
      let h = esc(t);
      h = h.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');
      h = h.replace(/__(.+?)__/g, '<span class="underline decoration-2 underline-offset-2">$1</span>');
      h = h.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
      h = h.replace(/`(.+?)`/g, '<code class="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>');
      h = h.replace(/\bNon-Compliant\b/g, '<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">Non-Compliant</span>');
      h = h.replace(/(?<![-\w])Compliant\b/g, '<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">Compliant</span>');
      return h;
    };

    const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l.trim());
    const isTableSep = (l: string) => /^\s*\|?(\s*:?-+:?\s*\|)+\s*$/.test(l.trim());
    const lines = text.split(/\r?\n/);
    const chunks: string[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i], tr = line.trim();

      if (/^-{3,}$|^\*{3,}$|^_{3,}$/.test(tr)) {
        chunks.push('<hr class="my-3 border-gray-200" />');
        i++; continue;
      }

      const hm = tr.match(/^(#{1,4})\s+(.+)$/);
      if (hm) {
        const lvl = hm[1].length;
        const cls = [
          'text-base font-bold text-gray-900 mt-4 mb-2 pb-1 border-b border-gray-200',
          'text-sm font-bold text-gray-800 mt-3 mb-1.5',
          'text-sm font-semibold text-gray-700 mt-2 mb-1',
          'text-xs font-semibold text-gray-600 mt-2 mb-1 uppercase tracking-wide'
        ][lvl - 1];
        chunks.push(`<div class="${cls}">${inline(hm[2])}</div>`);
        i++; continue;
      }

      if (isTableRow(line)) {
        const tl: string[] = [];
        while (i < lines.length && (isTableRow(lines[i]) || isTableSep(lines[i]) || !lines[i].trim())) { if (lines[i].trim()) tl.push(lines[i]); i++; }
        let hp = false, thead = '', tbody = '', ri = 0;
        for (const row of tl) {
          if (isTableSep(row)) continue;
          const cells = row.trim().replace(/^\|/,'').replace(/\|$/,'').split('|').map(c => inline(c.trim()));
          if (!hp) {
            thead = `<thead><tr class="bg-slate-50">${cells.map(c=>`<th class="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider border-b-2 border-slate-200">${c}</th>`).join('')}</tr></thead>`;
            hp = true;
          } else {
            tbody += `<tr class="${ri%2?'bg-slate-50/50':''} hover:bg-blue-50/30 transition-colors">${cells.map(c=>`<td class="px-3 py-2 text-sm text-slate-700 border-b border-slate-100">${c}</td>`).join('')}</tr>`;
            ri++;
          }
        }
        chunks.push(`<div class="overflow-x-auto my-3 rounded-lg border border-slate-200 shadow-sm"><table class="w-full text-sm">${thead}<tbody>${tbody}</tbody></table></div>`);
        continue;
      }

      if (/^\s*[-*]\s+/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/,'')); i++; }
        chunks.push(`<ul class="my-2 ml-1 space-y-1">${items.map(it=>`<li class="flex items-start gap-2 text-sm text-gray-700"><span class="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-400 flex-shrink-0"></span><span>${inline(it)}</span></li>`).join('')}</ul>`);
        continue;
      }

      if (/^\s*\d+[.)]\s+/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+[.)]\s+/,'')); i++; }
        chunks.push(`<ol class="my-2 ml-1 space-y-1">${items.map((it,idx)=>`<li class="flex items-start gap-2.5 text-sm text-gray-700"><span class="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center mt-0.5">${idx+1}</span><span>${inline(it)}</span></li>`).join('')}</ol>`);
        continue;
      }

      i++;
      if (tr) chunks.push(`<p class="my-1.5 text-sm leading-relaxed text-gray-700">${inline(tr)}</p>`);
    }
    return { __html: chunks.join('') };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardTopbar />
      
      {/* Masked PDF Confirmation Dialog - Always available */}
      <Dialog open={showMaskedPdfConfirmation} onOpenChange={setShowMaskedPdfConfirmation}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Confirm Masked PDF Upload
            </DialogTitle>
            <DialogDescription>
              Please confirm that you have uploaded <strong>ONLY masked PDF files</strong> for this analysis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Important:</strong> This analysis should only be performed on PDF files that have been masked using the PDF Masker tool to hide sensitive information.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label htmlFor="masked-confirmation">
                Type <strong>"MASKED"</strong> to confirm:
              </Label>
              <Input
                id="masked-confirmation"
                placeholder="Type MASKED here"
                value={maskedConfirmationText}
                onChange={(e) => setMaskedConfirmationText(e.target.value)}
                className="uppercase"
                autoFocus
              />
              <p className="text-xs text-gray-500">
                You must type "MASKED" exactly to proceed with the analysis.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowMaskedPdfConfirmation(false);
                setMaskedConfirmationText("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={proceedWithAnalysis}
              disabled={maskedConfirmationText.trim().toUpperCase() !== "MASKED"}
              className="bg-primary"
            >
              Confirm and Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="pt-20 px-4 sm:px-6 lg:px-8">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Results Audit Analyzer</h1>
          <p className="text-gray-600 mt-1">AI-powered analysis of results reports with individual model settings</p>
        </div>
        {analysisComplete && (
          <Button variant="outline" onClick={handleStartNewAnalysis}>
            Start New Analysis
          </Button>
        )}
      </div>

      {!analysisComplete ? (
        <>
          {showCheckSelection ? (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <CheckSelection />
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">Results Report Analysis</h2>
                <p className="text-gray-600">
                  Upload your results report PDF and configure analysis parameters.
                </p>
              </div>

              {/* Quarter Selection */}
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Quarter Selection</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="quarter-select" className="text-sm font-medium">
                        Select the Quarter for Analysis
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">
                        Choose the quarter to determine which checkpoints are applicable for your analysis.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter) => (
                        <Button
                          key={quarter}
                          variant={selectedQuarter === quarter ? "default" : "outline"}
                          onClick={() => {
                            setSelectedQuarter(quarter);
                          }}
                          className="h-12 text-lg font-semibold"
                        >
                          {quarter}
                        </Button>
                      ))}
                    </div>

                    {selectedQuarter && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Selected:</strong> {selectedQuarter} 
                          {selectedType ? ` + ${selectedType}` : ''} - {getFilteredChecks(selectedQuarter, selectedType).length} checkpoints available
                        </p>
                        {selectedType ? (
                          <p className="text-xs text-blue-600 mt-1">
                            Combined filtering: Showing checkpoints that match both {selectedQuarter} and {selectedType} criteria
                          </p>
                        ) : (
                          <p className="text-xs text-blue-600 mt-1">
                            Showing {selectedQuarter}-specific checkpoints. Select a type to further refine the selection.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Type Selection */}
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Type Selection</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="type-select" className="text-sm font-medium">
                        Select the Type of Results
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">
                        Choose whether you are analyzing Consolidated or Standalone financial results.
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <Button
                        variant={selectedType === 'consolidated' ? "default" : "outline"}
                        onClick={() => setSelectedType('consolidated')}
                        className="h-12 text-lg font-semibold"
                      >
                        Consolidated
                      </Button>
                      <Button
                        variant={selectedType === 'standalone' ? "default" : "outline"}
                        onClick={() => setSelectedType('standalone')}
                        className="h-12 text-lg font-semibold"
                      >
                        Standalone
                      </Button>
                      <Button
                        variant={selectedType === 'Normal' ? "default" : "outline"}
                        onClick={() => setSelectedType('Normal')}
                        className="h-12 text-lg font-semibold"
                      >
                        Company Without Components
                      </Button>
                      <div className="col-span-3">
                        <p className="text-xs text-gray-600 mt-1 ml-1">
                          <span className="font-semibold">Company Without Components</span>: Company without components (i.e., not consolidated, not standalone with subsidiaries/associates/joint ventures)
                        </p>
                      </div>
                    </div>
                    {/* {selectedType && (
                      <div className="mt-4 p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-800">
                          <strong>Selected:</strong> {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} Results
                          {selectedQuarter ? ` + ${selectedQuarter}` : ''} - {getFilteredChecks(selectedQuarter, selectedType).length} checkpoints available
                        </p>
                        {selectedQuarter ? (
                          <p className="text-xs text-green-600 mt-1">
                            Combined filtering: Showing checkpoints that match both {selectedType} and {selectedQuarter} criteria
                          </p>
                        ) : (
                          <p className="text-xs text-green-600 mt-1">
                            Showing {selectedType}-specific checkpoints. Select a quarter to further refine the selection.
                          </p>
                        )}
                      </div>
                    )} */}
                  </div>
                </CardContent>
              </Card>

              {/* Audit Questions Section */}
              {selectedType && (
                <Card className="mb-6">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Pre-Review Questionnaire</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAuditQuestions(!showAuditQuestions)}
                      >
                        {showAuditQuestions ? 'Hide Questions' : 'Show Questions'}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Answer these questions to enable relevant audit checks based on your document characteristics.
                    </p>
                    
                    {showAuditQuestions && (
                      <div className="space-y-4">
                        {getFilteredQuestions(AUDIT_QUESTIONS, auditAnswers).map((question) => (
                          <div key={question.id} className="p-4 border rounded-lg">
                            <Label className="text-sm font-medium mb-2 block">
                              {question.question}
                            </Label>
                            <div className="space-y-2">
                              {question.type === 'select' ? (
                                <select
                                  value={auditAnswers[question.id] || ''}
                                  onChange={(e) => setAuditAnswers(prev => ({
                                    ...prev,
                                    [question.id]: e.target.value
                                  }))}
                                  className="w-full p-2 border rounded-md"
                                >
                                  <option value="">Select an option</option>
                                  {question.options?.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              ) : (
                                <div className="flex space-x-4">
                                  {['Yes', 'No'].map((option) => (
                                    <label key={option} className="flex items-center space-x-2">
                                      <input
                                        type="radio"
                                        name={question.id}
                                        value={option}
                                        checked={auditAnswers[question.id] === option}
                                        onChange={(e) => setAuditAnswers(prev => ({
                                          ...prev,
                                          [question.id]: e.target.value
                                        }))}
                                        className="w-4 h-4"
                                      />
                                      <span className="text-sm">{option}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {Object.keys(auditAnswers).length > 0 && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Relevant Checks:</strong> {getRelevantAuditChecks(auditAnswers, selectedType).length} audit checks will be included based on your answers.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
             
              {/* Document Upload */}
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Document Upload</h3>
                  <FileUploader />
                </CardContent>
              </Card>

              {/* User Declared Questions */}
              {resultsReport && (
                <Card className="mb-6">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Auditor Declared Questions</h3>
                      <Button
                        variant="outline"
                        onClick={() => setShowUserQuestions(!showUserQuestions)}
                        size="sm"
                      >
                        {showUserQuestions ? 'Hide Questions' : 'Show Questions'}
                      </Button>
                    </div>
                    {showUserQuestions && (
                      <UserDeclaredQuestions
                        onAnswersChange={handleUserAnswersChange}
                        initialAnswers={userAnswers}
                        onValidationChange={setQuestionsValid}
                      />
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <Button
                  onClick={handleShowCheckSelection}
                  disabled={isProcessing || !resultsReport || !questionsValid}
                  className="w-full md:w-auto"
                  size="lg"
                >
                  {isProcessing ? (
                    <span className="flex items-center">
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Select Checks to Run
                    </span>
                  )}
                </Button>
              </div>
              
              {/* Validation Message */}
              {resultsReport && !questionsValid && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Cannot proceed:</span>
                    <span>All {USER_QUESTIONS.length} Auditor Declared Questions must be answered before running analysis.</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Processing Progress */}
          {isProcessing && (
            <Card className="mt-6">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">
                      Processing checks: {processingProgress.completed} of {processingProgress.total}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-medium text-gray-500">
                        {getProgressPercentage()}%
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleStopAnalysis}
                        className="flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Stop Analysis
                      </Button>
                    </div>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-primary h-3 rounded-full transition-all duration-500"
                      style={{ width: `${getProgressPercentage()}%` }}
                    ></div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {processingProgress.currentCheck === "Analysis complete" ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : processingProgress.currentCheck === "Analysis cancelled" || processingProgress.currentCheck === "Analysis cancelled by user" ? (
                        <X className="h-4 w-4 text-red-500" />
                      ) : (
                        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                      )}
                      <span>{processingProgress.currentCheck}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        {getProgressPercentage() === 100 ? "Complete" : 
                         processingProgress.currentCheck.includes("cancelled") ? "Cancelled" : 
                         "Processing..."}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        analysisData && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Company Name Dialog */}
            <Dialog open={companyNameDialogOpen} onOpenChange={setCompanyNameDialogOpen}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Enter Company Name</DialogTitle>
                  <DialogDescription>
                    Please enter or confirm the company name for this analysis. This will be used to identify the report in your history.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Company Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="company-name"
                      placeholder="e.g., ABC Corporation Ltd."
                      value={companyName}
                      onChange={(e) => {
                        setCompanyName(e.target.value);
                        setCompanyNameError("");
                      }}
                      className={companyNameError ? "border-red-500" : ""}
                    />
                    {companyNameError && (
                      <p className="text-sm text-red-500">{companyNameError}</p>
                    )}
                  </div>
                  <div className="bg-blue-50 p-3 rounded-md">
                    <p className="text-sm text-blue-900">
                      <strong>Note:</strong> You can add comments/opinions to each checklist item below. The company name and comments will be saved together with your analysis.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={async () => {
                    if (!companyName.trim()) {
                      setCompanyNameError("Company name is required");
                      return;
                    }
                    setCompanyNameError("");
                    setCompanyNameDialogOpen(false);
                    
                    // Save to Firebase
                    try {
                      if (!currentUser || !currentUser.uid) {
                        throw new Error("User not authenticated");
                      }

                      const compatibleParameters = {
                        company_name: companyName.trim(),
                        audit_date: new Date(),
                        profit_or_loss: "Profit",
                        company_listing_status: "Unlisted",
                        top_1000_or_500: "No",
                        is_nbfc: "No",
                        audit_report_type: selectedType || "Normal",
                        has_cfs: "No"
                      };

                      // Add comments to results - CONVERT TO PROPER FORMAT
                      console.log('💬 Saving with auditor comments:', auditorComments);
                      console.log('💬 Total comments to save:', Object.keys(auditorComments).length);
                      
                      // Convert results to CheckResult format for Firebase
                      const resultsWithComments: Record<string, CheckResult> = {};
                      Object.entries(analysisData.results).forEach(([key, value]: [string, any]) => {
                        const category = key.startsWith('user_question_') 
                          ? 'User Declared Questions' 
                          : AVAILABLE_CHECKS.find(c => c.id === key)?.category || 'Unknown';
                        
                        // Convert to proper CheckResult format
                        resultsWithComments[key] = {
                          isCompliant: value.compliant !== undefined ? value.compliant : value.isCompliant,
                          explanation: value.details || value.explanation || '',
                          confidence: value.confidence,
                          detail: value.detail || `Model: ${value.model_used || 'unknown'}`,
                          source: category,
                          isUserQuestion: value.isUserQuestion || key.startsWith('user_question_'),
                          userAnswer: value.userAnswer,
                          checklistName: value.checklistMetadata?.name,
                          checklistDescription: value.checklistMetadata?.description,
                          checklistCategory: value.checklistMetadata?.category,
                          auditorComment: auditorComments[key] || "",
                          extractedData: value.feedback
                        } as CheckResult;
                        
                        if (auditorComments[key]) {
                          console.log(`💬 Adding comment to ${key}:`, auditorComments[key].substring(0, 50));
                        }
                      });
                      
                      console.log('💬 Results with comments prepared:', Object.keys(resultsWithComments).length, 'items');

                      const savedAnalysisId = await saveAnalysisResults(
                        currentUser.uid,
                        compatibleParameters as any,
                        resultsWithComments,
                        { results_report: resultsReport! },
                        analysisData.summary as any,
                        analysisData.userAnswers
                      );

                      console.log('✅ Analysis saved with ID:', savedAnalysisId);
                      console.log('🔄 Refreshing history for user:', currentUser.uid);

                      // Invalidate and refetch query cache to refresh history
                      await queryClient.invalidateQueries({
                        queryKey: ["analysisHistory", currentUser.uid]
                      });
                      
                      // Explicitly refetch to ensure history is updated
                      const refetchResult = await queryClient.refetchQueries({
                        queryKey: ["analysisHistory", currentUser.uid]
                      });
                      
                      console.log('✅ History refetched:', refetchResult);

                      toast({
                        title: "Analysis Saved",
                        description: `Analysis for ${companyName} has been saved successfully with ${Object.keys(auditorComments).filter(k => auditorComments[k]).length} comments.`,
                      });
                      
                      // Update analysis data with new ID
                      setAnalysisData({
                        ...analysisData,
                        id: savedAnalysisId
                      });
                    } catch (error) {
                      console.error("Failed to save analysis:", error);
                      toast({
                        title: "Save Failed",
                        description: `Failed to save analysis: ${error instanceof Error ? error.message : 'Unknown error'}`,
                        variant: "destructive",
                      });
                    }
                  }} className="w-full">
                    Confirm and Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold">Analysis Results{companyName && ` - ${companyName}`}</h2>
                  {analysisData.summary.isPartial && (
                    <Badge variant="secondary" className="text-orange-600 bg-orange-50">
                      Partial Results
                    </Badge>
                  )}
                </div>
                <div className="flex space-x-2">
                  <Badge variant="outline" className="text-green-600">
                    {analysisData.summary.total} Checks Run
                  </Badge>
                  {companyName && (
                    <Button
                      variant="default"
                      onClick={async () => {
                        // Save/Update analysis with current comments
                        try {
                          if (!currentUser || !currentUser.uid) {
                            throw new Error("User not authenticated");
                          }

                          const compatibleParameters = {
                            company_name: companyName.trim(),
                            audit_date: new Date(),
                            profit_or_loss: "Profit",
                            company_listing_status: "Unlisted",
                            top_1000_or_500: "No",
                            is_nbfc: "No",
                            audit_report_type: selectedType || "Normal",
                            has_cfs: "No"
                          };

                          // Add comments to results - CONVERT TO PROPER FORMAT
                          console.log('💬 Saving/Updating with auditor comments:', auditorComments);
                          
                          // Convert results to CheckResult format for Firebase
                          const resultsWithComments: Record<string, CheckResult> = {};
                          Object.entries(analysisData.results).forEach(([key, value]: [string, any]) => {
                            const category = key.startsWith('user_question_') 
                              ? 'User Declared Questions' 
                              : AVAILABLE_CHECKS.find(c => c.id === key)?.category || 'Unknown';
                            
                            // Convert to proper CheckResult format
                            resultsWithComments[key] = {
                              isCompliant: value.compliant !== undefined ? value.compliant : value.isCompliant,
                              explanation: value.details || value.explanation || '',
                              confidence: value.confidence,
                              detail: value.detail || `Model: ${value.model_used || 'unknown'}`,
                              source: category,
                              isUserQuestion: value.isUserQuestion || key.startsWith('user_question_'),
                              userAnswer: value.userAnswer,
                              checklistName: value.checklistMetadata?.name,
                              checklistDescription: value.checklistMetadata?.description,
                              checklistCategory: value.checklistMetadata?.category,
                              auditorComment: auditorComments[key] || "",
                              extractedData: value.feedback
                            } as CheckResult;
                          });

                          const savedAnalysisId = await saveAnalysisResults(
                            currentUser.uid,
                            compatibleParameters as any,
                            resultsWithComments,
                            { results_report: resultsReport! },
                            analysisData.summary as any,
                            analysisData.userAnswers
                          );

                          // Invalidate and refetch query cache to refresh history
                          await queryClient.invalidateQueries({
                            queryKey: ["analysisHistory", currentUser.uid]
                          });
                          
                          // Explicitly refetch to ensure history is updated
                          await queryClient.refetchQueries({
                            queryKey: ["analysisHistory", currentUser.uid]
                          });

                          toast({
                            title: "Analysis Updated",
                            description: `Comments saved successfully for ${companyName}.`,
                          });
                          
                          // Update analysis data with new ID
                          setAnalysisData({
                            ...analysisData,
                            id: savedAnalysisId
                          });
                        } catch (error) {
                          console.error("Failed to save analysis:", error);
                          toast({
                            title: "Save Failed",
                            description: `Failed to save comments: ${error instanceof Error ? error.message : 'Unknown error'}`,
                            variant: "destructive",
                          });
                        }
                      }}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Save Comments
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      console.log('Navigating to analysis detail with ID:', analysisData.id);
                      console.log('Full navigation path:', `/dashboard/analysis/${analysisData.id}`);
                      navigate(`/dashboard/analysis/${analysisData.id}`);
                    }}
                  >
                    View Full Report
                  </Button>
                </div>
              </div>

              {/* Summary Statistics */}
              <div className="space-y-4 mb-6">
                {/* AI Checks Summary */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <Card>
                  <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-blue-600">{analysisData.summary.total || 0}</div>
                      <div className="text-sm text-gray-500">Checks Run</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-green-600">{analysisData.summary.compliant || 0}</div>
                      <div className="text-sm text-gray-500">Compliant</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-red-600">{analysisData.summary.nonCompliant || 0}</div>
                      <div className="text-sm text-gray-500">Non-Compliant</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-orange-600">{analysisData.summary.check || 0}</div>
                      <div className="text-sm text-gray-500">Check</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-orange-400">{analysisData.summary.dataUnavailable || 0}</div>
                      <div className="text-sm text-gray-500">Data Unavailable</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-purple-600">{analysisData.summary.compliancePercentage || 0}%</div>
                      <div className="text-sm text-gray-500">Compliance Rate</div>
                  </CardContent>
                </Card>
              </div>
                
                {/* User Input Summary */}
                {analysisData.summary.userInput && analysisData.summary.userInput.total > 0 && (
                  <Card className="bg-gray-50">
                    <CardContent className="pt-4">
                      <div className="text-sm font-semibold text-gray-700 mb-3">User Input</div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-600">{analysisData.summary.userInput.yes || 0}</div>
                          <div className="text-xs text-gray-500">Yes</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-red-600">{analysisData.summary.userInput.no || 0}</div>
                          <div className="text-xs text-gray-500">No</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-gray-600">{analysisData.summary.userInput.na || 0}</div>
                          <div className="text-xs text-gray-500">NA</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              

              <p className="text-gray-600 mb-4">
                Analysis completed on {format(new Date(analysisData.timestamp), "PPP 'at' p")}
              </p>
              
              {analysisData.summary.isPartial && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <p className="text-orange-800 font-medium">
                      Partial Analysis Results
                    </p>
                  </div>
                  <p className="text-orange-700 text-sm mt-1">
                    This analysis was stopped early. Showing results for {analysisData.summary.completedChecks} out of {analysisData.summary.totalSelectedChecks} selected checks.
                  </p>
                </div>
              )}

              {/* Compliance Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div
                  className={`h-3 rounded-full ${
                    analysisData.summary.compliancePercentage < 50 ? "bg-red-500" :
                    analysisData.summary.compliancePercentage < 80 ? "bg-yellow-500" : "bg-green-500"
                  }`}
                  style={{ width: `${analysisData.summary.compliancePercentage}%` }}
                ></div>
              </div>
            </div>


            {/* Results Details */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Detailed Results</h3>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadResultsAsHTML}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download Results
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={expandAllCards}
                    disabled={allExpanded}
                  >
                    Expand All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={collapseAllCards}
                    disabled={expandedCards.size === 0}
                  >
                    Collapse All
                  </Button>
                </div>
              </div>
              
              {/* AI Analysis Results */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-700">AI Analysis Results</h4>
                {Object.entries(analysisData.results)
                  .filter(([checkId]) => !checkId.startsWith('user_question_'))
                  .sort(([checkIdA, resultA], [checkIdB, resultB]) => {
                    const checkStatusIds = ['eps_pat_ratio_comparison', 'subsidiary_word_highlighting', 'castings_check_horizontal', 'basic_logic_issues', 'tax_pbt_ratio_highlighting', 'standalone_consolidated_notes_interlink', 'profit_loss_quarter_ytd_consistency', 'line_items_comparison', 'qualification_eom_consistency'];
                    const isSpecialCheckA = checkStatusIds.includes(checkIdA) && !resultA.compliant;
                    const isSpecialCheckB = checkStatusIds.includes(checkIdB) && !resultB.compliant;
                    const isCheckA = resultA.isWarning || isSpecialCheckA;
                    const isCheckB = resultB.isWarning || isSpecialCheckB;
                    
                    const getPri = (r: any, isChk: boolean) => r.isDataUnavailable ? 3 : (!r.compliant && !isChk ? 0 : isChk ? 1 : 4);
                    return getPri(resultA, isCheckA) - getPri(resultB, isCheckB);
                  })
                  .map(([checkId, result]) => {
                const isExpanded = expandedCards.has(checkId);
                // Use metadata from results if available, otherwise fallback to lookup
                const checkTitle = result.checklistMetadata?.name || checkId.replace(/_/g, ' ');
                const checkDescription = result.checklistMetadata?.description || '';
                const checkCategory = result.checklistMetadata?.category || '';
                
                return (
                  <Card key={checkId}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex flex-col flex-1 pr-4">
                          <CardTitle className="text-base">
                            {checkTitle}
                          </CardTitle>
                          {checkCategory && (
                            <Badge variant="outline" className="text-xs mt-1 w-fit">
                              {checkCategory}
                            </Badge>
                          )}
                          {checkDescription && (
                            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                              {checkDescription}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge
                            className={
                              result.isDataUnavailable
                                ? "bg-orange-200 text-orange-900 border border-orange-300"
                                : (checkId === 'eps_pat_ratio_comparison' || checkId === 'subsidiary_word_highlighting' || checkId === 'castings_check_horizontal' || checkId === 'basic_logic_issues' || checkId === 'tax_pbt_ratio_highlighting' || checkId === 'standalone_consolidated_notes_interlink' || checkId === 'profit_loss_quarter_ytd_consistency' || checkId === 'line_items_comparison' || checkId === 'qualification_eom_consistency') && !result.compliant
                                ? "bg-orange-500 text-white"
                                : result.isWarning 
                                ? "bg-orange-500 text-white" 
                                : result.compliant 
                                  ? "bg-green-600 text-white" 
                                  : "bg-red-600 text-white"
                            }
                          >
                            {result.isDataUnavailable
                              ? "Data Unavailable"
                              : (checkId === 'eps_pat_ratio_comparison' || checkId === 'subsidiary_word_highlighting' || checkId === 'castings_check_horizontal' || checkId === 'basic_logic_issues' || checkId === 'tax_pbt_ratio_highlighting' || checkId === 'standalone_consolidated_notes_interlink' || checkId === 'profit_loss_quarter_ytd_consistency' || checkId === 'line_items_comparison' || checkId === 'qualification_eom_consistency') && !result.compliant
                              ? "Check"
                              : result.isWarning 
                                ? "Check" 
                                : result.compliant 
                                  ? "Compliant"
                                  : "Non-Compliant"}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleCardExpansion(checkId)}
                            className="p-1 h-8 w-8"
                          >
                            {isExpanded ? (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    {isExpanded && (
                      <CardContent className="pt-0">
                        <div className="space-y-3">
                          {/* Status bar */}
                          <div className="flex items-center gap-4 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                            <Badge className={
                              result.isDataUnavailable
                                ? "bg-orange-100 text-orange-900 border border-orange-300"
                                : (checkId === 'eps_pat_ratio_comparison' || checkId === 'subsidiary_word_highlighting' || checkId === 'castings_check_horizontal' || checkId === 'basic_logic_issues' || checkId === 'tax_pbt_ratio_highlighting' || checkId === 'standalone_consolidated_notes_interlink' || checkId === 'profit_loss_quarter_ytd_consistency' || checkId === 'line_items_comparison' || checkId === 'qualification_eom_consistency') && !result.compliant
                                ? "bg-orange-100 text-orange-800 border border-orange-200"
                                : result.isWarning
                                ? "bg-orange-100 text-orange-800 border border-orange-200"
                                : result.compliant
                                  ? "bg-green-100 text-green-800 border border-green-200"
                                  : "bg-red-100 text-red-800 border border-red-200"
                            }>
                              {result.isDataUnavailable ? "Data Unavailable" : (checkId === 'eps_pat_ratio_comparison' || checkId === 'subsidiary_word_highlighting' || checkId === 'castings_check_horizontal' || checkId === 'basic_logic_issues' || checkId === 'tax_pbt_ratio_highlighting' || checkId === 'standalone_consolidated_notes_interlink' || checkId === 'profit_loss_quarter_ytd_consistency' || checkId === 'line_items_comparison' || checkId === 'qualification_eom_consistency') && !result.compliant ? "Check" : result.isWarning ? "Check" : result.compliant ? "Compliant" : "Non-Compliant"}
                            </Badge>
                            {result.confidence && <span className="text-xs text-slate-500">Confidence: {Math.round((result.confidence || 0) * 100)}%</span>}
                            {result.model_used && <span className="text-xs text-slate-500">Model: {result.model_used}</span>}
                          </div>

                          {/* Analysis Details - open by default */}
                          <details open className="group rounded-lg border border-slate-200 overflow-hidden shadow-sm">
                            <summary className="flex items-center gap-2.5 px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none [&::-webkit-details-marker]:hidden list-none">
                              <span className="w-7 h-7 rounded-md bg-slate-200 text-slate-600 flex items-center justify-center text-sm flex-shrink-0">📋</span>
                              <span className="font-semibold text-sm text-slate-800">Analysis Details</span>
                              <svg className="ml-auto w-4 h-4 text-slate-400 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </summary>
                            <div className="px-4 py-3 text-sm" dangerouslySetInnerHTML={formatStructuredText(result.details)} />
                          </details>

                          {/* Reasoning */}
                          <details className="group rounded-lg border border-blue-200 overflow-hidden shadow-sm">
                            <summary className="flex items-center gap-2.5 px-4 py-3 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors select-none [&::-webkit-details-marker]:hidden list-none">
                              <span className="w-7 h-7 rounded-md bg-blue-200 text-blue-700 flex items-center justify-center text-sm flex-shrink-0">💡</span>
                              <span className="font-semibold text-sm text-blue-900">Reasoning</span>
                              <svg className="ml-auto w-4 h-4 text-blue-400 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </summary>
                            <div className="px-4 py-3 text-sm bg-blue-50/30" dangerouslySetInnerHTML={formatStructuredText(result.feedback.reasoning)} />
                          </details>

                          {/* Evidence */}
                          {result.feedback.evidence.length > 0 && (
                            <details className="group rounded-lg border border-emerald-200 overflow-hidden shadow-sm">
                              <summary className="flex items-center gap-2.5 px-4 py-3 bg-emerald-50 cursor-pointer hover:bg-emerald-100 transition-colors select-none [&::-webkit-details-marker]:hidden list-none">
                                <span className="w-7 h-7 rounded-md bg-emerald-200 text-emerald-700 flex items-center justify-center text-sm flex-shrink-0">🔍</span>
                                <span className="font-semibold text-sm text-emerald-900">Evidence Found</span>
                                <span className="text-xs text-emerald-600 ml-1">({result.feedback.evidence.length} items)</span>
                                <svg className="ml-auto w-4 h-4 text-emerald-400 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                              </summary>
                              <div className="px-4 py-3 text-sm bg-emerald-50/30" dangerouslySetInnerHTML={formatStructuredText(result.feedback.evidence.join('\n'))} />
                            </details>
                          )}

                          {/* Recommendations */}
                          {result.feedback.recommendations && result.feedback.recommendations.length > 0 && (
                            <details className="group rounded-lg border border-amber-200 overflow-hidden shadow-sm">
                              <summary className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors select-none [&::-webkit-details-marker]:hidden list-none">
                                <span className="w-7 h-7 rounded-md bg-amber-200 text-amber-700 flex items-center justify-center text-sm flex-shrink-0">⚡</span>
                                <span className="font-semibold text-sm text-amber-900">Recommendations</span>
                                <span className="text-xs text-amber-600 ml-1">({result.feedback.recommendations.length})</span>
                                <svg className="ml-auto w-4 h-4 text-amber-400 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                              </summary>
                              <div className="px-4 py-3 text-sm bg-amber-50/30" dangerouslySetInnerHTML={formatStructuredText(result.feedback.recommendations.join('\n'))} />
                            </details>
                          )}

                          {/* Auditor Comment */}
                          <div className="mt-2 pt-3 border-t border-slate-200">
                            <Label htmlFor={`comment-${checkId}`} className="text-sm font-semibold text-gray-900 mb-2 block">
                              Auditor Comment / Opinion <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                            </Label>
                            <Textarea
                              id={`comment-${checkId}`}
                              placeholder="Enter your professional opinion or comments about this check..."
                              value={auditorComments[checkId] || ''}
                              onChange={(e) => {
                                setAuditorComments(prev => ({
                                  ...prev,
                                  [checkId]: e.target.value
                                }));
                              }}
                              className="min-h-[80px] bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-400 mt-1">
                              This comment will be saved with the analysis results for future reference.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
              </div>
              
              {/* User Questions Results */}
              {Object.entries(analysisData.results)
                .filter(([checkId]) => checkId.startsWith('user_question_'))
                .length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-700">Auditor Declared Questions Results</h4>
                  {Object.entries(analysisData.results)
                    .filter(([checkId]) => checkId.startsWith('user_question_'))
                    .map(([checkId, result]) => {
                      const isExpanded = expandedCards.has(checkId);
                      const questionId = checkId.replace('user_question_', '');
                      const question = USER_QUESTIONS.find(q => q.id === questionId);
                      
                      // Extract the actual user answer from the result
                      const userAnswer = (result as any).userAnswer || result.details?.replace('User Answer: ', '')?.split(' - ')[0] || 'N/A';
                      const answerText = userAnswer === 'yes' ? 'Yes' : userAnswer === 'no' ? 'No' : 'N/A';
                      const answerClass = userAnswer === 'yes' ? 'bg-green-600 text-white' : userAnswer === 'no' ? 'bg-red-600 text-white' : 'bg-gray-600 text-white';
                      
                      return (
                        <Card key={checkId}>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-base">
                                {question ? `${question.question}` : questionId.replace(/_/g, ' ')}
                              </CardTitle>
                              <div className="flex items-center space-x-2">
                                <Badge className={answerClass}>
                                  {answerText}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleCardExpansion(checkId)}
                                  className="p-1 h-8 w-8"
                                >
                                  {isExpanded ? (
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          {isExpanded && (
                            <CardContent>
                              <div className="space-y-4">
                                <div className="bg-gray-50 p-3 rounded-lg">
                                  <h4 className="font-medium text-sm mb-2">User Answer</h4>
                                  <div className="text-sm">
                                    <strong>{answerText}</strong>
                                    {result.details?.includes(' - ') && (
                                      <span> - {result.details.split(' - ').slice(1).join(' - ')}</span>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="space-y-3">
                                  <div className="bg-blue-50 p-3 rounded-lg">
                                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                      <span className="text-blue-600">💡</span>
                                      Reasoning
                                    </h4>
                                    <div 
                                      className="text-sm text-gray-700" 
                                      dangerouslySetInnerHTML={formatStructuredText(result.feedback.reasoning)}
                                    />
                                  </div>
                                  
                                  {result.feedback.evidence.length > 0 && (
                                    <div className="bg-green-50 p-3 rounded-lg">
                                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                        <span className="text-green-600">📝</span>
                                        Additional Notes
                                      </h4>
                                      <div 
                                        className="text-sm text-gray-700" 
                                        dangerouslySetInnerHTML={formatStructuredText(result.feedback.evidence.join('\n'))}
                                      />
                                    </div>
                                  )}
                                  
                                  {result.feedback.recommendations && result.feedback.recommendations.length > 0 && (
                                    <div className="bg-amber-50 p-3 rounded-lg">
                                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                        <span className="text-amber-600">💡</span>
                                        Recommendations
                                      </h4>
                                      <div 
                                        className="text-sm text-gray-700" 
                                        dangerouslySetInnerHTML={formatStructuredText(result.feedback.recommendations.join('\n'))}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )
      )}
        </div>
      </div>
    </div>
  );
};

export default ResultsAnalyzer;